import { athenaApi as api } from './athenaApi';
export const addTagTypes = [
  'Actions',
  'Admin',
  'Approval Queue',
  'Attachments',
  'Audit Logs',
  'Authentication',
  'Billing',
  'CLIN (Contract Line Item Number)',
  'Contacts',
  'CTP (Consent to Purchase)',
  'Deliverables',
  'Financials',
  'Financial Plans',
  'Forms',
  'Health',
  'Library',
  'Library Directories',
  'Locations',
  'Master Schedule',
  'Type Catalogs',
  'Organizations',
  'Platform Config',
  'PNR (Problem Notification Report)',
  'PowerBI',
  'Programs',
  'Roles',
  'Projects',
  'RIP (Request to Initiate Purchase)',
  'IRO Risk (Issues/Risks/Opportunities)',
  'TAMS Inventory',
  'TAR (Travel Authorization Request)',
  'TR (Trip Report)',
  'Users',
  'Workflows',
  'Workflow Catalog',
  'Signatures',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getApiActions: build.query<GetApiActionsApiResponse, GetApiActionsApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/actions`,
            params: {
              includeArchived: queryArg.includeArchived,
            },
          }),
          providesTags: ['Actions'],
        },
      ),
      postApiActions: build.mutation<
        PostApiActionsApiResponse,
        PostApiActionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/actions`,
          method: 'POST',
          body: queryArg.handlersCreateActionRequest,
        }),
        invalidatesTags: ['Actions'],
      }),
      getApiActionsByActionName: build.query<
        GetApiActionsByActionNameApiResponse,
        GetApiActionsByActionNameApiArg
      >({
        query: (queryArg) => ({ url: `/api/actions/${queryArg.actionName}` }),
        providesTags: ['Actions'],
      }),
      deleteApiActionsByActionName: build.mutation<
        DeleteApiActionsByActionNameApiResponse,
        DeleteApiActionsByActionNameApiArg
      >({
        query: (queryArg) => ({
          url: `/api/actions/${queryArg.actionName}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Actions'],
      }),
      patchApiActionsByActionName: build.mutation<
        PatchApiActionsByActionNameApiResponse,
        PatchApiActionsByActionNameApiArg
      >({
        query: (queryArg) => ({
          url: `/api/actions/${queryArg.actionName}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateActionRequest,
        }),
        invalidatesTags: ['Actions'],
      }),
      getApiAdminRoleNotificationPreferences: build.query<
        GetApiAdminRoleNotificationPreferencesApiResponse,
        GetApiAdminRoleNotificationPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/role-notification-preferences`,
          params: {
            programId: queryArg.programId,
          },
        }),
        providesTags: ['Admin'],
      }),
      putApiAdminRoleNotificationPreferences: build.mutation<
        PutApiAdminRoleNotificationPreferencesApiResponse,
        PutApiAdminRoleNotificationPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/role-notification-preferences`,
          method: 'PUT',
          body: queryArg.handlersUpdateRoleNotificationPreferencesRequest,
        }),
        invalidatesTags: ['Admin'],
      }),
      getApiApprovalQueue: build.query<
        GetApiApprovalQueueApiResponse,
        GetApiApprovalQueueApiArg
      >({
        query: (queryArg) => ({
          url: `/api/approval-queue`,
          params: {
            approvalState: queryArg.approvalState,
            page: queryArg.page,
            limit: queryArg.limit,
          },
        }),
        providesTags: ['Approval Queue'],
      }),
      getApiApprovalQueueStats: build.query<
        GetApiApprovalQueueStatsApiResponse,
        GetApiApprovalQueueStatsApiArg
      >({
        query: () => ({ url: `/api/approval-queue/stats` }),
        providesTags: ['Approval Queue'],
      }),
      getApiAttachments: build.query<
        GetApiAttachmentsApiResponse,
        GetApiAttachmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments`,
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            objectId: queryArg.objectId,
            objectType: queryArg.objectType,
            creatorContactId: queryArg.creatorContactId,
            formSectionId: queryArg.formSectionId,
          },
        }),
        providesTags: ['Attachments'],
      }),
      postApiAttachments: build.mutation<
        PostApiAttachmentsApiResponse,
        PostApiAttachmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Attachments'],
      }),
      postApiAttachmentsBatch: build.mutation<
        PostApiAttachmentsBatchApiResponse,
        PostApiAttachmentsBatchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/batch`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Attachments'],
      }),
      postApiAttachmentsDownload: build.mutation<
        PostApiAttachmentsDownloadApiResponse,
        PostApiAttachmentsDownloadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/download`,
          method: 'POST',
          body: queryArg.handlersDownloadAttachmentsZipRequest,
        }),
        invalidatesTags: ['Attachments'],
      }),
      getApiAttachmentsById: build.query<
        GetApiAttachmentsByIdApiResponse,
        GetApiAttachmentsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/attachments/${queryArg.id}` }),
        providesTags: ['Attachments'],
      }),
      deleteApiAttachmentsById: build.mutation<
        DeleteApiAttachmentsByIdApiResponse,
        DeleteApiAttachmentsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Attachments'],
      }),
      patchApiAttachmentsById: build.mutation<
        PatchApiAttachmentsByIdApiResponse,
        PatchApiAttachmentsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateAttachmentRequest,
        }),
        invalidatesTags: ['Attachments'],
      }),
      getApiAttachmentsByIdDownload: build.query<
        GetApiAttachmentsByIdDownloadApiResponse,
        GetApiAttachmentsByIdDownloadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}/download`,
        }),
        providesTags: ['Attachments'],
      }),
      getApiAttachmentsByIdVAndVersion: build.query<
        GetApiAttachmentsByIdVAndVersionApiResponse,
        GetApiAttachmentsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}/v/${queryArg.version}`,
        }),
        providesTags: ['Attachments'],
      }),
      deleteApiAttachmentsByIdVAndVersion: build.mutation<
        DeleteApiAttachmentsByIdVAndVersionApiResponse,
        DeleteApiAttachmentsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}/v/${queryArg.version}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Attachments'],
      }),
      getApiAttachmentsByIdVAndVersionDownload: build.query<
        GetApiAttachmentsByIdVAndVersionDownloadApiResponse,
        GetApiAttachmentsByIdVAndVersionDownloadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}/v/${queryArg.version}/download`,
        }),
        providesTags: ['Attachments'],
      }),
      getApiAttachmentsByIdVersions: build.query<
        GetApiAttachmentsByIdVersionsApiResponse,
        GetApiAttachmentsByIdVersionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}/versions`,
        }),
        providesTags: ['Attachments'],
      }),
      postApiAttachmentsByIdVersions: build.mutation<
        PostApiAttachmentsByIdVersionsApiResponse,
        PostApiAttachmentsByIdVersionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/attachments/${queryArg.id}/versions`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Attachments'],
      }),
      getApiAuditLogs: build.query<
        GetApiAuditLogsApiResponse,
        GetApiAuditLogsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/audit-logs`,
          params: {
            verboseLevel: queryArg.verboseLevel,
            userId: queryArg.userId,
            objectType: queryArg.objectType,
            objectId: queryArg.objectId,
            startTime: queryArg.startTime,
            endTime: queryArg.endTime,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Audit Logs'],
      }),
      postApiAuditLogs: build.mutation<
        PostApiAuditLogsApiResponse,
        PostApiAuditLogsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/audit-logs`,
          method: 'POST',
          body: queryArg.handlersCreateAuditLogRequest,
        }),
        invalidatesTags: ['Audit Logs'],
      }),
      getApiAuditLogsVerboseLevels: build.query<
        GetApiAuditLogsVerboseLevelsApiResponse,
        GetApiAuditLogsVerboseLevelsApiArg
      >({
        query: () => ({ url: `/api/audit-logs/verbose-levels` }),
        providesTags: ['Audit Logs'],
      }),
      getApiAuditLogsById: build.query<
        GetApiAuditLogsByIdApiResponse,
        GetApiAuditLogsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/audit-logs/${queryArg.id}` }),
        providesTags: ['Audit Logs'],
      }),
      getApiAuthCallback: build.query<
        GetApiAuthCallbackApiResponse,
        GetApiAuthCallbackApiArg
      >({
        query: (queryArg) => ({
          url: `/api/auth/callback`,
          params: {
            state: queryArg.state,
            code: queryArg.code,
          },
        }),
        providesTags: ['Authentication'],
      }),
      getApiAuthLogin: build.query<
        GetApiAuthLoginApiResponse,
        GetApiAuthLoginApiArg
      >({
        query: () => ({ url: `/api/auth/login` }),
        providesTags: ['Authentication'],
      }),
      postApiBillingImports: build.mutation<
        PostApiBillingImportsApiResponse,
        PostApiBillingImportsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/billing/imports`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Billing'],
      }),
      getApiClins: build.query<GetApiClinsApiResponse, GetApiClinsApiArg>({
        query: (queryArg) => ({
          url: `/api/clins`,
          params: {
            programId: queryArg.programId,
            fundingSourceId: queryArg.fundingSourceId,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['CLIN (Contract Line Item Number)'],
      }),
      postApiClins: build.mutation<PostApiClinsApiResponse, PostApiClinsApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/clins`,
            method: 'POST',
            body: queryArg.handlersCreateClinRequest,
          }),
          invalidatesTags: ['CLIN (Contract Line Item Number)'],
        },
      ),
      getApiClinsById: build.query<
        GetApiClinsByIdApiResponse,
        GetApiClinsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/clins/${queryArg.id}` }),
        providesTags: ['CLIN (Contract Line Item Number)'],
      }),
      deleteApiClinsById: build.mutation<
        DeleteApiClinsByIdApiResponse,
        DeleteApiClinsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/clins/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['CLIN (Contract Line Item Number)'],
      }),
      patchApiClinsById: build.mutation<
        PatchApiClinsByIdApiResponse,
        PatchApiClinsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/clins/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateClinRequest,
        }),
        invalidatesTags: ['CLIN (Contract Line Item Number)'],
      }),
      getApiContacts: build.query<
        GetApiContactsApiResponse,
        GetApiContactsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contacts`,
          params: {
            name: queryArg.name,
            organizationId: queryArg.organizationId,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Contacts'],
      }),
      postApiContacts: build.mutation<
        PostApiContactsApiResponse,
        PostApiContactsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contacts`,
          method: 'POST',
          body: queryArg.handlersCreateContactRequest,
        }),
        invalidatesTags: ['Contacts'],
      }),
      getApiContactsByContactId: build.query<
        GetApiContactsByContactIdApiResponse,
        GetApiContactsByContactIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/contacts/${queryArg.contactId}` }),
        providesTags: ['Contacts'],
      }),
      deleteApiContactsByContactId: build.mutation<
        DeleteApiContactsByContactIdApiResponse,
        DeleteApiContactsByContactIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contacts/${queryArg.contactId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Contacts'],
      }),
      patchApiContactsByContactId: build.mutation<
        PatchApiContactsByContactIdApiResponse,
        PatchApiContactsByContactIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contacts/${queryArg.contactId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateContactRequest,
        }),
        invalidatesTags: ['Contacts'],
      }),
      getApiCtps: build.query<GetApiCtpsApiResponse, GetApiCtpsApiArg>({
        query: (queryArg) => ({
          url: `/api/ctps`,
          params: {
            projectId: queryArg.projectId,
            toContactId: queryArg.toContactId,
            fromContactId: queryArg.fromContactId,
            currentVersion: queryArg.currentVersion,
            search: queryArg.search,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['CTP (Consent to Purchase)'],
      }),
      postApiCtps: build.mutation<PostApiCtpsApiResponse, PostApiCtpsApiArg>({
        query: (queryArg) => ({
          url: `/api/ctps`,
          method: 'POST',
          body: queryArg.handlersCreateCtpRequest,
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      getApiCtpsById: build.query<
        GetApiCtpsByIdApiResponse,
        GetApiCtpsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/ctps/${queryArg.id}` }),
        providesTags: ['CTP (Consent to Purchase)'],
      }),
      deleteApiCtpsById: build.mutation<
        DeleteApiCtpsByIdApiResponse,
        DeleteApiCtpsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      patchApiCtpsById: build.mutation<
        PatchApiCtpsByIdApiResponse,
        PatchApiCtpsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateCtpRequest,
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      postApiCtpsByIdEstimate: build.mutation<
        PostApiCtpsByIdEstimateApiResponse,
        PostApiCtpsByIdEstimateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}/estimate`,
          method: 'POST',
          body: queryArg.handlersCreateEstimateRequest,
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      deleteApiCtpsByIdEstimateAndEstimateId: build.mutation<
        DeleteApiCtpsByIdEstimateAndEstimateIdApiResponse,
        DeleteApiCtpsByIdEstimateAndEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}/estimate/${queryArg.estimateId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      patchApiCtpsByIdEstimateAndEstimateId: build.mutation<
        PatchApiCtpsByIdEstimateAndEstimateIdApiResponse,
        PatchApiCtpsByIdEstimateAndEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}/estimate/${queryArg.estimateId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateEstimateRequest,
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      postApiCtpsByIdSubmit: build.mutation<
        PostApiCtpsByIdSubmitApiResponse,
        PostApiCtpsByIdSubmitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}/submit`,
          method: 'POST',
        }),
        invalidatesTags: ['CTP (Consent to Purchase)'],
      }),
      getApiCtpsByIdVAndVersion: build.query<
        GetApiCtpsByIdVAndVersionApiResponse,
        GetApiCtpsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ctps/${queryArg.id}/v/${queryArg.version}`,
        }),
        providesTags: ['CTP (Consent to Purchase)'],
      }),
      getApiCtpsByIdVersions: build.query<
        GetApiCtpsByIdVersionsApiResponse,
        GetApiCtpsByIdVersionsApiArg
      >({
        query: (queryArg) => ({ url: `/api/ctps/${queryArg.id}/versions` }),
        providesTags: ['CTP (Consent to Purchase)'],
      }),
      getApiDeliverables: build.query<
        GetApiDeliverablesApiResponse,
        GetApiDeliverablesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            deliverableTypeId: queryArg.deliverableTypeId,
            locationId: queryArg.locationId,
            authorId: queryArg.authorId,
            programId: queryArg.programId,
            projectId: queryArg.projectId,
            workflowState: queryArg.workflowState,
            lastModifiedAfter: queryArg.lastModifiedAfter,
            lastModifiedBefore: queryArg.lastModifiedBefore,
            dueDateAfter: queryArg.dueDateAfter,
            dueDateBefore: queryArg.dueDateBefore,
          },
        }),
        providesTags: ['Deliverables'],
      }),
      postApiDeliverables: build.mutation<
        PostApiDeliverablesApiResponse,
        PostApiDeliverablesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables`,
          method: 'POST',
          body: queryArg.handlersDeliverableCreateRequest,
        }),
        invalidatesTags: ['Deliverables'],
      }),
      getApiDeliverablesTypes: build.query<
        GetApiDeliverablesTypesApiResponse,
        GetApiDeliverablesTypesApiArg
      >({
        query: () => ({ url: `/api/deliverables/types` }),
        providesTags: ['Deliverables'],
      }),
      getApiDeliverablesById: build.query<
        GetApiDeliverablesByIdApiResponse,
        GetApiDeliverablesByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/deliverables/${queryArg.id}` }),
        providesTags: ['Deliverables'],
      }),
      deleteApiDeliverablesById: build.mutation<
        DeleteApiDeliverablesByIdApiResponse,
        DeleteApiDeliverablesByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Deliverables'],
      }),
      patchApiDeliverablesById: build.mutation<
        PatchApiDeliverablesByIdApiResponse,
        PatchApiDeliverablesByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersDeliverableUpdateRequest,
        }),
        invalidatesTags: ['Deliverables'],
      }),
      getApiDeliverablesByIdApprovalStatus: build.query<
        GetApiDeliverablesByIdApprovalStatusApiResponse,
        GetApiDeliverablesByIdApprovalStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables/${queryArg.id}/approval-status`,
        }),
        providesTags: ['Deliverables'],
      }),
      postApiDeliverablesByIdCancel: build.mutation<
        PostApiDeliverablesByIdCancelApiResponse,
        PostApiDeliverablesByIdCancelApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables/${queryArg.id}/cancel`,
          method: 'POST',
        }),
        invalidatesTags: ['Deliverables'],
      }),
      postApiDeliverablesByIdSubmit: build.mutation<
        PostApiDeliverablesByIdSubmitApiResponse,
        PostApiDeliverablesByIdSubmitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables/${queryArg.id}/submit`,
          method: 'POST',
        }),
        invalidatesTags: ['Deliverables'],
      }),
      getApiDeliverablesByIdWorkflow: build.query<
        GetApiDeliverablesByIdWorkflowApiResponse,
        GetApiDeliverablesByIdWorkflowApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deliverables/${queryArg.id}/workflow`,
        }),
        providesTags: ['Deliverables'],
      }),
      postApiDevSendTestEmail: build.mutation<
        PostApiDevSendTestEmailApiResponse,
        PostApiDevSendTestEmailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/dev/send-test-email`,
          method: 'POST',
          body: queryArg.handlersSendTestEmailRequest,
        }),
        invalidatesTags: ['Authentication'],
      }),
      postApiDevSignCms: build.mutation<
        PostApiDevSignCmsApiResponse,
        PostApiDevSignCmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/dev/sign-cms`,
          method: 'POST',
          body: queryArg.handlersDevSignCmsRequest,
        }),
        invalidatesTags: ['Authentication'],
      }),
      postApiDevToken: build.mutation<
        PostApiDevTokenApiResponse,
        PostApiDevTokenApiArg
      >({
        query: (queryArg) => ({
          url: `/api/dev/token`,
          method: 'POST',
          body: queryArg.handlersCreateDevTokenRequest,
        }),
        invalidatesTags: ['Authentication'],
      }),
      getApiDevTokenAdmin: build.query<
        GetApiDevTokenAdminApiResponse,
        GetApiDevTokenAdminApiArg
      >({
        query: () => ({ url: `/api/dev/token/admin` }),
        providesTags: ['Authentication'],
      }),
      getApiDevTokenExamples: build.query<
        GetApiDevTokenExamplesApiResponse,
        GetApiDevTokenExamplesApiArg
      >({
        query: () => ({ url: `/api/dev/token/examples` }),
        providesTags: ['Authentication'],
      }),
      getApiFinancialsDashboard: build.query<
        GetApiFinancialsDashboardApiResponse,
        GetApiFinancialsDashboardApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/dashboard`,
          params: {
            programId: queryArg.programId,
            projectId: queryArg.projectId,
            clinId: queryArg.clinId,
            startDate: queryArg.startDate,
            endDate: queryArg.endDate,
          },
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsFidelityTypes: build.query<
        GetApiFinancialsFidelityTypesApiResponse,
        GetApiFinancialsFidelityTypesApiArg
      >({
        query: () => ({ url: `/api/financials/fidelity-types` }),
        providesTags: ['Financial Plans'],
      }),
      getApiFinancialsGfebsReports: build.query<
        GetApiFinancialsGfebsReportsApiResponse,
        GetApiFinancialsGfebsReportsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/gfebs-reports`,
          params: {
            programId: queryArg.programId,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsGfebsReportsByReportIdLineItems: build.query<
        GetApiFinancialsGfebsReportsByReportIdLineItemsApiResponse,
        GetApiFinancialsGfebsReportsByReportIdLineItemsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/gfebs-reports/${queryArg.reportId}/line-items`,
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsInvoices: build.query<
        GetApiFinancialsInvoicesApiResponse,
        GetApiFinancialsInvoicesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/invoices`,
          params: {
            programId: queryArg.programId,
            projectId: queryArg.projectId,
            clinId: queryArg.clinId,
            startDate: queryArg.startDate,
            endDate: queryArg.endDate,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsInvoicesByCode: build.query<
        GetApiFinancialsInvoicesByCodeApiResponse,
        GetApiFinancialsInvoicesByCodeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/invoices/${queryArg.code}`,
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsInvoicesByInvoiceCodeCostsByClin: build.query<
        GetApiFinancialsInvoicesByInvoiceCodeCostsByClinApiResponse,
        GetApiFinancialsInvoicesByInvoiceCodeCostsByClinApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/invoices/${queryArg.invoiceCode}/costs-by-clin`,
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsInvoicesByInvoiceCodeCostsByProject: build.query<
        GetApiFinancialsInvoicesByInvoiceCodeCostsByProjectApiResponse,
        GetApiFinancialsInvoicesByInvoiceCodeCostsByProjectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/invoices/${queryArg.invoiceCode}/costs-by-project`,
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsItdReports: build.query<
        GetApiFinancialsItdReportsApiResponse,
        GetApiFinancialsItdReportsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/itd-reports`,
          params: {
            programId: queryArg.programId,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsItdReportsBySummaryIdDetails: build.query<
        GetApiFinancialsItdReportsBySummaryIdDetailsApiResponse,
        GetApiFinancialsItdReportsBySummaryIdDetailsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/itd-reports/${queryArg.summaryId}/details`,
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsMetricsBurnRate: build.query<
        GetApiFinancialsMetricsBurnRateApiResponse,
        GetApiFinancialsMetricsBurnRateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/metrics/burn-rate`,
          params: {
            programId: queryArg.programId,
            projectId: queryArg.projectId,
            clinId: queryArg.clinId,
            period: queryArg.period,
          },
        }),
        providesTags: ['Financials'],
      }),
      getApiFinancialsTabularData: build.query<
        GetApiFinancialsTabularDataApiResponse,
        GetApiFinancialsTabularDataApiArg
      >({
        query: (queryArg) => ({
          url: `/api/financials/tabular-data`,
          params: {
            programId: queryArg.programId,
            view: queryArg.view,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Financials'],
      }),
      getApiForms: build.query<GetApiFormsApiResponse, GetApiFormsApiArg>({
        query: () => ({ url: `/api/forms` }),
        providesTags: ['Forms'],
      }),
      getApiFormsTypes: build.query<
        GetApiFormsTypesApiResponse,
        GetApiFormsTypesApiArg
      >({
        query: () => ({ url: `/api/forms/types` }),
        providesTags: ['Forms'],
      }),
      getApiFormsById: build.query<
        GetApiFormsByIdApiResponse,
        GetApiFormsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/forms/${queryArg.id}`,
          params: {
            version: queryArg.version,
          },
        }),
        providesTags: ['Forms'],
      }),
      getApiHealth: build.query<GetApiHealthApiResponse, GetApiHealthApiArg>({
        query: () => ({ url: `/api/health` }),
        providesTags: ['Health'],
      }),
      getApiLibrary: build.query<GetApiLibraryApiResponse, GetApiLibraryApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/library`,
            params: {
              includeAll: queryArg.includeAll,
            },
          }),
          providesTags: ['Library'],
        },
      ),
      getApiLibraryDirectories: build.query<
        GetApiLibraryDirectoriesApiResponse,
        GetApiLibraryDirectoriesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library-directories`,
          params: {
            programId: queryArg.programId,
            projectId: queryArg.projectId,
            parentDirectoryId: queryArg.parentDirectoryId,
          },
        }),
        providesTags: ['Library Directories'],
      }),
      postApiLibraryDirectories: build.mutation<
        PostApiLibraryDirectoriesApiResponse,
        PostApiLibraryDirectoriesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library-directories`,
          method: 'POST',
          body: queryArg.handlersCreateLibraryDirectoryRequest,
        }),
        invalidatesTags: ['Library Directories'],
      }),
      getApiLibraryDirectoriesByDirectoryId: build.query<
        GetApiLibraryDirectoriesByDirectoryIdApiResponse,
        GetApiLibraryDirectoriesByDirectoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library-directories/${queryArg.directoryId}`,
        }),
        providesTags: ['Library Directories'],
      }),
      deleteApiLibraryDirectoriesByDirectoryId: build.mutation<
        DeleteApiLibraryDirectoriesByDirectoryIdApiResponse,
        DeleteApiLibraryDirectoriesByDirectoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library-directories/${queryArg.directoryId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Library Directories'],
      }),
      patchApiLibraryDirectoriesByDirectoryId: build.mutation<
        PatchApiLibraryDirectoriesByDirectoryIdApiResponse,
        PatchApiLibraryDirectoriesByDirectoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library-directories/${queryArg.directoryId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateLibraryDirectoryRequest,
        }),
        invalidatesTags: ['Library Directories'],
      }),
      postApiLibraryLabelByIdAndNewLabel: build.mutation<
        PostApiLibraryLabelByIdAndNewLabelApiResponse,
        PostApiLibraryLabelByIdAndNewLabelApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library/label/${queryArg.id}/${queryArg.newLabel}`,
          method: 'POST',
        }),
        invalidatesTags: ['Library'],
      }),
      postApiLibraryMoveByIdAndNewParentId: build.mutation<
        PostApiLibraryMoveByIdAndNewParentIdApiResponse,
        PostApiLibraryMoveByIdAndNewParentIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library/move/${queryArg.id}/${queryArg.newParentId}`,
          method: 'POST',
        }),
        invalidatesTags: ['Library'],
      }),
      getApiLibraryTree: build.query<
        GetApiLibraryTreeApiResponse,
        GetApiLibraryTreeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/library/tree`,
          params: {
            includeAll: queryArg.includeAll,
          },
        }),
        providesTags: ['Library'],
      }),
      getApiLibraryById: build.query<
        GetApiLibraryByIdApiResponse,
        GetApiLibraryByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/library/${queryArg.id}` }),
        providesTags: ['Library'],
      }),
      getApiLibraryByIdDownload: build.query<
        GetApiLibraryByIdDownloadApiResponse,
        GetApiLibraryByIdDownloadApiArg
      >({
        query: (queryArg) => ({ url: `/api/library/${queryArg.id}/download` }),
        providesTags: ['Library'],
      }),
      getApiLocationTypes: build.query<
        GetApiLocationTypesApiResponse,
        GetApiLocationTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/location-types`,
          params: {
            includeArchived: queryArg.includeArchived,
          },
        }),
        providesTags: ['Locations'],
      }),
      postApiLocationTypes: build.mutation<
        PostApiLocationTypesApiResponse,
        PostApiLocationTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/location-types`,
          method: 'POST',
          body: queryArg.handlersLocationTypeRequest,
        }),
        invalidatesTags: ['Locations'],
      }),
      getApiLocationTypesByLocationType: build.query<
        GetApiLocationTypesByLocationTypeApiResponse,
        GetApiLocationTypesByLocationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/location-types/${queryArg.locationType}`,
        }),
        providesTags: ['Locations'],
      }),
      deleteApiLocationTypesByLocationType: build.mutation<
        DeleteApiLocationTypesByLocationTypeApiResponse,
        DeleteApiLocationTypesByLocationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/location-types/${queryArg.locationType}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Locations'],
      }),
      patchApiLocationTypesByLocationType: build.mutation<
        PatchApiLocationTypesByLocationTypeApiResponse,
        PatchApiLocationTypesByLocationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/location-types/${queryArg.locationType}`,
          method: 'PATCH',
          body: queryArg.handlersLocationTypeUpdateRequest,
        }),
        invalidatesTags: ['Locations'],
      }),
      getApiLocations: build.query<
        GetApiLocationsApiResponse,
        GetApiLocationsApiArg
      >({
        query: () => ({ url: `/api/locations` }),
        providesTags: ['Locations'],
      }),
      postApiLocations: build.mutation<
        PostApiLocationsApiResponse,
        PostApiLocationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations`,
          method: 'POST',
          body: queryArg.handlersCreateLocationRequest,
        }),
        invalidatesTags: ['Locations'],
      }),
      getApiLocationsById: build.query<
        GetApiLocationsByIdApiResponse,
        GetApiLocationsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/locations/${queryArg.id}` }),
        providesTags: ['Locations'],
      }),
      deleteApiLocationsById: build.mutation<
        DeleteApiLocationsByIdApiResponse,
        DeleteApiLocationsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Locations'],
      }),
      patchApiLocationsById: build.mutation<
        PatchApiLocationsByIdApiResponse,
        PatchApiLocationsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/locations/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateLocationRequest,
        }),
        invalidatesTags: ['Locations'],
      }),
      getApiLogout: build.query<GetApiLogoutApiResponse, GetApiLogoutApiArg>({
        query: () => ({ url: `/api/logout` }),
        providesTags: ['Authentication'],
      }),
      getApiMasterSchedule: build.query<
        GetApiMasterScheduleApiResponse,
        GetApiMasterScheduleApiArg
      >({
        query: () => ({ url: `/api/master-schedule` }),
        providesTags: ['Master Schedule'],
      }),
      postApiMasterScheduleUpload: build.mutation<
        PostApiMasterScheduleUploadApiResponse,
        PostApiMasterScheduleUploadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/upload`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Master Schedule'],
      }),
      getApiMasterScheduleByScheduleId: build.query<
        GetApiMasterScheduleByScheduleIdApiResponse,
        GetApiMasterScheduleByScheduleIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/${queryArg.scheduleId}`,
        }),
        providesTags: ['Master Schedule'],
      }),
      deleteApiMasterScheduleByScheduleId: build.mutation<
        DeleteApiMasterScheduleByScheduleIdApiResponse,
        DeleteApiMasterScheduleByScheduleIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/${queryArg.scheduleId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Master Schedule'],
      }),
      patchApiMasterScheduleByScheduleId: build.mutation<
        PatchApiMasterScheduleByScheduleIdApiResponse,
        PatchApiMasterScheduleByScheduleIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/${queryArg.scheduleId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateMasterScheduleRequest,
        }),
        invalidatesTags: ['Master Schedule'],
      }),
      getApiMasterScheduleByScheduleIdMilestones: build.query<
        GetApiMasterScheduleByScheduleIdMilestonesApiResponse,
        GetApiMasterScheduleByScheduleIdMilestonesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/${queryArg.scheduleId}/milestones`,
        }),
        providesTags: ['Master Schedule'],
      }),
      getApiMasterScheduleByScheduleIdStatus: build.query<
        GetApiMasterScheduleByScheduleIdStatusApiResponse,
        GetApiMasterScheduleByScheduleIdStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/${queryArg.scheduleId}/status`,
        }),
        providesTags: ['Master Schedule'],
      }),
      getApiMasterScheduleByScheduleIdTasks: build.query<
        GetApiMasterScheduleByScheduleIdTasksApiResponse,
        GetApiMasterScheduleByScheduleIdTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/master-schedule/${queryArg.scheduleId}/tasks`,
        }),
        providesTags: ['Master Schedule'],
      }),
      postApiObjectTypes: build.mutation<
        PostApiObjectTypesApiResponse,
        PostApiObjectTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/object-types`,
          method: 'POST',
          body: queryArg.handlersObjectTypeRequest,
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      getApiObjectTypesByObjectType: build.query<
        GetApiObjectTypesByObjectTypeApiResponse,
        GetApiObjectTypesByObjectTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/object-types/${queryArg.objectType}`,
        }),
        providesTags: ['Type Catalogs'],
      }),
      deleteApiObjectTypesByObjectType: build.mutation<
        DeleteApiObjectTypesByObjectTypeApiResponse,
        DeleteApiObjectTypesByObjectTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/object-types/${queryArg.objectType}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      patchApiObjectTypesByObjectType: build.mutation<
        PatchApiObjectTypesByObjectTypeApiResponse,
        PatchApiObjectTypesByObjectTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/object-types/${queryArg.objectType}`,
          method: 'PATCH',
          body: queryArg.handlersObjectTypeUpdateRequest,
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      getApiOrganizations: build.query<
        GetApiOrganizationsApiResponse,
        GetApiOrganizationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations`,
          params: {
            organizationType: queryArg.organizationType,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['Organizations'],
      }),
      postApiOrganizations: build.mutation<
        PostApiOrganizationsApiResponse,
        PostApiOrganizationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations`,
          method: 'POST',
          body: queryArg.handlersCreateOrganizationRequest,
        }),
        invalidatesTags: ['Organizations'],
      }),
      getApiOrganizationsTypes: build.query<
        GetApiOrganizationsTypesApiResponse,
        GetApiOrganizationsTypesApiArg
      >({
        query: () => ({ url: `/api/organizations/types` }),
        providesTags: ['Organizations'],
      }),
      postApiOrganizationsTypes: build.mutation<
        PostApiOrganizationsTypesApiResponse,
        PostApiOrganizationsTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations/types`,
          method: 'POST',
          body: queryArg.handlersSimpleCatalogRequest,
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      getApiOrganizationsTypesByOrganizationType: build.query<
        GetApiOrganizationsTypesByOrganizationTypeApiResponse,
        GetApiOrganizationsTypesByOrganizationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations/types/${queryArg.organizationType}`,
        }),
        providesTags: ['Type Catalogs'],
      }),
      deleteApiOrganizationsTypesByOrganizationType: build.mutation<
        DeleteApiOrganizationsTypesByOrganizationTypeApiResponse,
        DeleteApiOrganizationsTypesByOrganizationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations/types/${queryArg.organizationType}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      patchApiOrganizationsTypesByOrganizationType: build.mutation<
        PatchApiOrganizationsTypesByOrganizationTypeApiResponse,
        PatchApiOrganizationsTypesByOrganizationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations/types/${queryArg.organizationType}`,
          method: 'PATCH',
          body: queryArg.handlersSimpleCatalogUpdateRequest,
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      getApiOrganizationsById: build.query<
        GetApiOrganizationsByIdApiResponse,
        GetApiOrganizationsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/organizations/${queryArg.id}` }),
        providesTags: ['Organizations'],
      }),
      deleteApiOrganizationsById: build.mutation<
        DeleteApiOrganizationsByIdApiResponse,
        DeleteApiOrganizationsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Organizations'],
      }),
      patchApiOrganizationsById: build.mutation<
        PatchApiOrganizationsByIdApiResponse,
        PatchApiOrganizationsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/organizations/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateOrganizationRequest,
        }),
        invalidatesTags: ['Organizations'],
      }),
      getApiPlatformConfigForms: build.query<
        GetApiPlatformConfigFormsApiResponse,
        GetApiPlatformConfigFormsApiArg
      >({
        query: () => ({ url: `/api/platform-config/forms` }),
        providesTags: ['Platform Config'],
      }),
      putApiPlatformConfigForms: build.mutation<
        PutApiPlatformConfigFormsApiResponse,
        PutApiPlatformConfigFormsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/platform-config/forms`,
          method: 'PUT',
          body: queryArg.handlersUpdateFormVisibilityRequest,
        }),
        invalidatesTags: ['Platform Config'],
      }),
      getApiPnrs: build.query<GetApiPnrsApiResponse, GetApiPnrsApiArg>({
        query: (queryArg) => ({
          url: `/api/pnrs`,
          params: {
            projectId: queryArg.projectId,
            currentVersion: queryArg.currentVersion,
            search: queryArg.search,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['PNR (Problem Notification Report)'],
      }),
      postApiPnrs: build.mutation<PostApiPnrsApiResponse, PostApiPnrsApiArg>({
        query: (queryArg) => ({
          url: `/api/pnrs`,
          method: 'POST',
          body: queryArg.handlersCreatePnrRequest,
        }),
        invalidatesTags: ['PNR (Problem Notification Report)'],
      }),
      getApiPnrsById: build.query<
        GetApiPnrsByIdApiResponse,
        GetApiPnrsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/pnrs/${queryArg.id}` }),
        providesTags: ['PNR (Problem Notification Report)'],
      }),
      deleteApiPnrsById: build.mutation<
        DeleteApiPnrsByIdApiResponse,
        DeleteApiPnrsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/pnrs/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['PNR (Problem Notification Report)'],
      }),
      patchApiPnrsById: build.mutation<
        PatchApiPnrsByIdApiResponse,
        PatchApiPnrsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/pnrs/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdatePnrRequest,
        }),
        invalidatesTags: ['PNR (Problem Notification Report)'],
      }),
      postApiPnrsByIdSubmit: build.mutation<
        PostApiPnrsByIdSubmitApiResponse,
        PostApiPnrsByIdSubmitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/pnrs/${queryArg.id}/submit`,
          method: 'POST',
        }),
        invalidatesTags: ['PNR (Problem Notification Report)'],
      }),
      getApiPnrsByIdVAndVersion: build.query<
        GetApiPnrsByIdVAndVersionApiResponse,
        GetApiPnrsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/pnrs/${queryArg.id}/v/${queryArg.version}`,
        }),
        providesTags: ['PNR (Problem Notification Report)'],
      }),
      getApiPnrsByIdVersions: build.query<
        GetApiPnrsByIdVersionsApiResponse,
        GetApiPnrsByIdVersionsApiArg
      >({
        query: (queryArg) => ({ url: `/api/pnrs/${queryArg.id}/versions` }),
        providesTags: ['PNR (Problem Notification Report)'],
      }),
      postApiPowerbiEmbedToken: build.mutation<
        PostApiPowerbiEmbedTokenApiResponse,
        PostApiPowerbiEmbedTokenApiArg
      >({
        query: (queryArg) => ({
          url: `/api/powerbi/embed-token`,
          method: 'POST',
          body: queryArg.handlersGetEmbedTokenRequest,
        }),
        invalidatesTags: ['PowerBI'],
      }),
      getApiPrograms: build.query<
        GetApiProgramsApiResponse,
        GetApiProgramsApiArg
      >({
        query: () => ({ url: `/api/programs` }),
        providesTags: ['Programs'],
      }),
      postApiPrograms: build.mutation<
        PostApiProgramsApiResponse,
        PostApiProgramsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs`,
          method: 'POST',
          body: queryArg.handlersCreateProgramRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProgramsAvailableDeliverableTypes: build.query<
        GetApiProgramsAvailableDeliverableTypesApiResponse,
        GetApiProgramsAvailableDeliverableTypesApiArg
      >({
        query: () => ({ url: `/api/programs/available-deliverable-types` }),
        providesTags: ['Programs'],
      }),
      getApiProgramsDeliverableTypes: build.query<
        GetApiProgramsDeliverableTypesApiResponse,
        GetApiProgramsDeliverableTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/deliverable-types`,
          params: {
            programId: queryArg.programId,
          },
        }),
        providesTags: ['Programs'],
      }),
      getApiProgramsDeltekContracts: build.query<
        GetApiProgramsDeltekContractsApiResponse,
        GetApiProgramsDeltekContractsApiArg
      >({
        query: () => ({ url: `/api/programs/deltek-contracts` }),
        providesTags: ['Programs'],
      }),
      getApiProgramsById: build.query<
        GetApiProgramsByIdApiResponse,
        GetApiProgramsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/programs/${queryArg.id}` }),
        providesTags: ['Programs'],
      }),
      deleteApiProgramsById: build.mutation<
        DeleteApiProgramsByIdApiResponse,
        DeleteApiProgramsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      patchApiProgramsById: build.mutation<
        PatchApiProgramsByIdApiResponse,
        PatchApiProgramsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateProgramRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProgramsByIdDeliverableTypes: build.query<
        GetApiProgramsByIdDeliverableTypesApiResponse,
        GetApiProgramsByIdDeliverableTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/deliverable-types`,
        }),
        providesTags: ['Programs'],
      }),
      postApiProgramsByIdDeliverableTypes: build.mutation<
        PostApiProgramsByIdDeliverableTypesApiResponse,
        PostApiProgramsByIdDeliverableTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/deliverable-types`,
          method: 'POST',
          body: queryArg.handlersProgramDeliverableTypesRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      deleteApiProgramsByIdDeliverableTypesAndDeliverableTypeId: build.mutation<
        DeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiResponse,
        DeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/deliverable-types/${queryArg.deliverableTypeId}`,
          method: 'DELETE',
          params: {
            deliverableNumber: queryArg.deliverableNumber,
          },
        }),
        invalidatesTags: ['Programs'],
      }),
      patchApiProgramsByIdDeliverableTypesAndDeliverableTypeId: build.mutation<
        PatchApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiResponse,
        PatchApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/deliverable-types/${queryArg.deliverableTypeId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateProgramDeliverableTypeRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProgramsByIdLocations: build.query<
        GetApiProgramsByIdLocationsApiResponse,
        GetApiProgramsByIdLocationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/locations`,
        }),
        providesTags: ['Programs'],
      }),
      postApiProgramsByIdLocations: build.mutation<
        PostApiProgramsByIdLocationsApiResponse,
        PostApiProgramsByIdLocationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/locations`,
          method: 'POST',
          body: queryArg.handlersAddProgramLocationRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      deleteApiProgramsByIdLocationsAndLocationId: build.mutation<
        DeleteApiProgramsByIdLocationsAndLocationIdApiResponse,
        DeleteApiProgramsByIdLocationsAndLocationIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/locations/${queryArg.locationId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProgramsByIdObjectTypes: build.query<
        GetApiProgramsByIdObjectTypesApiResponse,
        GetApiProgramsByIdObjectTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/object-types`,
        }),
        providesTags: ['Programs'],
      }),
      postApiProgramsByIdObjectTypes: build.mutation<
        PostApiProgramsByIdObjectTypesApiResponse,
        PostApiProgramsByIdObjectTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/object-types`,
          method: 'POST',
          body: queryArg.handlersAddProgramObjectTypeRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      deleteApiProgramsByIdObjectTypesAndObjectType: build.mutation<
        DeleteApiProgramsByIdObjectTypesAndObjectTypeApiResponse,
        DeleteApiProgramsByIdObjectTypesAndObjectTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/object-types/${queryArg.objectType}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProgramsByIdRoleAssignments: build.query<
        GetApiProgramsByIdRoleAssignmentsApiResponse,
        GetApiProgramsByIdRoleAssignmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/role-assignments`,
        }),
        providesTags: ['Programs'],
      }),
      getApiProgramsByIdRoles: build.query<
        GetApiProgramsByIdRolesApiResponse,
        GetApiProgramsByIdRolesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles`,
          params: {
            includeArchived: queryArg.includeArchived,
          },
        }),
        providesTags: ['Roles'],
      }),
      postApiProgramsByIdRoles: build.mutation<
        PostApiProgramsByIdRolesApiResponse,
        PostApiProgramsByIdRolesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles`,
          method: 'POST',
          body: queryArg.handlersCreateRoleRequest,
        }),
        invalidatesTags: ['Roles'],
      }),
      getApiProgramsByIdRolesAndRoleName: build.query<
        GetApiProgramsByIdRolesAndRoleNameApiResponse,
        GetApiProgramsByIdRolesAndRoleNameApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles/${queryArg.roleName}`,
        }),
        providesTags: ['Roles'],
      }),
      deleteApiProgramsByIdRolesAndRoleName: build.mutation<
        DeleteApiProgramsByIdRolesAndRoleNameApiResponse,
        DeleteApiProgramsByIdRolesAndRoleNameApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles/${queryArg.roleName}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Roles'],
      }),
      patchApiProgramsByIdRolesAndRoleName: build.mutation<
        PatchApiProgramsByIdRolesAndRoleNameApiResponse,
        PatchApiProgramsByIdRolesAndRoleNameApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles/${queryArg.roleName}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateRoleRequest,
        }),
        invalidatesTags: ['Roles'],
      }),
      getApiProgramsByIdRolesAndRoleNameActions: build.query<
        GetApiProgramsByIdRolesAndRoleNameActionsApiResponse,
        GetApiProgramsByIdRolesAndRoleNameActionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles/${queryArg.roleName}/actions`,
        }),
        providesTags: ['Roles'],
      }),
      postApiProgramsByIdRolesAndRoleNameActions: build.mutation<
        PostApiProgramsByIdRolesAndRoleNameActionsApiResponse,
        PostApiProgramsByIdRolesAndRoleNameActionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles/${queryArg.roleName}/actions`,
          method: 'POST',
          body: queryArg.handlersAddRoleActionRequest,
        }),
        invalidatesTags: ['Roles'],
      }),
      deleteApiProgramsByIdRolesAndRoleNameActionsActionName: build.mutation<
        DeleteApiProgramsByIdRolesAndRoleNameActionsActionNameApiResponse,
        DeleteApiProgramsByIdRolesAndRoleNameActionsActionNameApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/roles/${queryArg.roleName}/actions/${queryArg.actionName}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Roles'],
      }),
      getApiProgramsByIdSiteSettings: build.query<
        GetApiProgramsByIdSiteSettingsApiResponse,
        GetApiProgramsByIdSiteSettingsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/site-settings`,
        }),
        providesTags: ['Admin'],
      }),
      putApiProgramsByIdSiteSettingsAdminEmail: build.mutation<
        PutApiProgramsByIdSiteSettingsAdminEmailApiResponse,
        PutApiProgramsByIdSiteSettingsAdminEmailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/site-settings/admin-email`,
          method: 'PUT',
          body: queryArg.handlersUpdateAdminEmailRequest,
        }),
        invalidatesTags: ['Admin'],
      }),
      getApiProgramsByIdSiteSettingsDomains: build.query<
        GetApiProgramsByIdSiteSettingsDomainsApiResponse,
        GetApiProgramsByIdSiteSettingsDomainsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/site-settings/domains`,
        }),
        providesTags: ['Admin'],
      }),
      postApiProgramsByIdSiteSettingsDomains: build.mutation<
        PostApiProgramsByIdSiteSettingsDomainsApiResponse,
        PostApiProgramsByIdSiteSettingsDomainsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/site-settings/domains`,
          method: 'POST',
          body: queryArg.handlersAddDomainRequest,
        }),
        invalidatesTags: ['Admin'],
      }),
      deleteApiProgramsByIdSiteSettingsDomainsAndDomain: build.mutation<
        DeleteApiProgramsByIdSiteSettingsDomainsAndDomainApiResponse,
        DeleteApiProgramsByIdSiteSettingsDomainsAndDomainApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/site-settings/domains/${queryArg.domain}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Admin'],
      }),
      getApiProgramsByIdUserRoles: build.query<
        GetApiProgramsByIdUserRolesApiResponse,
        GetApiProgramsByIdUserRolesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/user-roles`,
        }),
        providesTags: ['Programs'],
      }),
      getApiProgramsByIdWorkflowLabels: build.query<
        GetApiProgramsByIdWorkflowLabelsApiResponse,
        GetApiProgramsByIdWorkflowLabelsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-labels`,
        }),
        providesTags: ['Programs'],
      }),
      patchApiProgramsByIdWorkflowLabels: build.mutation<
        PatchApiProgramsByIdWorkflowLabelsApiResponse,
        PatchApiProgramsByIdWorkflowLabelsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-labels`,
          method: 'PATCH',
          body: queryArg.handlersUpdateWorkflowLabelsRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      deleteApiProgramsByIdWorkflowLabelsStatesAndState: build.mutation<
        DeleteApiProgramsByIdWorkflowLabelsStatesAndStateApiResponse,
        DeleteApiProgramsByIdWorkflowLabelsStatesAndStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-labels/states/${queryArg.state}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      deleteApiProgramsByIdWorkflowLabelsTaskStatesAndState: build.mutation<
        DeleteApiProgramsByIdWorkflowLabelsTaskStatesAndStateApiResponse,
        DeleteApiProgramsByIdWorkflowLabelsTaskStatesAndStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-labels/task-states/${queryArg.state}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProgramsByIdWorkflowTypes: build.query<
        GetApiProgramsByIdWorkflowTypesApiResponse,
        GetApiProgramsByIdWorkflowTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-types`,
          params: { objectType: queryArg.objectType },
        }),
        providesTags: ['Programs'],
      }),
      postApiProgramsByIdWorkflowTypes: build.mutation<
        PostApiProgramsByIdWorkflowTypesApiResponse,
        PostApiProgramsByIdWorkflowTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-types`,
          method: 'POST',
          body: queryArg.handlersProgramWorkflowTypeRequest,
        }),
        invalidatesTags: ['Programs'],
      }),
      deleteApiProgramsByIdWorkflowTypesAndType: build.mutation<
        DeleteApiProgramsByIdWorkflowTypesAndTypeApiResponse,
        DeleteApiProgramsByIdWorkflowTypesAndTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/programs/${queryArg.id}/workflow-types/${queryArg['type']}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Programs'],
      }),
      getApiProjects: build.query<
        GetApiProjectsApiResponse,
        GetApiProjectsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects`,
          params: {
            page: queryArg.page,
            pageSize: queryArg.pageSize,
            programId: queryArg.programId,
            ownerId: queryArg.ownerId,
            locationId: queryArg.locationId,
            includeArchived: queryArg.includeArchived,
            search: queryArg.search,
            projectCode: queryArg.projectCode,
            createdStartDate: queryArg.createdStartDate,
            createdEndDate: queryArg.createdEndDate,
            modifiedStartDate: queryArg.modifiedStartDate,
            modifiedEndDate: queryArg.modifiedEndDate,
          },
        }),
        providesTags: ['Projects'],
      }),
      postApiProjects: build.mutation<
        PostApiProjectsApiResponse,
        PostApiProjectsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects`,
          method: 'POST',
          body: queryArg.handlersCreateProjectRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      getApiProjectsDeliverableTypes: build.query<
        GetApiProjectsDeliverableTypesApiResponse,
        GetApiProjectsDeliverableTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/deliverable-types`,
          params: {
            projectId: queryArg.projectId,
          },
        }),
        providesTags: ['Projects'],
      }),
      getApiProjectsById: build.query<
        GetApiProjectsByIdApiResponse,
        GetApiProjectsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/projects/${queryArg.id}` }),
        providesTags: ['Projects'],
      }),
      deleteApiProjectsById: build.mutation<
        DeleteApiProjectsByIdApiResponse,
        DeleteApiProjectsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Projects'],
      }),
      patchApiProjectsById: build.mutation<
        PatchApiProjectsByIdApiResponse,
        PatchApiProjectsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateProjectRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      getApiProjectsByIdClins: build.query<
        GetApiProjectsByIdClinsApiResponse,
        GetApiProjectsByIdClinsApiArg
      >({
        query: (queryArg) => ({ url: `/api/projects/${queryArg.id}/clins` }),
        providesTags: ['Projects'],
      }),
      postApiProjectsByIdClins: build.mutation<
        PostApiProjectsByIdClinsApiResponse,
        PostApiProjectsByIdClinsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}/clins`,
          method: 'POST',
          body: queryArg.handlersAddProjectClinRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      deleteApiProjectsByIdClinsAndClinId: build.mutation<
        DeleteApiProjectsByIdClinsAndClinIdApiResponse,
        DeleteApiProjectsByIdClinsAndClinIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}/clins/${queryArg.clinId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Projects'],
      }),
      putApiProjectsByIdDeliverableTypes: build.mutation<
        PutApiProjectsByIdDeliverableTypesApiResponse,
        PutApiProjectsByIdDeliverableTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}/deliverable-types`,
          method: 'PUT',
          body: queryArg.handlersProjectDeliverableTypesRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      postApiProjectsByIdDeliverableTypes: build.mutation<
        PostApiProjectsByIdDeliverableTypesApiResponse,
        PostApiProjectsByIdDeliverableTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}/deliverable-types`,
          method: 'POST',
          body: queryArg.handlersProjectDeliverableTypesRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      getApiProjectsByIdSummary: build.query<
        GetApiProjectsByIdSummaryApiResponse,
        GetApiProjectsByIdSummaryApiArg
      >({
        query: (queryArg) => ({ url: `/api/projects/${queryArg.id}/summary` }),
        providesTags: ['Projects'],
      }),
      putApiProjectsByIdSummary: build.mutation<
        PutApiProjectsByIdSummaryApiResponse,
        PutApiProjectsByIdSummaryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}/summary`,
          method: 'PUT',
          body: queryArg.handlersProjectSummaryRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      postApiProjectsByIdSummary: build.mutation<
        PostApiProjectsByIdSummaryApiResponse,
        PostApiProjectsByIdSummaryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.id}/summary`,
          method: 'POST',
          body: queryArg.handlersProjectSummaryRequest,
        }),
        invalidatesTags: ['Projects'],
      }),
      getApiRips: build.query<GetApiRipsApiResponse, GetApiRipsApiArg>({
        query: (queryArg) => ({
          url: `/api/rips`,
          params: {
            projectId: queryArg.projectId,
            toContactId: queryArg.toContactId,
            fromContactId: queryArg.fromContactId,
            currentVersion: queryArg.currentVersion,
            search: queryArg.search,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      postApiRips: build.mutation<PostApiRipsApiResponse, PostApiRipsApiArg>({
        query: (queryArg) => ({
          url: `/api/rips`,
          method: 'POST',
          body: queryArg.handlersCreateRipRequest,
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      getApiRipsById: build.query<
        GetApiRipsByIdApiResponse,
        GetApiRipsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/rips/${queryArg.id}` }),
        providesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      deleteApiRipsById: build.mutation<
        DeleteApiRipsByIdApiResponse,
        DeleteApiRipsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      patchApiRipsById: build.mutation<
        PatchApiRipsByIdApiResponse,
        PatchApiRipsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateRipRequest,
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      postApiRipsByIdEstimate: build.mutation<
        PostApiRipsByIdEstimateApiResponse,
        PostApiRipsByIdEstimateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}/estimate`,
          method: 'POST',
          body: queryArg.handlersCreateEstimateRequest,
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      deleteApiRipsByIdEstimateAndEstimateId: build.mutation<
        DeleteApiRipsByIdEstimateAndEstimateIdApiResponse,
        DeleteApiRipsByIdEstimateAndEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}/estimate/${queryArg.estimateId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      patchApiRipsByIdEstimateAndEstimateId: build.mutation<
        PatchApiRipsByIdEstimateAndEstimateIdApiResponse,
        PatchApiRipsByIdEstimateAndEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}/estimate/${queryArg.estimateId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateEstimateRequest,
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      postApiRipsByIdSubmit: build.mutation<
        PostApiRipsByIdSubmitApiResponse,
        PostApiRipsByIdSubmitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}/submit`,
          method: 'POST',
        }),
        invalidatesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      getApiRipsByIdVAndVersion: build.query<
        GetApiRipsByIdVAndVersionApiResponse,
        GetApiRipsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/rips/${queryArg.id}/v/${queryArg.version}`,
        }),
        providesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      getApiRipsByIdVersions: build.query<
        GetApiRipsByIdVersionsApiResponse,
        GetApiRipsByIdVersionsApiArg
      >({
        query: (queryArg) => ({ url: `/api/rips/${queryArg.id}/versions` }),
        providesTags: ['RIP (Request to Initiate Purchase)'],
      }),
      getApiRisks: build.query<GetApiRisksApiResponse, GetApiRisksApiArg>({
        query: (queryArg) => ({
          url: `/api/risks`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            contractCode: queryArg.contractCode,
            ownerId: queryArg.ownerId,
            escalate: queryArg.escalate,
            isClosure: queryArg.isClosure,
          },
        }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      postApiRisks: build.mutation<PostApiRisksApiResponse, PostApiRisksApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/risks`,
            method: 'POST',
            body: queryArg.handlersIroRiskCreateRequest,
          }),
          invalidatesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
        },
      ),
      getApiRisksCategories: build.query<
        GetApiRisksCategoriesApiResponse,
        GetApiRisksCategoriesApiArg
      >({
        query: () => ({ url: `/api/risks/categories` }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksCategoriesByCategoryCodeImpacts: build.query<
        GetApiRisksCategoriesByCategoryCodeImpactsApiResponse,
        GetApiRisksCategoriesByCategoryCodeImpactsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/risks/categories/${queryArg.categoryCode}/impacts`,
        }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksClosures: build.query<
        GetApiRisksClosuresApiResponse,
        GetApiRisksClosuresApiArg
      >({
        query: () => ({ url: `/api/risks/closures` }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksImpacts: build.query<
        GetApiRisksImpactsApiResponse,
        GetApiRisksImpactsApiArg
      >({
        query: () => ({ url: `/api/risks/impacts` }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksProbabilities: build.query<
        GetApiRisksProbabilitiesApiResponse,
        GetApiRisksProbabilitiesApiArg
      >({
        query: () => ({ url: `/api/risks/probabilities` }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksByRiskId: build.query<
        GetApiRisksByRiskIdApiResponse,
        GetApiRisksByRiskIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/risks/${queryArg.riskId}` }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      patchApiRisksByRiskId: build.mutation<
        PatchApiRisksByRiskIdApiResponse,
        PatchApiRisksByRiskIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/risks/${queryArg.riskId}`,
          method: 'PATCH',
          body: queryArg.handlersIroRiskUpdateRequest,
        }),
        invalidatesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksByRiskIdVAndRecordUpdate: build.query<
        GetApiRisksByRiskIdVAndRecordUpdateApiResponse,
        GetApiRisksByRiskIdVAndRecordUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/risks/${queryArg.riskId}/v/${queryArg.recordUpdate}`,
        }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiRisksByRiskIdVersions: build.query<
        GetApiRisksByRiskIdVersionsApiResponse,
        GetApiRisksByRiskIdVersionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/risks/${queryArg.riskId}/versions`,
        }),
        providesTags: ['IRO Risk (Issues/Risks/Opportunities)'],
      }),
      getApiTamsInventory: build.query<
        GetApiTamsInventoryApiResponse,
        GetApiTamsInventoryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tams/inventory`,
          params: {
            projectCode: queryArg.projectCode,
            location: queryArg.location,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['TAMS Inventory'],
      }),
      postApiTamsInventory: build.mutation<
        PostApiTamsInventoryApiResponse,
        PostApiTamsInventoryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tams/inventory`,
          method: 'POST',
          body: queryArg.handlersCreateTamsInventoryRequest,
        }),
        invalidatesTags: ['TAMS Inventory'],
      }),
      getApiTamsInventoryById: build.query<
        GetApiTamsInventoryByIdApiResponse,
        GetApiTamsInventoryByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/tams/inventory/${queryArg.id}` }),
        providesTags: ['TAMS Inventory'],
      }),
      deleteApiTamsInventoryById: build.mutation<
        DeleteApiTamsInventoryByIdApiResponse,
        DeleteApiTamsInventoryByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tams/inventory/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['TAMS Inventory'],
      }),
      patchApiTamsInventoryById: build.mutation<
        PatchApiTamsInventoryByIdApiResponse,
        PatchApiTamsInventoryByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tams/inventory/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateTamsInventoryRequest,
        }),
        invalidatesTags: ['TAMS Inventory'],
      }),
      getApiTars: build.query<GetApiTarsApiResponse, GetApiTarsApiArg>({
        query: (queryArg) => ({
          url: `/api/tars`,
          params: {
            projectId: queryArg.projectId,
            tarContactId: queryArg.tarContactId,
            currentVersion: queryArg.currentVersion,
            search: queryArg.search,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['TAR (Travel Authorization Request)'],
      }),
      postApiTars: build.mutation<PostApiTarsApiResponse, PostApiTarsApiArg>({
        query: (queryArg) => ({
          url: `/api/tars`,
          method: 'POST',
          body: queryArg.handlersCreateTarRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      getApiTarsById: build.query<
        GetApiTarsByIdApiResponse,
        GetApiTarsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/tars/${queryArg.id}` }),
        providesTags: ['TAR (Travel Authorization Request)'],
      }),
      deleteApiTarsById: build.mutation<
        DeleteApiTarsByIdApiResponse,
        DeleteApiTarsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      patchApiTarsById: build.mutation<
        PatchApiTarsByIdApiResponse,
        PatchApiTarsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateTarRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      postApiTarsByIdEstimate: build.mutation<
        PostApiTarsByIdEstimateApiResponse,
        PostApiTarsByIdEstimateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/estimate`,
          method: 'POST',
          body: queryArg.handlersCreateEstimateRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      deleteApiTarsByIdEstimateAndEstimateId: build.mutation<
        DeleteApiTarsByIdEstimateAndEstimateIdApiResponse,
        DeleteApiTarsByIdEstimateAndEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/estimate/${queryArg.estimateId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      patchApiTarsByIdEstimateAndEstimateId: build.mutation<
        PatchApiTarsByIdEstimateAndEstimateIdApiResponse,
        PatchApiTarsByIdEstimateAndEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/estimate/${queryArg.estimateId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateEstimateRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      postApiTarsByIdItinerary: build.mutation<
        PostApiTarsByIdItineraryApiResponse,
        PostApiTarsByIdItineraryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/itinerary`,
          method: 'POST',
          body: queryArg.handlersCreateItineraryRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      deleteApiTarsByIdItineraryAndItineraryId: build.mutation<
        DeleteApiTarsByIdItineraryAndItineraryIdApiResponse,
        DeleteApiTarsByIdItineraryAndItineraryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/itinerary/${queryArg.itineraryId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      patchApiTarsByIdItineraryAndItineraryId: build.mutation<
        PatchApiTarsByIdItineraryAndItineraryIdApiResponse,
        PatchApiTarsByIdItineraryAndItineraryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/itinerary/${queryArg.itineraryId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateItineraryRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      postApiTarsByIdItineraryAndItineraryIdEstimate: build.mutation<
        PostApiTarsByIdItineraryAndItineraryIdEstimateApiResponse,
        PostApiTarsByIdItineraryAndItineraryIdEstimateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/itinerary/${queryArg.itineraryId}/estimate`,
          method: 'POST',
          body: queryArg.handlersCreateEstimateRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      deleteApiTarsByIdItineraryAndItineraryIdEstimateEstimateId:
        build.mutation<
          DeleteApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiResponse,
          DeleteApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiArg
        >({
          query: (queryArg) => ({
            url: `/api/tars/${queryArg.id}/itinerary/${queryArg.itineraryId}/estimate/${queryArg.estimateId}`,
            method: 'DELETE',
          }),
          invalidatesTags: ['TAR (Travel Authorization Request)'],
        }),
      patchApiTarsByIdItineraryAndItineraryIdEstimateEstimateId: build.mutation<
        PatchApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiResponse,
        PatchApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/itinerary/${queryArg.itineraryId}/estimate/${queryArg.estimateId}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateEstimateRequest,
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      postApiTarsByIdSubmit: build.mutation<
        PostApiTarsByIdSubmitApiResponse,
        PostApiTarsByIdSubmitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/submit`,
          method: 'POST',
        }),
        invalidatesTags: ['TAR (Travel Authorization Request)'],
      }),
      getApiTarsByIdVAndVersion: build.query<
        GetApiTarsByIdVAndVersionApiResponse,
        GetApiTarsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/tars/${queryArg.id}/v/${queryArg.version}`,
        }),
        providesTags: ['TAR (Travel Authorization Request)'],
      }),
      getApiTarsByIdVersions: build.query<
        GetApiTarsByIdVersionsApiResponse,
        GetApiTarsByIdVersionsApiArg
      >({
        query: (queryArg) => ({ url: `/api/tars/${queryArg.id}/versions` }),
        providesTags: ['TAR (Travel Authorization Request)'],
      }),
      getApiToken: build.query<GetApiTokenApiResponse, GetApiTokenApiArg>({
        query: (queryArg) => ({
          url: `/api/token`,
          headers: {
            login_token: queryArg.loginToken,
          },
        }),
        providesTags: ['Authentication'],
      }),
      getApiTripReports: build.query<
        GetApiTripReportsApiResponse,
        GetApiTripReportsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports`,
          params: {
            projectId: queryArg.projectId,
            tarId: queryArg.tarId,
            travelerId: queryArg.travelerId,
            currentVersion: queryArg.currentVersion,
            search: queryArg.search,
            page: queryArg.page,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['TR (Trip Report)'],
      }),
      postApiTripReports: build.mutation<
        PostApiTripReportsApiResponse,
        PostApiTripReportsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports`,
          method: 'POST',
          body: queryArg.handlersCreateTrRequest,
        }),
        invalidatesTags: ['TR (Trip Report)'],
      }),
      getApiTripReportsById: build.query<
        GetApiTripReportsByIdApiResponse,
        GetApiTripReportsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/trip-reports/${queryArg.id}` }),
        providesTags: ['TR (Trip Report)'],
      }),
      deleteApiTripReportsById: build.mutation<
        DeleteApiTripReportsByIdApiResponse,
        DeleteApiTripReportsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['TR (Trip Report)'],
      }),
      patchApiTripReportsById: build.mutation<
        PatchApiTripReportsByIdApiResponse,
        PatchApiTripReportsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateTrRequest,
        }),
        invalidatesTags: ['TR (Trip Report)'],
      }),
      postApiTripReportsByIdSubmit: build.mutation<
        PostApiTripReportsByIdSubmitApiResponse,
        PostApiTripReportsByIdSubmitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports/${queryArg.id}/submit`,
          method: 'POST',
        }),
        invalidatesTags: ['TR (Trip Report)'],
      }),
      getApiTripReportsByIdVAndVersion: build.query<
        GetApiTripReportsByIdVAndVersionApiResponse,
        GetApiTripReportsByIdVAndVersionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports/${queryArg.id}/v/${queryArg.version}`,
        }),
        providesTags: ['TR (Trip Report)'],
      }),
      getApiTripReportsByIdVersions: build.query<
        GetApiTripReportsByIdVersionsApiResponse,
        GetApiTripReportsByIdVersionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/trip-reports/${queryArg.id}/versions`,
        }),
        providesTags: ['TR (Trip Report)'],
      }),
      getApiTytoauth: build.query<
        GetApiTytoauthApiResponse,
        GetApiTytoauthApiArg
      >({
        query: () => ({ url: `/api/tytoauth` }),
        providesTags: ['Authentication'],
      }),
      postApiTytoauth: build.mutation<
        PostApiTytoauthApiResponse,
        PostApiTytoauthApiArg
      >({
        query: () => ({ url: `/api/tytoauth`, method: 'POST' }),
        invalidatesTags: ['Authentication'],
      }),
      getApiUsers: build.query<GetApiUsersApiResponse, GetApiUsersApiArg>({
        query: () => ({ url: `/api/users` }),
        providesTags: ['Users'],
      }),
      postApiUsers: build.mutation<PostApiUsersApiResponse, PostApiUsersApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/users`,
            method: 'POST',
            body: queryArg.handlersCreateUserRequest,
          }),
          invalidatesTags: ['Users'],
        },
      ),
      getApiUsersActions: build.query<
        GetApiUsersActionsApiResponse,
        GetApiUsersActionsApiArg
      >({
        query: () => ({ url: `/api/users/actions` }),
        providesTags: ['Users'],
      }),
      getApiUsersMe: build.query<GetApiUsersMeApiResponse, GetApiUsersMeApiArg>(
        {
          query: () => ({ url: `/api/users/me` }),
          providesTags: ['Users'],
        },
      ),
      getApiUsersMeNotificationPreferences: build.query<
        GetApiUsersMeNotificationPreferencesApiResponse,
        GetApiUsersMeNotificationPreferencesApiArg
      >({
        query: () => ({ url: `/api/users/me/notification-preferences` }),
        providesTags: ['Users'],
      }),
      putApiUsersMeNotificationPreferences: build.mutation<
        PutApiUsersMeNotificationPreferencesApiResponse,
        PutApiUsersMeNotificationPreferencesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/me/notification-preferences`,
          method: 'PUT',
          body: queryArg.handlersUpdateNotificationPreferencesRequest,
        }),
        invalidatesTags: ['Users'],
      }),
      getApiUsersRoles: build.query<
        GetApiUsersRolesApiResponse,
        GetApiUsersRolesApiArg
      >({
        query: () => ({ url: `/api/users/roles` }),
        providesTags: ['Users'],
      }),
      getApiUsersTypes: build.query<
        GetApiUsersTypesApiResponse,
        GetApiUsersTypesApiArg
      >({
        query: () => ({ url: `/api/users/types` }),
        providesTags: ['Users'],
      }),
      postApiUsersTypes: build.mutation<
        PostApiUsersTypesApiResponse,
        PostApiUsersTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/types`,
          method: 'POST',
          body: queryArg.handlersSimpleCatalogRequest,
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      getApiUsersTypesByUserType: build.query<
        GetApiUsersTypesByUserTypeApiResponse,
        GetApiUsersTypesByUserTypeApiArg
      >({
        query: (queryArg) => ({ url: `/api/users/types/${queryArg.userType}` }),
        providesTags: ['Type Catalogs'],
      }),
      deleteApiUsersTypesByUserType: build.mutation<
        DeleteApiUsersTypesByUserTypeApiResponse,
        DeleteApiUsersTypesByUserTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/types/${queryArg.userType}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      patchApiUsersTypesByUserType: build.mutation<
        PatchApiUsersTypesByUserTypeApiResponse,
        PatchApiUsersTypesByUserTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/types/${queryArg.userType}`,
          method: 'PATCH',
          body: queryArg.handlersSimpleCatalogUpdateRequest,
        }),
        invalidatesTags: ['Type Catalogs'],
      }),
      getApiUsersById: build.query<
        GetApiUsersByIdApiResponse,
        GetApiUsersByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}` }),
        providesTags: ['Users'],
      }),
      deleteApiUsersById: build.mutation<
        DeleteApiUsersByIdApiResponse,
        DeleteApiUsersByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Users'],
      }),
      patchApiUsersById: build.mutation<
        PatchApiUsersByIdApiResponse,
        PatchApiUsersByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersUpdateUserRequest,
        }),
        invalidatesTags: ['Users'],
      }),
      getApiUsersByIdAssignedTasks: build.query<
        GetApiUsersByIdAssignedTasksApiResponse,
        GetApiUsersByIdAssignedTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.id}/assigned-tasks`,
        }),
        providesTags: ['Users'],
      }),
      getApiUsersByIdWorkflows: build.query<
        GetApiUsersByIdWorkflowsApiResponse,
        GetApiUsersByIdWorkflowsApiArg
      >({
        query: (queryArg) => ({ url: `/api/users/${queryArg.id}/workflows` }),
        providesTags: ['Users'],
      }),
      getApiUsersByUserIdRoleProjects: build.query<
        GetApiUsersByUserIdRoleProjectsApiResponse,
        GetApiUsersByUserIdRoleProjectsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.userId}/role-projects`,
        }),
        providesTags: ['Users'],
      }),
      postApiUsersByUserIdRoleProjects: build.mutation<
        PostApiUsersByUserIdRoleProjectsApiResponse,
        PostApiUsersByUserIdRoleProjectsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.userId}/role-projects`,
          method: 'POST',
          body: queryArg.handlersAddUserRoleProjectRequest,
        }),
        invalidatesTags: ['Users'],
      }),
      deleteApiUsersByUserIdRoleProjectsAndRoleNameProgramIdProjectId:
        build.mutation<
          DeleteApiUsersByUserIdRoleProjectsAndRoleNameProgramIdProjectIdApiResponse,
          DeleteApiUsersByUserIdRoleProjectsAndRoleNameProgramIdProjectIdApiArg
        >({
          query: (queryArg) => ({
            url: `/api/users/${queryArg.userId}/role-projects/${queryArg.roleName}/${queryArg.programId}/${queryArg.projectId}`,
            method: 'DELETE',
          }),
          invalidatesTags: ['Users'],
        }),
      getApiUsersByUserIdRoles: build.query<
        GetApiUsersByUserIdRolesApiResponse,
        GetApiUsersByUserIdRolesApiArg
      >({
        query: (queryArg) => ({ url: `/api/users/${queryArg.userId}/roles` }),
        providesTags: ['Users'],
      }),
      postApiUsersByUserIdRoles: build.mutation<
        PostApiUsersByUserIdRolesApiResponse,
        PostApiUsersByUserIdRolesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.userId}/roles`,
          method: 'POST',
          body: queryArg.handlersAddUserRoleRequest,
        }),
        invalidatesTags: ['Users'],
      }),
      deleteApiUsersByUserIdRolesAndRoleNameProgramId: build.mutation<
        DeleteApiUsersByUserIdRolesAndRoleNameProgramIdApiResponse,
        DeleteApiUsersByUserIdRolesAndRoleNameProgramIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/users/${queryArg.userId}/roles/${queryArg.roleName}/${queryArg.programId}`,
          method: 'DELETE',
          params: {
            cascade: queryArg.cascade,
          },
        }),
        invalidatesTags: ['Users'],
      }),
      getApiVersion: build.query<GetApiVersionApiResponse, GetApiVersionApiArg>(
        {
          query: () => ({ url: `/api/version` }),
          providesTags: ['Health'],
        },
      ),
      getApiWorkflows: build.query<
        GetApiWorkflowsApiResponse,
        GetApiWorkflowsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows`,
          params: {
            objectType: queryArg.objectType,
            objectId: queryArg.objectId,
            workflowType: queryArg.workflowType,
            workflowState: queryArg.workflowState,
            page: queryArg.page,
            limit: queryArg.limit,
          },
        }),
        providesTags: ['Workflows'],
      }),
      postApiWorkflows: build.mutation<
        PostApiWorkflowsApiResponse,
        PostApiWorkflowsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows`,
          method: 'POST',
          body: queryArg.handlersWorkflowCreateRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      getApiWorkflowsStates: build.query<
        GetApiWorkflowsStatesApiResponse,
        GetApiWorkflowsStatesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/states`,
          params: {
            archived: queryArg.archived,
          },
        }),
        providesTags: ['Workflows'],
      }),
      postApiWorkflowsStates: build.mutation<
        PostApiWorkflowsStatesApiResponse,
        PostApiWorkflowsStatesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/states`,
          method: 'POST',
          body: queryArg.handlersWorkflowCatalogRequest,
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      getApiWorkflowsStatesByState: build.query<
        GetApiWorkflowsStatesByStateApiResponse,
        GetApiWorkflowsStatesByStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/states/${queryArg.state}`,
        }),
        providesTags: ['Workflow Catalog'],
      }),
      deleteApiWorkflowsStatesByState: build.mutation<
        DeleteApiWorkflowsStatesByStateApiResponse,
        DeleteApiWorkflowsStatesByStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/states/${queryArg.state}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      patchApiWorkflowsStatesByState: build.mutation<
        PatchApiWorkflowsStatesByStateApiResponse,
        PatchApiWorkflowsStatesByStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/states/${queryArg.state}`,
          method: 'PATCH',
          body: queryArg.handlersWorkflowCatalogUpdateRequest,
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      getApiWorkflowsTaskStates: build.query<
        GetApiWorkflowsTaskStatesApiResponse,
        GetApiWorkflowsTaskStatesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/task-states`,
          params: {
            archived: queryArg.archived,
          },
        }),
        providesTags: ['Workflows'],
      }),
      postApiWorkflowsTaskStates: build.mutation<
        PostApiWorkflowsTaskStatesApiResponse,
        PostApiWorkflowsTaskStatesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/task-states`,
          method: 'POST',
          body: queryArg.handlersWorkflowCatalogRequest,
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      getApiWorkflowsTaskStatesByState: build.query<
        GetApiWorkflowsTaskStatesByStateApiResponse,
        GetApiWorkflowsTaskStatesByStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/task-states/${queryArg.state}`,
        }),
        providesTags: ['Workflow Catalog'],
      }),
      deleteApiWorkflowsTaskStatesByState: build.mutation<
        DeleteApiWorkflowsTaskStatesByStateApiResponse,
        DeleteApiWorkflowsTaskStatesByStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/task-states/${queryArg.state}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      patchApiWorkflowsTaskStatesByState: build.mutation<
        PatchApiWorkflowsTaskStatesByStateApiResponse,
        PatchApiWorkflowsTaskStatesByStateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/task-states/${queryArg.state}`,
          method: 'PATCH',
          body: queryArg.handlersWorkflowCatalogUpdateRequest,
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      getApiWorkflowsTypes: build.query<
        GetApiWorkflowsTypesApiResponse,
        GetApiWorkflowsTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/types`,
          params: {
            archived: queryArg.archived,
          },
        }),
        providesTags: ['Workflows'],
      }),
      postApiWorkflowsTypes: build.mutation<
        PostApiWorkflowsTypesApiResponse,
        PostApiWorkflowsTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/types`,
          method: 'POST',
          body: queryArg.handlersWorkflowCatalogRequest,
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      getApiWorkflowsTypesByWorkflowType: build.query<
        GetApiWorkflowsTypesByWorkflowTypeApiResponse,
        GetApiWorkflowsTypesByWorkflowTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/types/${queryArg.workflowType}`,
        }),
        providesTags: ['Workflow Catalog'],
      }),
      deleteApiWorkflowsTypesByWorkflowType: build.mutation<
        DeleteApiWorkflowsTypesByWorkflowTypeApiResponse,
        DeleteApiWorkflowsTypesByWorkflowTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/types/${queryArg.workflowType}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      patchApiWorkflowsTypesByWorkflowType: build.mutation<
        PatchApiWorkflowsTypesByWorkflowTypeApiResponse,
        PatchApiWorkflowsTypesByWorkflowTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/types/${queryArg.workflowType}`,
          method: 'PATCH',
          body: queryArg.handlersWorkflowCatalogUpdateRequest,
        }),
        invalidatesTags: ['Workflow Catalog'],
      }),
      getApiWorkflowsById: build.query<
        GetApiWorkflowsByIdApiResponse,
        GetApiWorkflowsByIdApiArg
      >({
        query: (queryArg) => ({ url: `/api/workflows/${queryArg.id}` }),
        providesTags: ['Workflows'],
      }),
      deleteApiWorkflowsById: build.mutation<
        DeleteApiWorkflowsByIdApiResponse,
        DeleteApiWorkflowsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Workflows'],
      }),
      patchApiWorkflowsById: build.mutation<
        PatchApiWorkflowsByIdApiResponse,
        PatchApiWorkflowsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.handlersWorkflowUpdateRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      postApiWorkflowsByIdComplete: build.mutation<
        PostApiWorkflowsByIdCompleteApiResponse,
        PostApiWorkflowsByIdCompleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/complete`,
          method: 'POST',
        }),
        invalidatesTags: ['Workflows'],
      }),
      postApiWorkflowsByIdStart: build.mutation<
        PostApiWorkflowsByIdStartApiResponse,
        PostApiWorkflowsByIdStartApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/start`,
          method: 'POST',
        }),
        invalidatesTags: ['Workflows'],
      }),
      getApiWorkflowsByIdTasks: build.query<
        GetApiWorkflowsByIdTasksApiResponse,
        GetApiWorkflowsByIdTasksApiArg
      >({
        query: (queryArg) => ({ url: `/api/workflows/${queryArg.id}/tasks` }),
        providesTags: ['Workflows'],
      }),
      postApiWorkflowsByIdTasks: build.mutation<
        PostApiWorkflowsByIdTasksApiResponse,
        PostApiWorkflowsByIdTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks`,
          method: 'POST',
          body: queryArg.handlersWorkflowTaskCreateRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      getApiWorkflowsByIdTasksAndTaskNumber: build.query<
        GetApiWorkflowsByIdTasksAndTaskNumberApiResponse,
        GetApiWorkflowsByIdTasksAndTaskNumberApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}`,
        }),
        providesTags: ['Workflows'],
      }),
      deleteApiWorkflowsByIdTasksAndTaskNumber: build.mutation<
        DeleteApiWorkflowsByIdTasksAndTaskNumberApiResponse,
        DeleteApiWorkflowsByIdTasksAndTaskNumberApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Workflows'],
      }),
      patchApiWorkflowsByIdTasksAndTaskNumber: build.mutation<
        PatchApiWorkflowsByIdTasksAndTaskNumberApiResponse,
        PatchApiWorkflowsByIdTasksAndTaskNumberApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}`,
          method: 'PATCH',
          body: queryArg.handlersWorkflowTaskUpdateRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      getApiWorkflowsByIdTasksAndTaskNumberActions: build.query<
        GetApiWorkflowsByIdTasksAndTaskNumberActionsApiResponse,
        GetApiWorkflowsByIdTasksAndTaskNumberActionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/actions`,
        }),
        providesTags: ['Workflows'],
      }),
      postApiWorkflowsByIdTasksAndTaskNumberActions: build.mutation<
        PostApiWorkflowsByIdTasksAndTaskNumberActionsApiResponse,
        PostApiWorkflowsByIdTasksAndTaskNumberActionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/actions`,
          method: 'POST',
          body: queryArg.handlersWorkflowTaskActionCreateRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      getApiWorkflowsByIdTasksAndTaskNumberActionsActionNumber: build.query<
        GetApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiResponse,
        GetApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/actions/${queryArg.actionNumber}`,
        }),
        providesTags: ['Workflows'],
      }),
      deleteApiWorkflowsByIdTasksAndTaskNumberActionsActionNumber:
        build.mutation<
          DeleteApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiResponse,
          DeleteApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiArg
        >({
          query: (queryArg) => ({
            url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/actions/${queryArg.actionNumber}`,
            method: 'DELETE',
          }),
          invalidatesTags: ['Workflows'],
        }),
      patchApiWorkflowsByIdTasksAndTaskNumberActionsActionNumber:
        build.mutation<
          PatchApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiResponse,
          PatchApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiArg
        >({
          query: (queryArg) => ({
            url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/actions/${queryArg.actionNumber}`,
            method: 'PATCH',
            body: queryArg.handlersWorkflowTaskActionUpdateRequest,
          }),
          invalidatesTags: ['Workflows'],
        }),
      postApiWorkflowsByIdTasksAndTaskNumberApprove: build.mutation<
        PostApiWorkflowsByIdTasksAndTaskNumberApproveApiResponse,
        PostApiWorkflowsByIdTasksAndTaskNumberApproveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/approve`,
          method: 'POST',
          body: queryArg.handlersTaskApprovalRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      postApiWorkflowsByIdTasksAndTaskNumberAssign: build.mutation<
        PostApiWorkflowsByIdTasksAndTaskNumberAssignApiResponse,
        PostApiWorkflowsByIdTasksAndTaskNumberAssignApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/assign`,
          method: 'POST',
          body: queryArg.handlersTaskAssignmentRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      postApiWorkflowsByIdTasksAndTaskNumberReject: build.mutation<
        PostApiWorkflowsByIdTasksAndTaskNumberRejectApiResponse,
        PostApiWorkflowsByIdTasksAndTaskNumberRejectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/workflows/${queryArg.id}/tasks/${queryArg.taskNumber}/reject`,
          method: 'POST',
          body: queryArg.handlersTaskRejectionRequest,
        }),
        invalidatesTags: ['Workflows'],
      }),
      getDemoByFilename: build.query<
        GetDemoByFilenameApiResponse,
        GetDemoByFilenameApiArg
      >({
        query: (queryArg) => ({ url: `/demo/${queryArg.filename}` }),
        providesTags: ['Authentication'],
      }),
      getHealthz: build.query<GetHealthzApiResponse, GetHealthzApiArg>({
        query: () => ({ url: `/healthz` }),
        providesTags: ['Health'],
      }),
      getReady: build.query<GetReadyApiResponse, GetReadyApiArg>({
        query: () => ({ url: `/ready` }),
        providesTags: ['Health'],
      }),
      postSamlAcs: build.mutation<PostSamlAcsApiResponse, PostSamlAcsApiArg>({
        query: (queryArg) => ({
          url: `/saml/acs`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Authentication'],
      }),
      getSamlMetadata: build.query<
        GetSamlMetadataApiResponse,
        GetSamlMetadataApiArg
      >({
        query: () => ({ url: `/saml/metadata` }),
        providesTags: ['Authentication'],
      }),
      getSamlSlo: build.query<GetSamlSloApiResponse, GetSamlSloApiArg>({
        query: () => ({ url: `/saml/slo` }),
        providesTags: ['Authentication'],
      }),
      postSignatures: build.mutation<
        PostSignaturesApiResponse,
        PostSignaturesApiArg
      >({
        query: (queryArg) => ({
          url: `/signatures`,
          method: 'POST',
          body: queryArg.handlersSignatureCreateRequest,
        }),
        invalidatesTags: ['Signatures'],
      }),
      getSignaturesById: build.query<
        GetSignaturesByIdApiResponse,
        GetSignaturesByIdApiArg
      >({
        query: (queryArg) => ({ url: `/signatures/${queryArg.id}` }),
        providesTags: ['Signatures'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as athenaApi };
export type GetApiActionsApiResponse =
  /** status 200 OK */ HandlersActionCatalogResponse[];
export type GetApiActionsApiArg = {
  /** Include archived actions */
  includeArchived?: boolean;
};
export type PostApiActionsApiResponse =
  /** status 201 Created */ HandlersActionCatalogResponse;
export type PostApiActionsApiArg = {
  /** Action payload */
  handlersCreateActionRequest: HandlersCreateActionRequest;
};
export type GetApiActionsByActionNameApiResponse =
  /** status 200 OK */ HandlersActionCatalogResponse;
export type GetApiActionsByActionNameApiArg = {
  /** Action name */
  actionName: string;
};
export type DeleteApiActionsByActionNameApiResponse = unknown;
export type DeleteApiActionsByActionNameApiArg = {
  /** Action name */
  actionName: string;
};
export type PatchApiActionsByActionNameApiResponse =
  /** status 200 OK */ HandlersActionCatalogResponse;
export type PatchApiActionsByActionNameApiArg = {
  /** Action name */
  actionName: string;
  /** Patch payload */
  handlersUpdateActionRequest: HandlersUpdateActionRequest;
};
export type GetApiAdminRoleNotificationPreferencesApiResponse =
  /** status 200 OK */ HandlersRoleNotificationPreferencesResponse;
export type GetApiAdminRoleNotificationPreferencesApiArg = {
  /** Filter by program ID */
  programId?: string;
};
export type PutApiAdminRoleNotificationPreferencesApiResponse =
  /** status 200 OK */ {
    [key: string]: string;
  };
export type PutApiAdminRoleNotificationPreferencesApiArg = {
  /** Role notification preferences */
  handlersUpdateRoleNotificationPreferencesRequest: HandlersUpdateRoleNotificationPreferencesRequest;
};
export type GetApiApprovalQueueApiResponse =
  /** status 200 OK */ HandlersApprovalQueueResponse;
export type GetApiApprovalQueueApiArg = {
  /** Filter by approval state (all, pending, approved, rejected, in-recovery) */
  approvalState?: 'all' | 'pending' | 'approved' | 'rejected' | 'in-recovery';
  /** Page number (default: 1, minimum: 1) */
  page?: number;
  /** Items per page (default: 50, minimum: 1, maximum: 500) */
  limit?: number;
};
export type GetApiApprovalQueueStatsApiResponse =
  /** status 200 OK */ HandlersApprovalQueueStatsResponse;
export type GetApiApprovalQueueStatsApiArg = void;
export type GetApiAttachmentsApiResponse =
  /** status 200 Paginated list of attachments with metadata */ HandlersAttachmentListResponse;
export type GetApiAttachmentsApiArg = {
  /** Page number for pagination (default: 1, minimum: 1). Invalid values (≤0, non-numeric) return 400 error */
  page?: number;
  /** Number of attachments per page (default: 20, maximum: 100). Invalid values (≤0, >100, non-numeric) return 400 error */
  pageSize?: number;
  /** Filter by UUID of the entity this attachment belongs to (Project/Program/Deliverable/etc) */
  objectId?: string;
  /** Filter by entity type. Valid values: Project, Program, Deliverable, TDP, CTP, PNR, TAR, TR, RIP, Archive, CLIN, Comment, LCAT, ODC, ProjectCLIN. Invalid values return empty results */
  objectType?: string;
  /** Filter by UUID of the Contact who created the attachment */
  creatorContactId?: string;
  /** Filter by UUID of the FormSection used for categorization */
  formSectionId?: string;
};
export type PostApiAttachmentsApiResponse =
  /** status 201 Created attachment with version 1 */ HandlersAttachmentResponse;
export type PostApiAttachmentsApiArg = {
  body: {
    /** UUID of the entity this attachment belongs to (Project/Program/Deliverable/Directory/etc) */
    objectId: string;
    /** Type of entity referenced by objectId. Valid values: Project, Program, Deliverable, TDP, CTP, PNR, TAR, TR, RIP, Archive, CLIN, Comment, LCAT, ODC, ProjectCLIN, Directory, Itinerary */
    objectType: string;
    /** UUID of FormSection for categorization (optional, uses PROJECT TITLE section if not provided) */
    formSectionId?: string;
    /** Display name for the attachment file */
    name: string;
    /** Optional description of the attachment */
    description?: string;
    /** UUID of the Contact who is creating this attachment */
    creatorContactId: string;
    /** File to upload (any file type supported) */
    file: Blob;
  };
};
export type PostApiAttachmentsBatchApiResponse =
  /** status 201 Batch upload result with succeeded and failed files */ HandlersBatchUploadResponse;
export type PostApiAttachmentsBatchApiArg = {
  body: {
    /** UUID of the entity these attachments belong to (Project/Program/Deliverable/Directory/etc) */
    objectId: string;
    /** Type of entity referenced by objectId. Valid values: Project, Program, Deliverable, TDP, CTP, PNR, TAR, TR, RIP, Archive, CLIN, Comment, LCAT, ODC, ProjectCLIN, Directory, Itinerary */
    objectType: string;
    /** UUID of FormSection for categorization (optional, uses PROJECT TITLE section if not provided) */
    formSectionId?: string;
    /** UUID of the Contact who is creating these attachments */
    creatorContactId: string;
    /** Files to upload (multiple files allowed, any file type supported) */
    files: Blob;
  };
};
export type PostApiAttachmentsDownloadApiResponse = unknown;
export type PostApiAttachmentsDownloadApiArg = {
  /** Attachment IDs to download */
  handlersDownloadAttachmentsZipRequest: HandlersDownloadAttachmentsZipRequest;
};
export type GetApiAttachmentsByIdApiResponse =
  /** status 200 Attachment metadata including objectId (entity UUID), objectType (entity type), and file information */ HandlersAttachmentResponse;
export type GetApiAttachmentsByIdApiArg = {
  /** UUID of the attachment to retrieve */
  id: string;
};
export type DeleteApiAttachmentsByIdApiResponse = unknown;
export type DeleteApiAttachmentsByIdApiArg = {
  /** UUID of the attachment to delete (all versions will be archived) */
  id: string;
};
export type PatchApiAttachmentsByIdApiResponse =
  /** status 200 Updated attachment metadata */ HandlersAttachmentResponse;
export type PatchApiAttachmentsByIdApiArg = {
  /** UUID of the attachment to update */
  id: string;
  /** Updated attachment metadata (name and/or description only) */
  handlersUpdateAttachmentRequest: HandlersUpdateAttachmentRequest;
};
export type GetApiAttachmentsByIdDownloadApiResponse = unknown;
export type GetApiAttachmentsByIdDownloadApiArg = {
  /** UUID of the attachment to download */
  id: string;
};
export type GetApiAttachmentsByIdVAndVersionApiResponse =
  /** status 200 Attachment version metadata including isLatestVersion flag */ HandlersAttachmentResponse;
export type GetApiAttachmentsByIdVAndVersionApiArg = {
  /** UUID of the attachment */
  id: string;
  /** Version number to retrieve (must be >= 1) */
  version: number;
};
export type DeleteApiAttachmentsByIdVAndVersionApiResponse = unknown;
export type DeleteApiAttachmentsByIdVAndVersionApiArg = {
  /** UUID of the attachment */
  id: string;
  /** Version number to delete (must be >= 1) */
  version: number;
};
export type GetApiAttachmentsByIdVAndVersionDownloadApiResponse = unknown;
export type GetApiAttachmentsByIdVAndVersionDownloadApiArg = {
  /** UUID of the attachment to download */
  id: string;
  /** Version number to download (must be >= 1) */
  version: number;
};
export type GetApiAttachmentsByIdVersionsApiResponse =
  /** status 200 Array of all attachment versions with metadata (including archived versions). First item is latest non-archived version. Archived versions have 'archived: true'. */ HandlersAttachmentResponse[];
export type GetApiAttachmentsByIdVersionsApiArg = {
  /** UUID of the attachment whose versions to retrieve */
  id: string;
};
export type PostApiAttachmentsByIdVersionsApiResponse =
  /** status 201 Created new version with incremented version number */ HandlersAttachmentResponse;
export type PostApiAttachmentsByIdVersionsApiArg = {
  /** UUID of the existing attachment to create a new version for */
  id: string;
  body: {
    /** Display name for the new version */
    name: string;
    /** Optional description of the new version */
    description?: string;
    /** UUID of the Contact creating this new version */
    creatorContactId: string;
    /** New file content to upload (any file type supported) */
    file: Blob;
  };
};
export type GetApiAuditLogsApiResponse =
  /** status 200 OK */ HandlersAuditLogListResponse;
export type GetApiAuditLogsApiArg = {
  /** Filter by verbose level (1-6) */
  verboseLevel?: number;
  /** Filter by triggered user ID */
  userId?: string;
  /** Filter by object type */
  objectType?: string;
  /** Filter by object ID */
  objectId?: string;
  /** Filter logs after this time (RFC3339) */
  startTime?: string;
  /** Filter logs before this time (RFC3339) */
  endTime?: string;
  /** Page number */
  page?: number;
  /** Page size */
  pageSize?: number;
};
export type PostApiAuditLogsApiResponse =
  /** status 201 Created */ HandlersAuditLogResponse;
export type PostApiAuditLogsApiArg = {
  /** Audit log data */
  handlersCreateAuditLogRequest: HandlersCreateAuditLogRequest;
};
export type GetApiAuditLogsVerboseLevelsApiResponse =
  /** status 200 OK */ HandlersVerboseLevelResponse[];
export type GetApiAuditLogsVerboseLevelsApiArg = void;
export type GetApiAuditLogsByIdApiResponse =
  /** status 200 OK */ HandlersAuditLogResponse;
export type GetApiAuditLogsByIdApiArg = {
  /** Audit Log ID */
  id: string;
};
export type GetApiAuthCallbackApiResponse = unknown;
export type GetApiAuthCallbackApiArg = {
  /** Opaque CSRF state echoed by the authority */
  state: string;
  /** Authorization code */
  code: string;
};
export type GetApiAuthLoginApiResponse = unknown;
export type GetApiAuthLoginApiArg = void;
export type PostApiBillingImportsApiResponse = /** status 200 OK */ DbEtlRunLog;
export type PostApiBillingImportsApiArg = {
  body: {
    /** Deltek invoice CSV (UTF-8, 44 columns matching StagingDeltekBilling minus ProjRptId) */
    file: Blob;
  };
};
export type GetApiClinsApiResponse =
  /** status 200 OK */ HandlersClinListResponse;
export type GetApiClinsApiArg = {
  /** Filter by Program ID */
  programId?: string;
  /** Filter by Funding Source ID */
  fundingSourceId?: string;
  /** Page number */
  page?: number;
  /** Page size */
  pageSize?: number;
};
export type PostApiClinsApiResponse =
  /** status 201 Created */ HandlersClinResponse;
export type PostApiClinsApiArg = {
  /** CLIN data */
  handlersCreateClinRequest: HandlersCreateClinRequest;
};
export type GetApiClinsByIdApiResponse =
  /** status 200 OK */ HandlersClinResponse;
export type GetApiClinsByIdApiArg = {
  /** CLIN ID */
  id: string;
};
export type DeleteApiClinsByIdApiResponse = unknown;
export type DeleteApiClinsByIdApiArg = {
  /** CLIN ID */
  id: string;
};
export type PatchApiClinsByIdApiResponse =
  /** status 200 OK */ HandlersClinResponse;
export type PatchApiClinsByIdApiArg = {
  /** CLIN ID */
  id: string;
  /** CLIN update data */
  handlersUpdateClinRequest: HandlersUpdateClinRequest;
};
export type GetApiContactsApiResponse =
  /** status 200 OK */ HandlersContactListResponse;
export type GetApiContactsApiArg = {
  /** Filter by name (partial match, case-insensitive) */
  name?: string;
  /** Filter by organization ID */
  organizationId?: string;
  /** Page number */
  page?: number;
  /** Page size */
  pageSize?: number;
};
export type PostApiContactsApiResponse =
  /** status 201 Created */ HandlersContactResponse;
export type PostApiContactsApiArg = {
  /** Contact data */
  handlersCreateContactRequest: HandlersCreateContactRequest;
};
export type GetApiContactsByContactIdApiResponse =
  /** status 200 OK */ HandlersContactResponse;
export type GetApiContactsByContactIdApiArg = {
  /** Contact ID */
  contactId: string;
};
export type DeleteApiContactsByContactIdApiResponse = unknown;
export type DeleteApiContactsByContactIdApiArg = {
  /** Contact ID */
  contactId: string;
};
export type PatchApiContactsByContactIdApiResponse =
  /** status 200 OK */ HandlersContactResponse;
export type PatchApiContactsByContactIdApiArg = {
  /** Contact ID */
  contactId: string;
  /** Contact update data */
  handlersUpdateContactRequest: HandlersUpdateContactRequest;
};
export type GetApiCtpsApiResponse =
  /** status 200 Successfully retrieved CTPs */ HandlersCtpListResponse;
export type GetApiCtpsApiArg = {
  /** Filter by Project ID (UUID) */
  projectId?: string;
  /** Filter by To Contact ID (UUID) */
  toContactId?: string;
  /** Filter by From Contact ID (UUID) */
  fromContactId?: string;
  /** Filter by current version only (default: true) */
  currentVersion?: boolean;
  /** Search in CTP number, contractor name, client name, project name, subject, proposed subcontractor */
  search?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Page size (default: 50, max: 100) */
  pageSize?: number;
};
export type PostApiCtpsApiResponse =
  /** status 201 Successfully created CTP */ HandlersCtpResponse;
export type PostApiCtpsApiArg = {
  /** CTP creation data */
  handlersCreateCtpRequest: HandlersCreateCtpRequest;
};
export type GetApiCtpsByIdApiResponse =
  /** status 200 Successfully retrieved CTP */ HandlersCtpResponse;
export type GetApiCtpsByIdApiArg = {
  /** CTP ID */
  id: string;
};
export type DeleteApiCtpsByIdApiResponse = unknown;
export type DeleteApiCtpsByIdApiArg = {
  /** CTP ID */
  id: string;
};
export type PatchApiCtpsByIdApiResponse =
  /** status 200 Successfully updated CTP */ HandlersCtpResponse;
export type PatchApiCtpsByIdApiArg = {
  /** CTP ID */
  id: string;
  /** CTP update data */
  handlersUpdateCtpRequest: HandlersUpdateCtpRequest;
};
export type PostApiCtpsByIdEstimateApiResponse =
  /** status 201 Successfully created estimate */ HandlersEstimateResponse;
export type PostApiCtpsByIdEstimateApiArg = {
  /** CTP ID */
  id: string;
  /** Estimate details */
  handlersCreateEstimateRequest: HandlersCreateEstimateRequest;
};
export type DeleteApiCtpsByIdEstimateAndEstimateIdApiResponse = unknown;
export type DeleteApiCtpsByIdEstimateAndEstimateIdApiArg = {
  /** CTP ID */
  id: string;
  /** Estimate ID */
  estimateId: string;
};
export type PatchApiCtpsByIdEstimateAndEstimateIdApiResponse =
  /** status 200 Successfully updated estimate */ HandlersEstimateResponse;
export type PatchApiCtpsByIdEstimateAndEstimateIdApiArg = {
  /** CTP ID */
  id: string;
  /** Estimate ID */
  estimateId: string;
  /** Estimate update details */
  handlersUpdateEstimateRequest: HandlersUpdateEstimateRequest;
};
export type PostApiCtpsByIdSubmitApiResponse =
  /** status 200 OK */ HandlersFormWorkflowResponse;
export type PostApiCtpsByIdSubmitApiArg = {
  /** CTP ID */
  id: string;
};
export type GetApiCtpsByIdVAndVersionApiResponse =
  /** status 200 Successfully retrieved CTP version */ HandlersCtpResponse;
export type GetApiCtpsByIdVAndVersionApiArg = {
  /** CTP ID */
  id: string;
  /** Version number */
  version: number;
};
export type GetApiCtpsByIdVersionsApiResponse =
  /** status 200 Successfully retrieved CTP versions */ HandlersCtpVersionResponse[];
export type GetApiCtpsByIdVersionsApiArg = {
  /** CTP ID */
  id: string;
};
export type GetApiDeliverablesApiResponse =
  /** status 200 OK */ HandlersDeliverableListResponse;
export type GetApiDeliverablesApiArg = {
  /** Page number for pagination (default 1) */
  page?: number;
  /** Number of deliverables per page (default 50) */
  limit?: number;
  /** Filter by deliverable type ID */
  deliverableTypeId?: string;
  /** Filter by location ID */
  locationId?: string;
  /** Filter by author Contact ID */
  authorId?: string;
  /** Filter by program ID */
  programId?: string;
  /** Filter by project ID */
  projectId?: string;
  /** Filter by workflow state(s) - supports multiple values via repeated parameters (?workflowState=Pending&workflowState=Internally Approved). Returns deliverables matching ANY of the supplied states. Note: comma-separated values are NOT supported since some workflow states contain commas. */
  workflowState?: string[];
  /** Filter by last modified on or after (YYYY-MM-DD or RFC3339) */
  lastModifiedAfter?: string;
  /** Filter by last modified before (YYYY-MM-DD or RFC3339) */
  lastModifiedBefore?: string;
  /** Filter by due date on or after (YYYY-MM-DD or RFC3339) */
  dueDateAfter?: string;
  /** Filter by due date before (YYYY-MM-DD or RFC3339) */
  dueDateBefore?: string;
};
export type PostApiDeliverablesApiResponse =
  /** status 201 Created */ HandlersDeliverableResponse;
export type PostApiDeliverablesApiArg = {
  /** Deliverable data */
  handlersDeliverableCreateRequest: HandlersDeliverableCreateRequest;
};
export type GetApiDeliverablesTypesApiResponse =
  /** status 200 OK */ HandlersDeliverableTypeWithProjectsResponse[];
export type GetApiDeliverablesTypesApiArg = void;
export type GetApiDeliverablesByIdApiResponse =
  /** status 200 OK */ HandlersDeliverableResponse;
export type GetApiDeliverablesByIdApiArg = {
  /** Deliverable ID */
  id: string;
};
export type DeleteApiDeliverablesByIdApiResponse = unknown;
export type DeleteApiDeliverablesByIdApiArg = {
  /** Deliverable ID */
  id: string;
};
export type PatchApiDeliverablesByIdApiResponse =
  /** status 200 OK */ HandlersDeliverableResponse;
export type PatchApiDeliverablesByIdApiArg = {
  /** Deliverable ID */
  id: string;
  /** Deliverable updates */
  handlersDeliverableUpdateRequest: HandlersDeliverableUpdateRequest;
};
export type GetApiDeliverablesByIdApprovalStatusApiResponse =
  /** status 200 OK */ HandlersDeliverableApprovalStatus;
export type GetApiDeliverablesByIdApprovalStatusApiArg = {
  /** Deliverable ID */
  id: string;
};
export type PostApiDeliverablesByIdCancelApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type PostApiDeliverablesByIdCancelApiArg = {
  /** Deliverable ID */
  id: string;
};
export type PostApiDeliverablesByIdSubmitApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type PostApiDeliverablesByIdSubmitApiArg = {
  /** Deliverable ID */
  id: string;
};
export type GetApiDeliverablesByIdWorkflowApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type GetApiDeliverablesByIdWorkflowApiArg = {
  /** Deliverable ID */
  id: string;
};
export type PostApiDevSendTestEmailApiResponse =
  /** status 200 Email sent successfully */ HandlersSendTestEmailResponse;
export type PostApiDevSendTestEmailApiArg = {
  /** Email details */
  handlersSendTestEmailRequest: HandlersSendTestEmailRequest;
};
export type PostApiDevSignCmsApiResponse =
  /** status 200 OK */ HandlersDevSignCmsResponse;
export type PostApiDevSignCmsApiArg = {
  /** Decision tuple to sign */
  handlersDevSignCmsRequest: HandlersDevSignCmsRequest;
};
export type PostApiDevTokenApiResponse =
  /** status 200 Returns both aclToken (for Swagger) and loginToken (for frontend cookie) */ HandlersCreateDevTokenResponse;
export type PostApiDevTokenApiArg = {
  /** Token configuration with ACL permissions */
  handlersCreateDevTokenRequest: HandlersCreateDevTokenRequest;
};
export type GetApiDevTokenAdminApiResponse =
  /** status 200 Returns both aclToken (for Swagger) and loginToken (for frontend cookie) - both work for sitewide admin */ HandlersCreateDevTokenResponse;
export type GetApiDevTokenAdminApiArg = void;
export type GetApiDevTokenExamplesApiResponse =
  /** status 200 OK */ HandlersDevTokenExamplesResponse;
export type GetApiDevTokenExamplesApiArg = void;
export type GetApiFinancialsDashboardApiResponse =
  /** status 200 OK */ HandlersFinancialDashboardResponse;
export type GetApiFinancialsDashboardApiArg = {
  /** Filter by Program ID */
  programId?: string;
  /** Filter by Project ID */
  projectId?: string;
  /** Filter by CLIN ID */
  clinId?: string;
  /** Start date in YYYY-MM-DD format. Invalid format returns 400 error */
  startDate?: string;
  /** End date in YYYY-MM-DD format. Must be >= startDate. Invalid format/range returns 400 error */
  endDate?: string;
};
export type GetApiFinancialsFidelityTypesApiResponse =
  /** status 200 OK */ HandlersFinancialFidelityTypeResponse[];
export type GetApiFinancialsFidelityTypesApiArg = void;
export type GetApiFinancialsGfebsReportsApiResponse =
  /** status 200 OK */ HandlersGfebsReportListResponse;
export type GetApiFinancialsGfebsReportsApiArg = {
  /** Filter by program ID */
  programId?: string;
  /** Page number (default 1) */
  page?: number;
  /** Page size (default 20, max 100) */
  pageSize?: number;
};
export type GetApiFinancialsGfebsReportsByReportIdLineItemsApiResponse =
  /** status 200 OK */ HandlersGfebsLineItemResponse[];
export type GetApiFinancialsGfebsReportsByReportIdLineItemsApiArg = {
  /** GFEBS report ID */
  reportId: string;
};
export type GetApiFinancialsInvoicesApiResponse =
  /** status 200 OK */ HandlersInvoiceListResponse;
export type GetApiFinancialsInvoicesApiArg = {
  /** Filter by Program ID */
  programId?: string;
  /** Filter by Project ID */
  projectId?: string;
  /** Filter by CLIN ID */
  clinId?: string;
  /** Start date in YYYY-MM-DD format. Invalid format returns 400 error */
  startDate?: string;
  /** End date in YYYY-MM-DD format. Must be >= startDate. Invalid format/range returns 400 error */
  endDate?: string;
  /** Page number */
  page?: number;
  /** Page size */
  pageSize?: number;
};
export type GetApiFinancialsInvoicesByCodeApiResponse =
  /** status 200 OK */ HandlersInvoiceResponse;
export type GetApiFinancialsInvoicesByCodeApiArg = {
  /** Invoice Code */
  code: string;
};
export type GetApiFinancialsInvoicesByInvoiceCodeCostsByClinApiResponse =
  /** status 200 OK */ HandlersInvoiceCostsByClinResponse;
export type GetApiFinancialsInvoicesByInvoiceCodeCostsByClinApiArg = {
  /** Invoice Code */
  invoiceCode: string;
};
export type GetApiFinancialsInvoicesByInvoiceCodeCostsByProjectApiResponse =
  /** status 200 OK */ HandlersInvoiceCostsByProjectResponse;
export type GetApiFinancialsInvoicesByInvoiceCodeCostsByProjectApiArg = {
  /** Invoice Code */
  invoiceCode: string;
};
export type GetApiFinancialsItdReportsApiResponse =
  /** status 200 OK */ HandlersItdReportListResponse;
export type GetApiFinancialsItdReportsApiArg = {
  /** Filter by program ID */
  programId?: string;
  /** Page number (default 1) */
  page?: number;
  /** Page size (default 20, max 100) */
  pageSize?: number;
};
export type GetApiFinancialsItdReportsBySummaryIdDetailsApiResponse =
  /** status 200 OK */ HandlersItdReportDetailResponse[];
export type GetApiFinancialsItdReportsBySummaryIdDetailsApiArg = {
  /** Summary ID */
  summaryId: string;
};
export type GetApiFinancialsMetricsBurnRateApiResponse =
  /** status 200 OK */ HandlersBurnRateData;
export type GetApiFinancialsMetricsBurnRateApiArg = {
  /** Filter by Program ID */
  programId?: string;
  /** Filter by Project ID */
  projectId?: string;
  /** Filter by CLIN ID */
  clinId?: string;
  /** Period for calculation */
  period: 'monthly' | 'quarterly' | 'yearly';
};
export type GetApiFinancialsTabularDataApiResponse =
  /** status 200 OK */ HandlersTabularPlanListResponse;
export type GetApiFinancialsTabularDataApiArg = {
  /** Program ID (UUID) */
  programId: string;
  /** View type */
  view:
    | 'allocations'
    | 'funding'
    | 'budgets'
    | 'forecasts'
    | 'actual-costs'
    | 'costs-by-clin';
  /** Page number */
  page?: number;
  /** Page size */
  pageSize?: number;
};
export type GetApiFormsApiResponse = /** status 200 OK */ DbForm[];
export type GetApiFormsApiArg = void;
export type GetApiFormsTypesApiResponse = /** status 200 OK */ DbFormType[];
export type GetApiFormsTypesApiArg = void;
export type GetApiFormsByIdApiResponse = /** status 200 OK */ DbForm;
export type GetApiFormsByIdApiArg = {
  /** Form ID */
  id: string;
  /** Form Version Number */
  version: number;
};
export type GetApiHealthApiResponse =
  /** status 200 OK */ HandlersHealthResponse;
export type GetApiHealthApiArg = void;
export type GetApiLibraryApiResponse =
  /** status 200 OK */ HandlersLibraryResponse;
export type GetApiLibraryApiArg = {
  /** When true, includes all deliverables including those with incomplete/in-progress workflows. When false (default), only shows deliverables where all workflows are complete. */
  includeAll?: boolean;
};
export type GetApiLibraryDirectoriesApiResponse =
  /** status 200 OK */ HandlersLibraryDirectoryResponse[];
export type GetApiLibraryDirectoriesApiArg = {
  /** Program ID to filter by */
  programId?: string;
  /** Project ID to filter by */
  projectId?: string;
  /** Parent Directory ID to filter by (for children of a directory) */
  parentDirectoryId?: number;
};
export type PostApiLibraryDirectoriesApiResponse =
  /** status 201 Created */ HandlersLibraryDirectoryResponse;
export type PostApiLibraryDirectoriesApiArg = {
  /** Library directory data (programId, directoryName, and directoryUrl are required) */
  handlersCreateLibraryDirectoryRequest: HandlersCreateLibraryDirectoryRequest;
};
export type GetApiLibraryDirectoriesByDirectoryIdApiResponse =
  /** status 200 OK */ HandlersLibraryDirectoryResponse;
export type GetApiLibraryDirectoriesByDirectoryIdApiArg = {
  /** Directory ID (UUID) */
  directoryId: string;
};
export type DeleteApiLibraryDirectoriesByDirectoryIdApiResponse = unknown;
export type DeleteApiLibraryDirectoriesByDirectoryIdApiArg = {
  /** Directory ID (UUID) */
  directoryId: string;
};
export type PatchApiLibraryDirectoriesByDirectoryIdApiResponse =
  /** status 200 OK */ HandlersLibraryDirectoryResponse;
export type PatchApiLibraryDirectoriesByDirectoryIdApiArg = {
  /** Directory ID (UUID) */
  directoryId: string;
  /** Updated library directory data (all fields optional) */
  handlersUpdateLibraryDirectoryRequest: HandlersUpdateLibraryDirectoryRequest;
};
export type PostApiLibraryLabelByIdAndNewLabelApiResponse =
  /** status 200 Updated library item */ HandlersFileDeliverable;
export type PostApiLibraryLabelByIdAndNewLabelApiArg = {
  /** Library item ID to update */
  id: string;
  /** New label value (URL encoded) */
  newLabel: string;
};
export type PostApiLibraryMoveByIdAndNewParentIdApiResponse =
  /** status 200 Moved library item */ HandlersFileDeliverable;
export type PostApiLibraryMoveByIdAndNewParentIdApiArg = {
  /** Library item ID to move */
  id: string;
  /** New parent ID (or 'root' for program level) */
  newParentId: string;
};
export type GetApiLibraryTreeApiResponse =
  /** status 200 OK */ HandlersLibraryTreeNode[];
export type GetApiLibraryTreeApiArg = {
  /** When true, includes all deliverables including those with incomplete/in-progress workflows. When false (default), only shows deliverables where all workflows are complete. */
  includeAll?: boolean;
};
export type GetApiLibraryByIdApiResponse =
  /** status 200 OK */ HandlersLibraryTreeNode;
export type GetApiLibraryByIdApiArg = {
  /** Library item ID (UUID) */
  id: string;
};
export type GetApiLibraryByIdDownloadApiResponse = unknown;
export type GetApiLibraryByIdDownloadApiArg = {
  /** Library item ID */
  id: string;
};
export type GetApiLocationTypesApiResponse =
  /** status 200 OK */ HandlersLocationTypeResponse[];
export type GetApiLocationTypesApiArg = {
  /** Include archived types */
  includeArchived?: boolean;
};
export type PostApiLocationTypesApiResponse =
  /** status 201 Created */ HandlersLocationTypeResponse;
export type PostApiLocationTypesApiArg = {
  /** Payload */
  handlersLocationTypeRequest: HandlersLocationTypeRequest;
};
export type GetApiLocationTypesByLocationTypeApiResponse =
  /** status 200 OK */ HandlersLocationTypeResponse;
export type GetApiLocationTypesByLocationTypeApiArg = {
  /** Location type */
  locationType: string;
};
export type DeleteApiLocationTypesByLocationTypeApiResponse = unknown;
export type DeleteApiLocationTypesByLocationTypeApiArg = {
  /** Location type */
  locationType: string;
};
export type PatchApiLocationTypesByLocationTypeApiResponse =
  /** status 200 OK */ HandlersLocationTypeResponse;
export type PatchApiLocationTypesByLocationTypeApiArg = {
  /** Location type */
  locationType: string;
  /** Payload */
  handlersLocationTypeUpdateRequest: HandlersLocationTypeUpdateRequest;
};
export type GetApiLocationsApiResponse = /** status 200 OK */ DbLocation[];
export type GetApiLocationsApiArg = void;
export type PostApiLocationsApiResponse =
  /** status 201 Created */ HandlersLocationCatalogResponse;
export type PostApiLocationsApiArg = {
  /** Payload */
  handlersCreateLocationRequest: HandlersCreateLocationRequest;
};
export type GetApiLocationsByIdApiResponse = /** status 200 OK */ DbLocation;
export type GetApiLocationsByIdApiArg = {
  /** Location ID */
  id: string;
};
export type DeleteApiLocationsByIdApiResponse = unknown;
export type DeleteApiLocationsByIdApiArg = {
  /** Location ID */
  id: string;
};
export type PatchApiLocationsByIdApiResponse =
  /** status 200 OK */ HandlersLocationCatalogResponse;
export type PatchApiLocationsByIdApiArg = {
  /** Location ID */
  id: string;
  /** Payload */
  handlersUpdateLocationRequest: HandlersUpdateLocationRequest;
};
export type GetApiLogoutApiResponse = unknown;
export type GetApiLogoutApiArg = void;
export type GetApiMasterScheduleApiResponse =
  /** status 200 OK */ ServicesMasterSchedule[];
export type GetApiMasterScheduleApiArg = void;
export type PostApiMasterScheduleUploadApiResponse =
  /** status 200 OK */ ServicesMasterSchedule;
export type PostApiMasterScheduleUploadApiArg = {
  body: {
    /** MPP file to upload */
    file: Blob;
    /** Project ID (UUID). Optional - admin access required when omitted */
    project_id?: string;
    /** Project name override */
    project_name?: string;
  };
};
export type GetApiMasterScheduleByScheduleIdApiResponse =
  /** status 200 OK */ ServicesMasterSchedule;
export type GetApiMasterScheduleByScheduleIdApiArg = {
  /** Schedule ID */
  scheduleId: string;
};
export type DeleteApiMasterScheduleByScheduleIdApiResponse = unknown;
export type DeleteApiMasterScheduleByScheduleIdApiArg = {
  /** Schedule ID */
  scheduleId: string;
};
export type PatchApiMasterScheduleByScheduleIdApiResponse =
  /** status 200 OK */ ServicesMasterSchedule;
export type PatchApiMasterScheduleByScheduleIdApiArg = {
  /** Schedule ID */
  scheduleId: string;
  /** Update fields */
  handlersUpdateMasterScheduleRequest: HandlersUpdateMasterScheduleRequest;
};
export type GetApiMasterScheduleByScheduleIdMilestonesApiResponse =
  /** status 200 OK */ MpxjTaskData[];
export type GetApiMasterScheduleByScheduleIdMilestonesApiArg = {
  /** Schedule ID */
  scheduleId: string;
};
export type GetApiMasterScheduleByScheduleIdStatusApiResponse =
  /** status 200 OK */ ServicesMasterScheduleStatusResponse;
export type GetApiMasterScheduleByScheduleIdStatusApiArg = {
  /** Schedule ID */
  scheduleId: string;
};
export type GetApiMasterScheduleByScheduleIdTasksApiResponse =
  /** status 200 OK */ MpxjTaskData[];
export type GetApiMasterScheduleByScheduleIdTasksApiArg = {
  /** Schedule ID */
  scheduleId: string;
};
export type PostApiObjectTypesApiResponse =
  /** status 201 Created */ HandlersObjectTypeResponse;
export type PostApiObjectTypesApiArg = {
  /** Payload */
  handlersObjectTypeRequest: HandlersObjectTypeRequest;
};
export type GetApiObjectTypesByObjectTypeApiResponse =
  /** status 200 OK */ HandlersObjectTypeResponse;
export type GetApiObjectTypesByObjectTypeApiArg = {
  /** Object type */
  objectType: string;
};
export type DeleteApiObjectTypesByObjectTypeApiResponse = unknown;
export type DeleteApiObjectTypesByObjectTypeApiArg = {
  /** Object type */
  objectType: string;
};
export type PatchApiObjectTypesByObjectTypeApiResponse =
  /** status 200 OK */ HandlersObjectTypeResponse;
export type PatchApiObjectTypesByObjectTypeApiArg = {
  /** Object type */
  objectType: string;
  /** Payload */
  handlersObjectTypeUpdateRequest: HandlersObjectTypeUpdateRequest;
};
export type GetApiOrganizationsApiResponse =
  /** status 200 OK */ HandlersOrganizationListResponse;
export type GetApiOrganizationsApiArg = {
  /** Filter by organization type */
  organizationType?: string;
  /** Page number */
  page?: number;
  /** Page size */
  pageSize?: number;
};
export type PostApiOrganizationsApiResponse =
  /** status 201 Created */ HandlersOrganizationResponse;
export type PostApiOrganizationsApiArg = {
  /** Organization data */
  handlersCreateOrganizationRequest: HandlersCreateOrganizationRequest;
};
export type GetApiOrganizationsTypesApiResponse =
  /** status 200 OK */ HandlersOrganizationTypeResponse[];
export type GetApiOrganizationsTypesApiArg = void;
export type PostApiOrganizationsTypesApiResponse =
  /** status 201 Created */ HandlersSimpleCatalogResponse;
export type PostApiOrganizationsTypesApiArg = {
  /** Payload */
  handlersSimpleCatalogRequest: HandlersSimpleCatalogRequest;
};
export type GetApiOrganizationsTypesByOrganizationTypeApiResponse =
  /** status 200 OK */ HandlersSimpleCatalogResponse;
export type GetApiOrganizationsTypesByOrganizationTypeApiArg = {
  /** Organization type */
  organizationType: string;
};
export type DeleteApiOrganizationsTypesByOrganizationTypeApiResponse = unknown;
export type DeleteApiOrganizationsTypesByOrganizationTypeApiArg = {
  /** Organization type */
  organizationType: string;
};
export type PatchApiOrganizationsTypesByOrganizationTypeApiResponse =
  /** status 200 OK */ HandlersSimpleCatalogResponse;
export type PatchApiOrganizationsTypesByOrganizationTypeApiArg = {
  /** Organization type */
  organizationType: string;
  /** Payload */
  handlersSimpleCatalogUpdateRequest: HandlersSimpleCatalogUpdateRequest;
};
export type GetApiOrganizationsByIdApiResponse =
  /** status 200 OK */ HandlersOrganizationResponse;
export type GetApiOrganizationsByIdApiArg = {
  /** Organization ID */
  id: string;
};
export type DeleteApiOrganizationsByIdApiResponse = unknown;
export type DeleteApiOrganizationsByIdApiArg = {
  /** Organization ID */
  id: string;
};
export type PatchApiOrganizationsByIdApiResponse =
  /** status 200 OK */ HandlersOrganizationResponse;
export type PatchApiOrganizationsByIdApiArg = {
  /** Organization ID */
  id: string;
  /** Organization update data */
  handlersUpdateOrganizationRequest: HandlersUpdateOrganizationRequest;
};
export type GetApiPlatformConfigFormsApiResponse =
  /** status 200 OK */ HandlersFormVisibilityResponse;
export type GetApiPlatformConfigFormsApiArg = void;
export type PutApiPlatformConfigFormsApiResponse =
  /** status 200 OK */ HandlersFormVisibilityResponse;
export type PutApiPlatformConfigFormsApiArg = {
  /** Form visibility configuration */
  handlersUpdateFormVisibilityRequest: HandlersUpdateFormVisibilityRequest;
};
export type GetApiPnrsApiResponse =
  /** status 200 Successfully retrieved PNRs */ HandlersPnrListResponse;
export type GetApiPnrsApiArg = {
  /** Filter by project ID */
  projectId?: string;
  /** Filter by current version (true/false) */
  currentVersion?: boolean;
  /** Search in PNRNumber and NatureSourceProblemDesc */
  search?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Page size (default: 50, max: 100) */
  pageSize?: number;
};
export type PostApiPnrsApiResponse =
  /** status 201 Successfully created PNR */ HandlersPnrResponse;
export type PostApiPnrsApiArg = {
  /** PNR creation request */
  handlersCreatePnrRequest: HandlersCreatePnrRequest;
};
export type GetApiPnrsByIdApiResponse =
  /** status 200 Successfully retrieved PNR */ HandlersPnrResponse;
export type GetApiPnrsByIdApiArg = {
  /** PNR ID */
  id: string;
};
export type DeleteApiPnrsByIdApiResponse = unknown;
export type DeleteApiPnrsByIdApiArg = {
  /** PNR ID (UUID format) */
  id: string;
};
export type PatchApiPnrsByIdApiResponse =
  /** status 200 Successfully updated PNR */ HandlersPnrResponse;
export type PatchApiPnrsByIdApiArg = {
  /** PNR ID */
  id: string;
  /** PNR update request */
  handlersUpdatePnrRequest: HandlersUpdatePnrRequest;
};
export type PostApiPnrsByIdSubmitApiResponse =
  /** status 200 OK */ HandlersFormWorkflowResponse;
export type PostApiPnrsByIdSubmitApiArg = {
  /** PNR ID */
  id: string;
};
export type GetApiPnrsByIdVAndVersionApiResponse =
  /** status 200 Successfully retrieved PNR version */ HandlersPnrResponse;
export type GetApiPnrsByIdVAndVersionApiArg = {
  /** PNR ID */
  id: string;
  /** Version number */
  version: number;
};
export type GetApiPnrsByIdVersionsApiResponse =
  /** status 200 Successfully retrieved PNR versions */ HandlersPnrResponse[];
export type GetApiPnrsByIdVersionsApiArg = {
  /** PNR ID */
  id: string;
};
export type PostApiPowerbiEmbedTokenApiResponse =
  /** status 200 OK */ HandlersGetEmbedTokenResponse;
export type PostApiPowerbiEmbedTokenApiArg = {
  /** Report and Dataset IDs */
  handlersGetEmbedTokenRequest: HandlersGetEmbedTokenRequest;
};
export type GetApiProgramsApiResponse =
  /** status 200 OK */ HandlersProgramResponse[];
export type GetApiProgramsApiArg = void;
export type PostApiProgramsApiResponse =
  /** status 201 Created */ HandlersProgramResponse;
export type PostApiProgramsApiArg = {
  /** Program creation request */
  handlersCreateProgramRequest: HandlersCreateProgramRequest;
};
export type GetApiProgramsAvailableDeliverableTypesApiResponse =
  /** status 200 OK */ DbDeliverableType[];
export type GetApiProgramsAvailableDeliverableTypesApiArg = void;
export type GetApiProgramsDeliverableTypesApiResponse =
  /** status 200 OK */ HandlersProgramDeliverableTypeResponse[];
export type GetApiProgramsDeliverableTypesApiArg = {
  /** Filter by Program ID (UUID format) */
  programId?: string;
};
export type GetApiProgramsDeltekContractsApiResponse =
  /** status 200 OK */ HandlersDeltekContractResponse[];
export type GetApiProgramsDeltekContractsApiArg = void;
export type GetApiProgramsByIdApiResponse =
  /** status 200 OK */ HandlersProgramResponse;
export type GetApiProgramsByIdApiArg = {
  /** Program ID */
  id: string;
};
export type DeleteApiProgramsByIdApiResponse = unknown;
export type DeleteApiProgramsByIdApiArg = {
  /** Program ID */
  id: string;
};
export type PatchApiProgramsByIdApiResponse =
  /** status 200 OK */ HandlersProgramResponse;
export type PatchApiProgramsByIdApiArg = {
  /** Program ID */
  id: string;
  /** Program update request */
  handlersUpdateProgramRequest: HandlersUpdateProgramRequest;
};
export type GetApiProgramsByIdDeliverableTypesApiResponse =
  /** status 200 OK */ HandlersProgramDeliverableTypeResponse[];
export type GetApiProgramsByIdDeliverableTypesApiArg = {
  /** Program ID */
  id: string;
};
export type PostApiProgramsByIdDeliverableTypesApiResponse =
  /** status 201 Created */ HandlersProgramDeliverableTypeResponse[];
export type PostApiProgramsByIdDeliverableTypesApiArg = {
  /** Program ID */
  id: string;
  /** Deliverable types to associate */
  handlersProgramDeliverableTypesRequest: HandlersProgramDeliverableTypesRequest;
};
export type DeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiResponse =
  unknown;
export type DeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiArg = {
  /** Program ID */
  id: string;
  /** Deliverable type ID */
  deliverableTypeId: string;
  /** Deliverable number (composite-PK row locator) */
  deliverableNumber: number;
};
export type PatchApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiResponse =
  /** status 200 OK */ HandlersProgramDeliverableTypeResponse;
export type PatchApiProgramsByIdDeliverableTypesAndDeliverableTypeIdApiArg = {
  /** Program ID */
  id: string;
  /** Current deliverable type ID (locator) */
  deliverableTypeId: string;
  /** Partial update; deliverableNumber required (row locator) */
  handlersUpdateProgramDeliverableTypeRequest: HandlersUpdateProgramDeliverableTypeRequest;
};
export type GetApiProgramsByIdLocationsApiResponse =
  /** status 200 OK */ HandlersProgramLocationResponse[];
export type GetApiProgramsByIdLocationsApiArg = {
  /** Program ID */
  id: string;
};
export type PostApiProgramsByIdLocationsApiResponse = unknown;
export type PostApiProgramsByIdLocationsApiArg = {
  /** Program ID */
  id: string;
  /** Payload */
  handlersAddProgramLocationRequest: HandlersAddProgramLocationRequest;
};
export type DeleteApiProgramsByIdLocationsAndLocationIdApiResponse = unknown;
export type DeleteApiProgramsByIdLocationsAndLocationIdApiArg = {
  /** Program ID */
  id: string;
  /** Location ID */
  locationId: string;
};
export type GetApiProgramsByIdObjectTypesApiResponse =
  /** status 200 OK */ HandlersProgramObjectTypeResponse[];
export type GetApiProgramsByIdObjectTypesApiArg = {
  /** Program ID */
  id: string;
};
export type PostApiProgramsByIdObjectTypesApiResponse = unknown;
export type PostApiProgramsByIdObjectTypesApiArg = {
  /** Program ID */
  id: string;
  /** Payload */
  handlersAddProgramObjectTypeRequest: HandlersAddProgramObjectTypeRequest;
};
export type DeleteApiProgramsByIdObjectTypesAndObjectTypeApiResponse = unknown;
export type DeleteApiProgramsByIdObjectTypesAndObjectTypeApiArg = {
  /** Program ID */
  id: string;
  /** Object type */
  objectType: string;
};
export type GetApiProgramsByIdRoleAssignmentsApiResponse =
  /** status 200 OK */ HandlersProgramRoleAssignmentResponse[];
export type GetApiProgramsByIdRoleAssignmentsApiArg = {
  /** Program ID */
  id: string;
};
export type GetApiProgramsByIdRolesApiResponse =
  /** status 200 OK */ HandlersProgramRoleResponse[];
export type GetApiProgramsByIdRolesApiArg = {
  /** Program ID */
  id: string;
  /** Include archived roles */
  includeArchived?: boolean;
};
export type PostApiProgramsByIdRolesApiResponse =
  /** status 201 Created */ HandlersProgramRoleResponse;
export type PostApiProgramsByIdRolesApiArg = {
  /** Program ID */
  id: string;
  /** Role payload */
  handlersCreateRoleRequest: HandlersCreateRoleRequest;
};
export type GetApiProgramsByIdRolesAndRoleNameApiResponse =
  /** status 200 OK */ HandlersProgramRoleResponse;
export type GetApiProgramsByIdRolesAndRoleNameApiArg = {
  /** Program ID */
  id: string;
  /** Role name */
  roleName: string;
};
export type DeleteApiProgramsByIdRolesAndRoleNameApiResponse = unknown;
export type DeleteApiProgramsByIdRolesAndRoleNameApiArg = {
  /** Program ID */
  id: string;
  /** Role name */
  roleName: string;
};
export type PatchApiProgramsByIdRolesAndRoleNameApiResponse =
  /** status 200 OK */ HandlersProgramRoleResponse;
export type PatchApiProgramsByIdRolesAndRoleNameApiArg = {
  /** Program ID */
  id: string;
  /** Role name */
  roleName: string;
  /** Patch payload */
  handlersUpdateRoleRequest: HandlersUpdateRoleRequest;
};
export type GetApiProgramsByIdRolesAndRoleNameActionsApiResponse =
  /** status 200 OK */ HandlersRoleActionResponse[];
export type GetApiProgramsByIdRolesAndRoleNameActionsApiArg = {
  /** Program ID */
  id: string;
  /** Role name */
  roleName: string;
};
export type PostApiProgramsByIdRolesAndRoleNameActionsApiResponse =
  /** status 201 Created */ HandlersRoleActionResponse;
export type PostApiProgramsByIdRolesAndRoleNameActionsApiArg = {
  /** Program ID */
  id: string;
  /** Role name */
  roleName: string;
  /** Payload */
  handlersAddRoleActionRequest: HandlersAddRoleActionRequest;
};
export type DeleteApiProgramsByIdRolesAndRoleNameActionsActionNameApiResponse =
  unknown;
export type DeleteApiProgramsByIdRolesAndRoleNameActionsActionNameApiArg = {
  /** Program ID */
  id: string;
  /** Role name */
  roleName: string;
  /** Action name */
  actionName: string;
};
export type GetApiProgramsByIdSiteSettingsApiResponse =
  /** status 200 OK */ HandlersSiteSettingsResponse;
export type GetApiProgramsByIdSiteSettingsApiArg = {
  /** Program ID */
  id: string;
};
export type PutApiProgramsByIdSiteSettingsAdminEmailApiResponse =
  /** status 200 OK */ HandlersSiteSettingsResponse;
export type PutApiProgramsByIdSiteSettingsAdminEmailApiArg = {
  /** Program ID */
  id: string;
  /** Admin email */
  handlersUpdateAdminEmailRequest: HandlersUpdateAdminEmailRequest;
};
export type GetApiProgramsByIdSiteSettingsDomainsApiResponse =
  /** status 200 OK */ string[];
export type GetApiProgramsByIdSiteSettingsDomainsApiArg = {
  /** Program ID */
  id: string;
};
export type PostApiProgramsByIdSiteSettingsDomainsApiResponse =
  /** status 201 Created */ HandlersAddDomainRequest;
export type PostApiProgramsByIdSiteSettingsDomainsApiArg = {
  /** Program ID */
  id: string;
  /** Domain to add */
  handlersAddDomainRequest: HandlersAddDomainRequest;
};
export type DeleteApiProgramsByIdSiteSettingsDomainsAndDomainApiResponse =
  unknown;
export type DeleteApiProgramsByIdSiteSettingsDomainsAndDomainApiArg = {
  /** Program ID */
  id: string;
  /** Domain to delete */
  domain: string;
};
export type GetApiProgramsByIdUserRolesApiResponse =
  /** status 200 OK */ HandlersProgramUserRoleResponse[];
export type GetApiProgramsByIdUserRolesApiArg = {
  /** Program ID */
  id: string;
};
export type GetApiProgramsByIdWorkflowLabelsApiResponse =
  /** status 200 OK */ HandlersWorkflowLabelsResponse;
export type GetApiProgramsByIdWorkflowLabelsApiArg = {
  /** Program ID */
  id: string;
};
export type PatchApiProgramsByIdWorkflowLabelsApiResponse =
  /** status 200 OK */ HandlersWorkflowLabelsResponse;
export type PatchApiProgramsByIdWorkflowLabelsApiArg = {
  /** Program ID */
  id: string;
  /** Workflow labels update request */
  handlersUpdateWorkflowLabelsRequest: HandlersUpdateWorkflowLabelsRequest;
};
export type DeleteApiProgramsByIdWorkflowLabelsStatesAndStateApiResponse =
  unknown;
export type DeleteApiProgramsByIdWorkflowLabelsStatesAndStateApiArg = {
  /** Program ID */
  id: string;
  /** Workflow state name (URL-encoded if contains spaces) */
  state: string;
};
export type DeleteApiProgramsByIdWorkflowLabelsTaskStatesAndStateApiResponse =
  unknown;
export type DeleteApiProgramsByIdWorkflowLabelsTaskStatesAndStateApiArg = {
  /** Program ID */
  id: string;
  /** Workflow task state name (URL-encoded if contains spaces) */
  state: string;
};
export type GetApiProgramsByIdWorkflowTypesApiResponse =
  /** status 200 OK */ string[];
export type GetApiProgramsByIdWorkflowTypesApiArg = {
  /** Program ID */
  id: string;
  /** Optional: only return types bound to this object type (e.g. Deliverable) */
  objectType?: string;
};
export type PostApiProgramsByIdWorkflowTypesApiResponse = unknown;
export type PostApiProgramsByIdWorkflowTypesApiArg = {
  /** Program ID */
  id: string;
  /** Workflow type request */
  handlersProgramWorkflowTypeRequest: HandlersProgramWorkflowTypeRequest;
};
export type DeleteApiProgramsByIdWorkflowTypesAndTypeApiResponse = unknown;
export type DeleteApiProgramsByIdWorkflowTypesAndTypeApiArg = {
  /** Program ID */
  id: string;
  /** Workflow type */
  type: string;
};
export type GetApiProjectsApiResponse =
  /** status 200 OK */ HandlersProjectListResponse;
export type GetApiProjectsApiArg = {
  /** Page number (default 1) */
  page?: number;
  /** Page size (default 20, max 100) */
  pageSize?: number;
  /** Filter by program ID (UUID) */
  programId?: string;
  /** Filter by owner ID (UUID) */
  ownerId?: string;
  /** Filter by location ID (UUID) */
  locationId?: string;
  /** Include archived projects (default false) */
  includeArchived?: boolean;
  /** Search in title, description, project code, location name, and owner name */
  search?: string;
  /** Filter by project code (exact match) */
  projectCode?: string;
  /** Filter by creation date start (YYYY-MM-DD) */
  createdStartDate?: string;
  /** Filter by creation date end (YYYY-MM-DD) */
  createdEndDate?: string;
  /** Filter by last modified start (YYYY-MM-DD) */
  modifiedStartDate?: string;
  /** Filter by last modified end (YYYY-MM-DD) */
  modifiedEndDate?: string;
};
export type PostApiProjectsApiResponse =
  /** status 201 Created */ HandlersProjectResponse;
export type PostApiProjectsApiArg = {
  /** Project creation request */
  handlersCreateProjectRequest: HandlersCreateProjectRequest;
};
export type GetApiProjectsDeliverableTypesApiResponse =
  /** status 200 OK */ HandlersProjectDeliverableTypeResponse[];
export type GetApiProjectsDeliverableTypesApiArg = {
  /** Filter by Project ID (UUID format) */
  projectId?: string;
};
export type GetApiProjectsByIdApiResponse =
  /** status 200 OK */ HandlersProjectResponse;
export type GetApiProjectsByIdApiArg = {
  /** Project ID (UUID) */
  id: string;
};
export type DeleteApiProjectsByIdApiResponse = unknown;
export type DeleteApiProjectsByIdApiArg = {
  /** Project ID (UUID) */
  id: string;
};
export type PatchApiProjectsByIdApiResponse =
  /** status 200 OK */ HandlersProjectResponse;
export type PatchApiProjectsByIdApiArg = {
  /** Project ID (UUID) */
  id: string;
  /** Project partial update request - only include fields to update */
  handlersUpdateProjectRequest: HandlersUpdateProjectRequest;
};
export type GetApiProjectsByIdClinsApiResponse =
  /** status 200 OK */ HandlersProjectClinResponse[];
export type GetApiProjectsByIdClinsApiArg = {
  /** Project ID */
  id: string;
};
export type PostApiProjectsByIdClinsApiResponse = unknown;
export type PostApiProjectsByIdClinsApiArg = {
  /** Project ID */
  id: string;
  /** Payload */
  handlersAddProjectClinRequest: HandlersAddProjectClinRequest;
};
export type DeleteApiProjectsByIdClinsAndClinIdApiResponse = unknown;
export type DeleteApiProjectsByIdClinsAndClinIdApiArg = {
  /** Project ID */
  id: string;
  /** CLIN ID */
  clinId: string;
};
export type PutApiProjectsByIdDeliverableTypesApiResponse =
  /** status 200 OK */ HandlersProjectDeliverableTypeResponse[];
export type PutApiProjectsByIdDeliverableTypesApiArg = {
  /** Project ID */
  id: string;
  /** Deliverable type IDs to associate (empty array removes all associations) */
  handlersProjectDeliverableTypesRequest: HandlersProjectDeliverableTypesRequest;
};
export type PostApiProjectsByIdDeliverableTypesApiResponse =
  /** status 201 Created */ HandlersProjectDeliverableTypeResponse[];
export type PostApiProjectsByIdDeliverableTypesApiArg = {
  /** Project ID */
  id: string;
  /** Deliverable type IDs to associate */
  handlersProjectDeliverableTypesRequest: HandlersProjectDeliverableTypesRequest;
};
export type GetApiProjectsByIdSummaryApiResponse =
  /** status 200 OK */ HandlersProjectSummaryResponse;
export type GetApiProjectsByIdSummaryApiArg = {
  /** Project ID (UUID) */
  id: string;
};
export type PutApiProjectsByIdSummaryApiResponse =
  /** status 200 OK */ HandlersProjectSummaryResponse;
export type PutApiProjectsByIdSummaryApiArg = {
  /** Project ID (UUID) */
  id: string;
  /** Updated project summary data */
  handlersProjectSummaryRequest: HandlersProjectSummaryRequest;
};
export type PostApiProjectsByIdSummaryApiResponse =
  /** status 201 Created */ HandlersProjectSummaryResponse;
export type PostApiProjectsByIdSummaryApiArg = {
  /** Project ID (UUID) */
  id: string;
  /** Project summary data */
  handlersProjectSummaryRequest: HandlersProjectSummaryRequest;
};
export type GetApiRipsApiResponse =
  /** status 200 Successfully retrieved RIPs */ HandlersRipListResponse;
export type GetApiRipsApiArg = {
  /** Filter by Project ID (UUID) */
  projectId?: string;
  /** Filter by To Contact ID (UUID) */
  toContactId?: string;
  /** Filter by From Contact ID (UUID) */
  fromContactId?: string;
  /** Filter by current version only (default: true) */
  currentVersion?: boolean;
  /** Search in RIP number, contractor name, client name, project name, subject */
  search?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Page size (default: 50, max: 100) */
  pageSize?: number;
};
export type PostApiRipsApiResponse =
  /** status 201 Successfully created RIP */ HandlersRipResponse;
export type PostApiRipsApiArg = {
  /** RIP creation data */
  handlersCreateRipRequest: HandlersCreateRipRequest;
};
export type GetApiRipsByIdApiResponse =
  /** status 200 Successfully retrieved RIP */ HandlersRipResponse;
export type GetApiRipsByIdApiArg = {
  /** RIP ID */
  id: string;
};
export type DeleteApiRipsByIdApiResponse = unknown;
export type DeleteApiRipsByIdApiArg = {
  /** RIP ID */
  id: string;
};
export type PatchApiRipsByIdApiResponse =
  /** status 200 Successfully updated RIP */ HandlersRipResponse;
export type PatchApiRipsByIdApiArg = {
  /** RIP ID */
  id: string;
  /** RIP update data */
  handlersUpdateRipRequest: HandlersUpdateRipRequest;
};
export type PostApiRipsByIdEstimateApiResponse =
  /** status 201 Successfully created estimate */ HandlersEstimateResponse;
export type PostApiRipsByIdEstimateApiArg = {
  /** RIP ID */
  id: string;
  /** Estimate details */
  handlersCreateEstimateRequest: HandlersCreateEstimateRequest;
};
export type DeleteApiRipsByIdEstimateAndEstimateIdApiResponse = unknown;
export type DeleteApiRipsByIdEstimateAndEstimateIdApiArg = {
  /** RIP ID */
  id: string;
  /** Estimate ID */
  estimateId: string;
};
export type PatchApiRipsByIdEstimateAndEstimateIdApiResponse =
  /** status 200 Successfully updated estimate */ HandlersEstimateResponse;
export type PatchApiRipsByIdEstimateAndEstimateIdApiArg = {
  /** RIP ID */
  id: string;
  /** Estimate ID */
  estimateId: string;
  /** Estimate update details */
  handlersUpdateEstimateRequest: HandlersUpdateEstimateRequest;
};
export type PostApiRipsByIdSubmitApiResponse =
  /** status 200 OK */ HandlersFormWorkflowResponse;
export type PostApiRipsByIdSubmitApiArg = {
  /** RIP ID */
  id: string;
};
export type GetApiRipsByIdVAndVersionApiResponse =
  /** status 200 Successfully retrieved RIP version */ HandlersRipResponse;
export type GetApiRipsByIdVAndVersionApiArg = {
  /** RIP ID */
  id: string;
  /** Version number */
  version: number;
};
export type GetApiRipsByIdVersionsApiResponse =
  /** status 200 Successfully retrieved RIP versions */ HandlersRipVersionResponse[];
export type GetApiRipsByIdVersionsApiArg = {
  /** RIP ID */
  id: string;
};
export type GetApiRisksApiResponse =
  /** status 200 OK */ HandlersIroRiskListResponse;
export type GetApiRisksApiArg = {
  /** Page number for pagination (default 1) */
  page?: number;
  /** Number of risks per page (default 50) */
  limit?: number;
  /** Filter by contract code */
  contractCode?: string;
  /** Filter by risk owner ID */
  ownerId?: string;
  /** Filter by escalation status */
  escalate?: boolean;
  /** Filter by closure status */
  isClosure?: boolean;
};
export type PostApiRisksApiResponse =
  /** status 201 Created */ HandlersIroRiskResponse;
export type PostApiRisksApiArg = {
  /** Risk data */
  handlersIroRiskCreateRequest: HandlersIroRiskCreateRequest;
};
export type GetApiRisksCategoriesApiResponse =
  /** status 200 OK */ DbIroRiskCategory[];
export type GetApiRisksCategoriesApiArg = void;
export type GetApiRisksCategoriesByCategoryCodeImpactsApiResponse =
  /** status 200 OK */ DbIroCategoryImpact[];
export type GetApiRisksCategoriesByCategoryCodeImpactsApiArg = {
  /** Category code */
  categoryCode: number;
};
export type GetApiRisksClosuresApiResponse =
  /** status 200 OK */ DbIroClosure[];
export type GetApiRisksClosuresApiArg = void;
export type GetApiRisksImpactsApiResponse = /** status 200 OK */ DbIroImpact[];
export type GetApiRisksImpactsApiArg = void;
export type GetApiRisksProbabilitiesApiResponse =
  /** status 200 OK */ DbIroProbability[];
export type GetApiRisksProbabilitiesApiArg = void;
export type GetApiRisksByRiskIdApiResponse =
  /** status 200 OK */ HandlersIroRiskResponse;
export type GetApiRisksByRiskIdApiArg = {
  /** Risk ID */
  riskId: number;
};
export type PatchApiRisksByRiskIdApiResponse =
  /** status 200 OK */ HandlersIroRiskResponse;
export type PatchApiRisksByRiskIdApiArg = {
  /** Risk ID */
  riskId: number;
  /** Risk updates */
  handlersIroRiskUpdateRequest: HandlersIroRiskUpdateRequest;
};
export type GetApiRisksByRiskIdVAndRecordUpdateApiResponse =
  /** status 200 OK */ HandlersIroRiskResponse;
export type GetApiRisksByRiskIdVAndRecordUpdateApiArg = {
  /** Risk ID */
  riskId: number;
  /** Record update timestamp */
  recordUpdate: string;
};
export type GetApiRisksByRiskIdVersionsApiResponse =
  /** status 200 OK */ HandlersIroRiskResponse[];
export type GetApiRisksByRiskIdVersionsApiArg = {
  /** Risk ID */
  riskId: number;
};
export type GetApiTamsInventoryApiResponse =
  /** status 200 OK */ HandlersTamsInventoryListResponse;
export type GetApiTamsInventoryApiArg = {
  /** Filter by project code */
  projectCode?: string;
  /** Filter by location */
  location?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Page size (default: 50, max: 100) */
  pageSize?: number;
};
export type PostApiTamsInventoryApiResponse =
  /** status 201 Created */ HandlersTamsInventoryResponse;
export type PostApiTamsInventoryApiArg = {
  /** Inventory item data */
  handlersCreateTamsInventoryRequest: HandlersCreateTamsInventoryRequest;
};
export type GetApiTamsInventoryByIdApiResponse =
  /** status 200 OK */ HandlersTamsInventoryResponse;
export type GetApiTamsInventoryByIdApiArg = {
  /** Inventory item ID */
  id: number;
};
export type DeleteApiTamsInventoryByIdApiResponse = unknown;
export type DeleteApiTamsInventoryByIdApiArg = {
  /** Inventory item ID */
  id: number;
};
export type PatchApiTamsInventoryByIdApiResponse =
  /** status 200 OK */ HandlersTamsInventoryResponse;
export type PatchApiTamsInventoryByIdApiArg = {
  /** Inventory item ID */
  id: number;
  /** Inventory item update data */
  handlersUpdateTamsInventoryRequest: HandlersUpdateTamsInventoryRequest;
};
export type GetApiTarsApiResponse =
  /** status 200 Successfully retrieved TARs */ HandlersTarListResponse;
export type GetApiTarsApiArg = {
  /** Filter by Project ID (UUID) */
  projectId?: string;
  /** Filter by TAR Contact ID (UUID) */
  tarContactId?: string;
  /** Filter by current version only (default: true) */
  currentVersion?: boolean;
  /** Search in TAR number and trip title */
  search?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Page size (default: 50, max: 100) */
  pageSize?: number;
};
export type PostApiTarsApiResponse =
  /** status 201 Successfully created TAR */ HandlersTarResponse;
export type PostApiTarsApiArg = {
  /** TAR creation data */
  handlersCreateTarRequest: HandlersCreateTarRequest;
};
export type GetApiTarsByIdApiResponse =
  /** status 200 Successfully retrieved TAR */ HandlersTarResponse;
export type GetApiTarsByIdApiArg = {
  /** TAR ID */
  id: string;
};
export type DeleteApiTarsByIdApiResponse = unknown;
export type DeleteApiTarsByIdApiArg = {
  /** TAR ID */
  id: string;
};
export type PatchApiTarsByIdApiResponse =
  /** status 200 Successfully updated TAR */ HandlersTarResponse;
export type PatchApiTarsByIdApiArg = {
  /** TAR ID */
  id: string;
  /** TAR update data */
  handlersUpdateTarRequest: HandlersUpdateTarRequest;
};
export type PostApiTarsByIdEstimateApiResponse =
  /** status 201 Successfully created estimate */ HandlersEstimateResponse;
export type PostApiTarsByIdEstimateApiArg = {
  /** TAR ID */
  id: string;
  /** Estimate details */
  handlersCreateEstimateRequest: HandlersCreateEstimateRequest;
};
export type DeleteApiTarsByIdEstimateAndEstimateIdApiResponse = unknown;
export type DeleteApiTarsByIdEstimateAndEstimateIdApiArg = {
  /** TAR ID */
  id: string;
  /** Estimate ID */
  estimateId: string;
};
export type PatchApiTarsByIdEstimateAndEstimateIdApiResponse =
  /** status 200 Successfully updated estimate */ HandlersEstimateResponse;
export type PatchApiTarsByIdEstimateAndEstimateIdApiArg = {
  /** TAR ID */
  id: string;
  /** Estimate ID */
  estimateId: string;
  /** Updated estimate details */
  handlersUpdateEstimateRequest: HandlersUpdateEstimateRequest;
};
export type PostApiTarsByIdItineraryApiResponse =
  /** status 201 Successfully created itinerary */ HandlersItineraryResponse;
export type PostApiTarsByIdItineraryApiArg = {
  /** TAR ID */
  id: string;
  /** Itinerary details */
  handlersCreateItineraryRequest: HandlersCreateItineraryRequest;
};
export type DeleteApiTarsByIdItineraryAndItineraryIdApiResponse = unknown;
export type DeleteApiTarsByIdItineraryAndItineraryIdApiArg = {
  /** TAR ID */
  id: string;
  /** Itinerary ID */
  itineraryId: string;
};
export type PatchApiTarsByIdItineraryAndItineraryIdApiResponse =
  /** status 200 Successfully updated itinerary */ HandlersItineraryResponse;
export type PatchApiTarsByIdItineraryAndItineraryIdApiArg = {
  /** TAR ID */
  id: string;
  /** Itinerary ID */
  itineraryId: string;
  /** Updated itinerary details */
  handlersUpdateItineraryRequest: HandlersUpdateItineraryRequest;
};
export type PostApiTarsByIdItineraryAndItineraryIdEstimateApiResponse =
  /** status 201 Successfully created estimate */ HandlersEstimateResponse;
export type PostApiTarsByIdItineraryAndItineraryIdEstimateApiArg = {
  /** TAR ID */
  id: string;
  /** Itinerary ID */
  itineraryId: string;
  /** Estimate details */
  handlersCreateEstimateRequest: HandlersCreateEstimateRequest;
};
export type DeleteApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiResponse =
  unknown;
export type DeleteApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiArg = {
  /** TAR ID */
  id: string;
  /** Itinerary ID */
  itineraryId: string;
  /** Estimate ID */
  estimateId: string;
};
export type PatchApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiResponse =
  /** status 200 Successfully updated estimate */ HandlersEstimateResponse;
export type PatchApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdApiArg = {
  /** TAR ID */
  id: string;
  /** Itinerary ID */
  itineraryId: string;
  /** Estimate ID */
  estimateId: string;
  /** Updated estimate details */
  handlersUpdateEstimateRequest: HandlersUpdateEstimateRequest;
};
export type PostApiTarsByIdSubmitApiResponse =
  /** status 200 OK */ HandlersFormWorkflowResponse;
export type PostApiTarsByIdSubmitApiArg = {
  /** TAR ID */
  id: string;
};
export type GetApiTarsByIdVAndVersionApiResponse =
  /** status 200 Successfully retrieved TAR version */ HandlersTarResponse;
export type GetApiTarsByIdVAndVersionApiArg = {
  /** TAR ID */
  id: string;
  /** Version number */
  version: number;
};
export type GetApiTarsByIdVersionsApiResponse =
  /** status 200 Successfully retrieved TAR versions */ HandlersTarVersionResponse[];
export type GetApiTarsByIdVersionsApiArg = {
  /** TAR ID */
  id: string;
};
export type GetApiTokenApiResponse =
  /** status 200 JWT token string containing userId, fullName, username, projectAcl, programAcl, and expiration (4 hours) */ string;
export type GetApiTokenApiArg = {
  /** Session cookie set by the configured auth provider */
  loginToken: string;
};
export type GetApiTripReportsApiResponse =
  /** status 200 Successful response with paginated TR list */ HandlersTrListResponse;
export type GetApiTripReportsApiArg = {
  /** Filter by Project ID */
  projectId?: string;
  /** Filter by TAR ID */
  tarId?: string;
  /** Filter by Traveler ID */
  travelerId?: string;
  /** Filter by current version only */
  currentVersion?: boolean;
  /** Search in TR number, project name, and purpose */
  search?: string;
  /** Page number for pagination */
  page?: number;
  /** Number of items per page (1-100) */
  pageSize?: number;
};
export type PostApiTripReportsApiResponse =
  /** status 201 Successfully created TR */ HandlersTrResponse;
export type PostApiTripReportsApiArg = {
  /** TR creation request */
  handlersCreateTrRequest: HandlersCreateTrRequest;
};
export type GetApiTripReportsByIdApiResponse =
  /** status 200 Successfully retrieved TR */ HandlersTrResponse;
export type GetApiTripReportsByIdApiArg = {
  /** TR ID (UUID format) */
  id: string;
};
export type DeleteApiTripReportsByIdApiResponse = unknown;
export type DeleteApiTripReportsByIdApiArg = {
  /** TR ID (UUID format) */
  id: string;
};
export type PatchApiTripReportsByIdApiResponse =
  /** status 200 Successfully updated TR */ HandlersTrResponse;
export type PatchApiTripReportsByIdApiArg = {
  /** TR ID (UUID format) */
  id: string;
  /** TR update request */
  handlersUpdateTrRequest: HandlersUpdateTrRequest;
};
export type PostApiTripReportsByIdSubmitApiResponse =
  /** status 200 OK */ HandlersFormWorkflowResponse;
export type PostApiTripReportsByIdSubmitApiArg = {
  /** Trip Report ID */
  id: string;
};
export type GetApiTripReportsByIdVAndVersionApiResponse =
  /** status 200 Successfully retrieved TR version */ HandlersTrResponse;
export type GetApiTripReportsByIdVAndVersionApiArg = {
  /** TR ID (UUID format) */
  id: string;
  /** Version number */
  version: number;
};
export type GetApiTripReportsByIdVersionsApiResponse =
  /** status 200 Successfully retrieved version history */ HandlersTrVersionResponse[];
export type GetApiTripReportsByIdVersionsApiArg = {
  /** TR ID (UUID format) */
  id: string;
};
export type GetApiTytoauthApiResponse = unknown;
export type GetApiTytoauthApiArg = void;
export type PostApiTytoauthApiResponse = unknown;
export type PostApiTytoauthApiArg = void;
export type GetApiUsersApiResponse =
  /** status 200 OK */ HandlersUserDetailResponse[];
export type GetApiUsersApiArg = void;
export type PostApiUsersApiResponse =
  /** status 201 Created */ HandlersUserDetailResponse;
export type PostApiUsersApiArg = {
  /** User creation request */
  handlersCreateUserRequest: HandlersCreateUserRequest;
};
export type GetApiUsersActionsApiResponse = /** status 200 OK */ {
  [key: string]: HandlersActionResponse;
};
export type GetApiUsersActionsApiArg = void;
export type GetApiUsersMeApiResponse =
  /** status 200 OK */ HandlersUserDetailResponse;
export type GetApiUsersMeApiArg = void;
export type GetApiUsersMeNotificationPreferencesApiResponse =
  /** status 200 OK */ HandlersNotificationPreferencesResponse;
export type GetApiUsersMeNotificationPreferencesApiArg = void;
export type PutApiUsersMeNotificationPreferencesApiResponse =
  /** status 200 OK */ {
    [key: string]: string;
  };
export type PutApiUsersMeNotificationPreferencesApiArg = {
  /** Preferences to update */
  handlersUpdateNotificationPreferencesRequest: HandlersUpdateNotificationPreferencesRequest;
};
export type GetApiUsersRolesApiResponse =
  /** status 200 OK */ DbRoleWithActions[];
export type GetApiUsersRolesApiArg = void;
export type GetApiUsersTypesApiResponse = /** status 200 OK */ DbUserType[];
export type GetApiUsersTypesApiArg = void;
export type PostApiUsersTypesApiResponse =
  /** status 201 Created */ HandlersSimpleCatalogResponse;
export type PostApiUsersTypesApiArg = {
  /** Payload */
  handlersSimpleCatalogRequest: HandlersSimpleCatalogRequest;
};
export type GetApiUsersTypesByUserTypeApiResponse =
  /** status 200 OK */ HandlersSimpleCatalogResponse;
export type GetApiUsersTypesByUserTypeApiArg = {
  /** User type */
  userType: string;
};
export type DeleteApiUsersTypesByUserTypeApiResponse = unknown;
export type DeleteApiUsersTypesByUserTypeApiArg = {
  /** User type */
  userType: string;
};
export type PatchApiUsersTypesByUserTypeApiResponse =
  /** status 200 OK */ HandlersSimpleCatalogResponse;
export type PatchApiUsersTypesByUserTypeApiArg = {
  /** User type */
  userType: string;
  /** Payload */
  handlersSimpleCatalogUpdateRequest: HandlersSimpleCatalogUpdateRequest;
};
export type GetApiUsersByIdApiResponse =
  /** status 200 OK */ HandlersUserDetailResponse;
export type GetApiUsersByIdApiArg = {
  /** User ID */
  id: string;
};
export type DeleteApiUsersByIdApiResponse = unknown;
export type DeleteApiUsersByIdApiArg = {
  /** User ID */
  id: string;
};
export type PatchApiUsersByIdApiResponse =
  /** status 200 OK */ HandlersUserDetailResponse;
export type PatchApiUsersByIdApiArg = {
  /** User ID */
  id: string;
  /** User update request */
  handlersUpdateUserRequest: HandlersUpdateUserRequest;
};
export type GetApiUsersByIdAssignedTasksApiResponse =
  /** status 200 OK */ HandlersUserTaskResponse[];
export type GetApiUsersByIdAssignedTasksApiArg = {
  /** User ID (not Contact ID) - The authenticated user's ID */
  id: string;
};
export type GetApiUsersByIdWorkflowsApiResponse =
  /** status 200 OK */ HandlersUserWorkflowResponse[];
export type GetApiUsersByIdWorkflowsApiArg = {
  /** User ID (not Contact ID) - The authenticated user's ID */
  id: string;
};
export type GetApiUsersByUserIdRoleProjectsApiResponse =
  /** status 200 OK */ HandlersUserRoleProjectResponse[];
export type GetApiUsersByUserIdRoleProjectsApiArg = {
  /** User ID */
  userId: string;
};
export type PostApiUsersByUserIdRoleProjectsApiResponse =
  /** status 201 Created */ HandlersUserRoleProjectResponse;
export type PostApiUsersByUserIdRoleProjectsApiArg = {
  /** User ID */
  userId: string;
  /** Payload */
  handlersAddUserRoleProjectRequest: HandlersAddUserRoleProjectRequest;
};
export type DeleteApiUsersByUserIdRoleProjectsAndRoleNameProgramIdProjectIdApiResponse =
  unknown;
export type DeleteApiUsersByUserIdRoleProjectsAndRoleNameProgramIdProjectIdApiArg =
  {
    /** User ID */
    userId: string;
    /** Role name */
    roleName: string;
    /** Program ID */
    programId: string;
    /** Project ID */
    projectId: string;
  };
export type GetApiUsersByUserIdRolesApiResponse =
  /** status 200 OK */ HandlersUserRoleResponse[];
export type GetApiUsersByUserIdRolesApiArg = {
  /** User ID */
  userId: string;
};
export type PostApiUsersByUserIdRolesApiResponse =
  /** status 201 Created */ HandlersUserRoleResponse;
export type PostApiUsersByUserIdRolesApiArg = {
  /** User ID */
  userId: string;
  /** Payload */
  handlersAddUserRoleRequest: HandlersAddUserRoleRequest;
};
export type DeleteApiUsersByUserIdRolesAndRoleNameProgramIdApiResponse =
  unknown;
export type DeleteApiUsersByUserIdRolesAndRoleNameProgramIdApiArg = {
  /** User ID */
  userId: string;
  /** Role name */
  roleName: string;
  /** Program ID */
  programId: string;
  /** Delete orphan UserRoleProject rows too */
  cascade?: boolean;
};
export type GetApiVersionApiResponse =
  /** status 200 OK */ HandlersVersionResponse;
export type GetApiVersionApiArg = void;
export type GetApiWorkflowsApiResponse =
  /** status 200 OK */ HandlersWorkflowListResponse;
export type GetApiWorkflowsApiArg = {
  /** Filter by object type */
  objectType?: string;
  /** Filter by object ID (UUID) */
  objectId?: string;
  /** Filter by workflow type */
  workflowType?: string;
  /** Filter by workflow state */
  workflowState?: string;
  /** Page number (default: 1) */
  page?: number;
  /** Items per page (default: 50) */
  limit?: number;
};
export type PostApiWorkflowsApiResponse =
  /** status 201 Created */ HandlersWorkflowResponse;
export type PostApiWorkflowsApiArg = {
  /** Workflow to create */
  handlersWorkflowCreateRequest: HandlersWorkflowCreateRequest;
};
export type GetApiWorkflowsStatesApiResponse =
  /** status 200 OK */ DbWorkflowState[];
export type GetApiWorkflowsStatesApiArg = {
  /** Include archived states */
  archived?: boolean;
};
export type PostApiWorkflowsStatesApiResponse =
  /** status 201 Created */ HandlersWorkflowCatalogResponse;
export type PostApiWorkflowsStatesApiArg = {
  /** Payload */
  handlersWorkflowCatalogRequest: HandlersWorkflowCatalogRequest;
};
export type GetApiWorkflowsStatesByStateApiResponse =
  /** status 200 OK */ HandlersWorkflowCatalogResponse;
export type GetApiWorkflowsStatesByStateApiArg = {
  /** Workflow state */
  state: string;
};
export type DeleteApiWorkflowsStatesByStateApiResponse = unknown;
export type DeleteApiWorkflowsStatesByStateApiArg = {
  /** Workflow state */
  state: string;
};
export type PatchApiWorkflowsStatesByStateApiResponse =
  /** status 200 OK */ HandlersWorkflowCatalogResponse;
export type PatchApiWorkflowsStatesByStateApiArg = {
  /** Workflow state */
  state: string;
  /** Payload */
  handlersWorkflowCatalogUpdateRequest: HandlersWorkflowCatalogUpdateRequest;
};
export type GetApiWorkflowsTaskStatesApiResponse =
  /** status 200 OK */ DbWorkflowTaskState[];
export type GetApiWorkflowsTaskStatesApiArg = {
  /** Include archived states */
  archived?: boolean;
};
export type PostApiWorkflowsTaskStatesApiResponse =
  /** status 201 Created */ HandlersWorkflowCatalogResponse;
export type PostApiWorkflowsTaskStatesApiArg = {
  /** Payload */
  handlersWorkflowCatalogRequest: HandlersWorkflowCatalogRequest;
};
export type GetApiWorkflowsTaskStatesByStateApiResponse =
  /** status 200 OK */ HandlersWorkflowCatalogResponse;
export type GetApiWorkflowsTaskStatesByStateApiArg = {
  /** Workflow task state */
  state: string;
};
export type DeleteApiWorkflowsTaskStatesByStateApiResponse = unknown;
export type DeleteApiWorkflowsTaskStatesByStateApiArg = {
  /** Workflow task state */
  state: string;
};
export type PatchApiWorkflowsTaskStatesByStateApiResponse =
  /** status 200 OK */ HandlersWorkflowCatalogResponse;
export type PatchApiWorkflowsTaskStatesByStateApiArg = {
  /** Workflow task state */
  state: string;
  /** Payload */
  handlersWorkflowCatalogUpdateRequest: HandlersWorkflowCatalogUpdateRequest;
};
export type GetApiWorkflowsTypesApiResponse =
  /** status 200 OK */ HandlersWorkflowTypeWithSigning[];
export type GetApiWorkflowsTypesApiArg = {
  /** Include archived types */
  archived?: boolean;
};
export type PostApiWorkflowsTypesApiResponse =
  /** status 201 Created */ HandlersWorkflowCatalogResponse;
export type PostApiWorkflowsTypesApiArg = {
  /** Payload */
  handlersWorkflowCatalogRequest: HandlersWorkflowCatalogRequest;
};
export type GetApiWorkflowsTypesByWorkflowTypeApiResponse =
  /** status 200 OK */ HandlersWorkflowCatalogResponse;
export type GetApiWorkflowsTypesByWorkflowTypeApiArg = {
  /** Workflow type */
  workflowType: string;
};
export type DeleteApiWorkflowsTypesByWorkflowTypeApiResponse = unknown;
export type DeleteApiWorkflowsTypesByWorkflowTypeApiArg = {
  /** Workflow type */
  workflowType: string;
};
export type PatchApiWorkflowsTypesByWorkflowTypeApiResponse =
  /** status 200 OK */ HandlersWorkflowCatalogResponse;
export type PatchApiWorkflowsTypesByWorkflowTypeApiArg = {
  /** Workflow type */
  workflowType: string;
  /** Payload */
  handlersWorkflowCatalogUpdateRequest: HandlersWorkflowCatalogUpdateRequest;
};
export type GetApiWorkflowsByIdApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type GetApiWorkflowsByIdApiArg = {
  /** Workflow ID (UUID) */
  id: string;
};
export type DeleteApiWorkflowsByIdApiResponse = unknown;
export type DeleteApiWorkflowsByIdApiArg = {
  /** Workflow ID (UUID) */
  id: string;
};
export type PatchApiWorkflowsByIdApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type PatchApiWorkflowsByIdApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Workflow fields to update */
  handlersWorkflowUpdateRequest: HandlersWorkflowUpdateRequest;
};
export type PostApiWorkflowsByIdCompleteApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type PostApiWorkflowsByIdCompleteApiArg = {
  /** Workflow ID (UUID) */
  id: string;
};
export type PostApiWorkflowsByIdStartApiResponse =
  /** status 200 OK */ HandlersWorkflowResponse;
export type PostApiWorkflowsByIdStartApiArg = {
  /** Workflow ID (UUID) */
  id: string;
};
export type GetApiWorkflowsByIdTasksApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskResponse[];
export type GetApiWorkflowsByIdTasksApiArg = {
  /** Workflow ID (UUID) */
  id: string;
};
export type PostApiWorkflowsByIdTasksApiResponse =
  /** status 201 Created */ HandlersWorkflowTaskResponse;
export type PostApiWorkflowsByIdTasksApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task to create */
  handlersWorkflowTaskCreateRequest: HandlersWorkflowTaskCreateRequest;
};
export type GetApiWorkflowsByIdTasksAndTaskNumberApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskResponse;
export type GetApiWorkflowsByIdTasksAndTaskNumberApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
};
export type DeleteApiWorkflowsByIdTasksAndTaskNumberApiResponse = unknown;
export type DeleteApiWorkflowsByIdTasksAndTaskNumberApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
};
export type PatchApiWorkflowsByIdTasksAndTaskNumberApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskResponse;
export type PatchApiWorkflowsByIdTasksAndTaskNumberApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Task fields to update */
  handlersWorkflowTaskUpdateRequest: HandlersWorkflowTaskUpdateRequest;
};
export type GetApiWorkflowsByIdTasksAndTaskNumberActionsApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskActionResponse[];
export type GetApiWorkflowsByIdTasksAndTaskNumberActionsApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
};
export type PostApiWorkflowsByIdTasksAndTaskNumberActionsApiResponse =
  /** status 201 Created */ HandlersWorkflowTaskActionResponse;
export type PostApiWorkflowsByIdTasksAndTaskNumberActionsApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Action to create */
  handlersWorkflowTaskActionCreateRequest: HandlersWorkflowTaskActionCreateRequest;
};
export type GetApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskActionResponse;
export type GetApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Action Number */
  actionNumber: number;
};
export type DeleteApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiResponse =
  unknown;
export type DeleteApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiArg =
  {
    /** Workflow ID (UUID) */
    id: string;
    /** Task Number */
    taskNumber: number;
    /** Action Number */
    actionNumber: number;
  };
export type PatchApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskActionResponse;
export type PatchApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Action Number */
  actionNumber: number;
  /** Action fields to update */
  handlersWorkflowTaskActionUpdateRequest: HandlersWorkflowTaskActionUpdateRequest;
};
export type PostApiWorkflowsByIdTasksAndTaskNumberApproveApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskResponse;
export type PostApiWorkflowsByIdTasksAndTaskNumberApproveApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Approval details */
  handlersTaskApprovalRequest: HandlersTaskApprovalRequest;
};
export type PostApiWorkflowsByIdTasksAndTaskNumberAssignApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskResponse;
export type PostApiWorkflowsByIdTasksAndTaskNumberAssignApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Assignment details */
  handlersTaskAssignmentRequest: HandlersTaskAssignmentRequest;
};
export type PostApiWorkflowsByIdTasksAndTaskNumberRejectApiResponse =
  /** status 200 OK */ HandlersWorkflowTaskResponse;
export type PostApiWorkflowsByIdTasksAndTaskNumberRejectApiArg = {
  /** Workflow ID (UUID) */
  id: string;
  /** Task Number */
  taskNumber: number;
  /** Rejection details */
  handlersTaskRejectionRequest: HandlersTaskRejectionRequest;
};
export type GetDemoByFilenameApiResponse = /** status 200 HTML page */ string;
export type GetDemoByFilenameApiArg = {
  /** Demo filename, e.g. release-0.15.0.html */
  filename: string;
};
export type GetHealthzApiResponse = /** status 200 OK */ {
  [key: string]: string;
};
export type GetHealthzApiArg = void;
export type GetReadyApiResponse = /** status 200 OK */ {
  [key: string]: string;
};
export type GetReadyApiArg = void;
export type PostSamlAcsApiResponse = unknown;
export type PostSamlAcsApiArg = {
  body: {
    /** Base64-encoded SAML assertion from identity provider */
    SAMLResponse: string;
  };
};
export type GetSamlMetadataApiResponse = unknown;
export type GetSamlMetadataApiArg = void;
export type GetSamlSloApiResponse = unknown;
export type GetSamlSloApiArg = void;
export type PostSignaturesApiResponse =
  /** status 201 Created */ HandlersSignatureResponse;
export type PostSignaturesApiArg = {
  /** CMS + digest */
  handlersSignatureCreateRequest: HandlersSignatureCreateRequest;
};
export type GetSignaturesByIdApiResponse =
  /** status 200 OK */ HandlersSignatureResponse;
export type GetSignaturesByIdApiArg = {
  /** Signature ID (uuid) */
  id: string;
};
export type HandlersActionCatalogResponse = {
  actionName?: string;
  actionScope?: string;
  actionType?: string;
  archived?: boolean;
  description?: string;
};
export type UtilErr401 = {
  code?: number;
  details?: string;
  error?: string;
};
export type UtilErrorResponse = {
  code?: number;
  details?: string;
  error?: string;
  errorCode?: string;
};
export type UtilErr403 = {
  code?: number;
  details?: string;
  error?: string;
};
export type UtilErr500 = {
  code?: number;
  details?: string;
  error?: string;
  errorCode?: string;
};
export type HandlersCreateActionRequest = {
  actionName: string;
  actionScope: string;
  actionType: string;
  description?: string;
};
export type UtilErr400 = {
  code?: number;
  details?: string;
  error?: string;
};
export type HandlersUpdateActionRequest = {
  actionScope?: string;
  actionType?: string;
  description?: string;
};
export type HandlersNotificationPreferenceResponse = {
  description?: string;
  enabled?: boolean;
  label?: string;
  notificationType?: string;
};
export type HandlersRoleNotificationPreferenceRoleResponse = {
  preferences?: HandlersNotificationPreferenceResponse[];
  programId?: string;
  roleName?: string;
  roleType?: string;
  scope?: string;
};
export type HandlersRoleNotificationPreferencesResponse = {
  roles?: HandlersRoleNotificationPreferenceRoleResponse[];
};
export type HandlersNotificationPreferenceInput = {
  enabled?: boolean;
  notificationType?: string;
};
export type HandlersUpdateRoleNotificationPreferencesRequest = {
  preferences?: HandlersNotificationPreferenceInput[];
  programId?: string;
  roleName?: string;
};
export type DbAuditLogAction = {
  actionCompleted?: string;
  actionName?: string;
  actionNumber?: number;
  /** Contact name */
  actionOwner?: string;
  actionStarted?: string;
  workflowTaskState?: string;
};
export type DbAuditLogTask = {
  actions?: DbAuditLogAction[];
  comment?: string;
  taskCompleted?: string;
  taskName?: string;
  /** Contact name */
  taskOwner?: string;
  taskStarted?: string;
  workflowTaskNumber?: number;
  workflowTaskState?: string;
};
export type HandlersApprovalQueueItem = {
  auditLog?: DbAuditLogTask[];
  description?: string;
  dueDate?: string;
  objectId?: string;
  objectType?: string;
  /** RequiredRoleAction is the role action a caller must hold to act on this
    task's phase (e.g. "tar-approval-cor"). Empty for legacy / non-role-gated
    phases, letting the UI enable Approve/Reject per role-gated TAR phase (#1301). */
  requiredRoleAction?: string;
  taskName?: string;
  taskOwnerId?: string;
  taskOwnerName?: string;
  taskStarted?: string;
  workflowId?: string;
  workflowName?: string;
  workflowStarted?: string;
  workflowState?: string;
  workflowTaskNumber?: number;
  workflowTaskState?: string;
  workflowType?: string;
};
export type HandlersApprovalQueueResponse = {
  data?: HandlersApprovalQueueItem[];
  limit?: number;
  page?: number;
  totalCount?: number;
  totalPages?: number;
};
export type HandlersApprovalQueueStatsResponse = {
  averageTasksPerWorkflow?: number;
  byObjectType?: {
    [key: string]: number;
  };
  byTaskState?: {
    [key: string]: number;
  };
  byWorkflowState?: {
    [key: string]: number;
  };
  byWorkflowType?: {
    [key: string]: number;
  };
  oldestPendingWorkflow?: string;
  totalPendingTasks?: number;
};
export type HandlersAttachmentResponse = {
  /** Whether this attachment version is archived (soft deleted) */
  archived?: boolean;
  /** UUID of the attachment */
  attachmentId?: string;
  /** Internal blob storage identifier for file content */
  blobId?: string;
  /** UUID of the Contact who created this attachment */
  creatorContactId?: string;
  /** Timestamp when this version was created */
  dateCreated?: string;
  /** Description of the attachment */
  description?: string;
  /** Signed URL for direct download (omitted when blob storage is unavailable; TTL controlled by BLOB_SIGNED_URL_TTL_SECONDS) */
  downloadUrl?: string;
  /** DEPRECATED (#1620): no longer populated (per-attachment blob stat removed); omitted when zero */
  fileSize?: number;
  /** UUID of the FormSection used for categorization */
  formSectionId?: string;
  /** Whether this is the latest version of the attachment */
  isLatestVersion?: boolean;
  /** MIME type of the file (null if no file content) */
  mimeType?: string;
  /** Display name of the attachment file */
  name?: string;
  /** UUID of the entity this attachment belongs to (Project/Program/Deliverable/etc.) */
  objectId?: string;
  /** Type of entity referenced by objectId */
  objectType?: string;
  /** Version number of this attachment (starts at 1) */
  versionNumber?: number;
};
export type HandlersAttachmentListResponse = {
  /** Array of attachment metadata */
  attachments?: HandlersAttachmentResponse[];
  /** Current page number */
  page?: number;
  /** Number of items per page */
  pageSize?: number;
  /** Total number of attachments matching the filter criteria */
  total?: number;
};
export type UtilErr503 = {
  code?: number;
  details?: string;
  error?: string;
  errorCode?: string;
};
export type HandlersFailedUpload = {
  /** Error message describing why the upload failed */
  error?: string;
  /** Name of the file that failed */
  filename?: string;
  /** Index of the file in the batch request (0-based) */
  index?: number;
};
export type HandlersBatchSummary = {
  /** Number of files that failed to upload */
  totalFailed?: number;
  /** Total number of files in the batch request */
  totalRequested?: number;
  /** Number of files that uploaded successfully */
  totalSucceeded?: number;
};
export type HandlersBatchUploadResponse = {
  /** Array of failed uploads with error details */
  failed?: HandlersFailedUpload[];
  /** Array of successfully uploaded attachments */
  succeeded?: HandlersAttachmentResponse[];
  /** Summary statistics for the batch operation */
  summary?: HandlersBatchSummary;
};
export type HandlersDownloadAttachmentsZipRequest = {
  /** Attachment IDs to include in the archive. The 10000 ceiling is a sanity
    guardrail (real deliverables carry a few thousand); the archive streams
    entry-by-entry so the count no longer implies a whole-archive buffer (#1615). */
  attachmentIds: string[];
};
export type HandlersUpdateAttachmentRequest = {
  /** New description for the attachment (optional) */
  description?: string;
  /** New display name for the attachment (optional) */
  name?: string;
};
export type HandlersAuditLogResponse = {
  logDescription?: string;
  logId?: string;
  logTime?: string;
  logTitle?: string;
  objectId?: string;
  objectType?: string;
  triggeredUserId?: string;
  verboseLevel?: number;
};
export type HandlersAuditLogListResponse = {
  items?: HandlersAuditLogResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersCreateAuditLogRequest = {
  logDescription: string;
  logTitle: string;
  objectId?: string;
  objectType?: string;
  verboseLevel: number;
};
export type HandlersVerboseLevelResponse = {
  description?: string;
  verboseLevel?: number;
  verboseTitle?: string;
};
export type DbEtlRunLog = {
  errorCode?: string;
  errorDetail?: string;
  errorMessage?: string;
  finishedAt?: string;
  rowsAssignments?: number;
  rowsCandWorkflow?: number;
  rowsCategories?: number;
  rowsCatimpact?: number;
  rowsClosures?: number;
  rowsCustomers?: number;
  rowsEmployees?: number;
  rowsImpacts?: number;
  rowsInvoices?: number;
  rowsLineItems?: number;
  rowsLoaded?: number;
  rowsOpenReqs?: number;
  rowsPendingHires?: number;
  rowsProbabilities?: number;
  rowsRejects?: number;
  rowsResourceTraining?: number;
  rowsResources?: number;
  rowsRisks?: number;
  rowsTrainingHdr?: number;
  rowsTrainingType?: number;
  runId?: number;
  runType?: string;
  startedAt?: string;
  status?: string;
};
export type HandlersClinResponse = {
  clinCode?: string;
  clinId?: string;
  clinPeriod?: string;
  clinTitle?: string;
  description?: string;
  funded?: number;
  popEndDate?: string;
  popStartDate?: string;
  programId?: string;
  projectAllocated?: number;
  projectAllocationRemaining?: number;
};
export type HandlersClinListResponse = {
  items?: HandlersClinResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersCreateClinRequest = {
  clinCode: string;
  clinPeriod?: string;
  clinTitle?: string;
  description?: string;
  funded?: number;
  popEndDate?: string;
  popStartDate?: string;
  programId: string;
  projectAllocated?: number;
};
export type HandlersUpdateClinRequest = {
  clinPeriod?: string;
  clinTitle?: string;
  description?: string;
  funded?: number;
  popEndDate?: string;
  popStartDate?: string;
  projectAllocated?: number;
};
export type HandlersContactResponse = {
  address?: string;
  contactId?: string;
  govEmail?: string;
  name?: string;
  organizationId?: string;
  personalEmail?: string;
  personalPhone?: string;
  title?: string;
  /** UPN is the Microsoft userPrincipalName claim used to bind a
    Contact to its signing certificate's SAN otherName. Populated by
    SAML login (#1001) or manual provisioning; surfaced here so the
    release-demo flow can round-trip the value through POST/GET. */
  upn?: string;
  workEmail?: string;
  workPhone?: string;
};
export type HandlersContactListResponse = {
  items?: HandlersContactResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersCreateContactRequest = {
  address?: string;
  govEmail?: string;
  name: string;
  organizationId?: string;
  personalEmail?: string;
  personalPhone?: string;
  title?: string;
  upn?: string;
  workEmail?: string;
  workPhone?: string;
};
export type HandlersUpdateContactRequest = {
  address?: string;
  govEmail?: string;
  name?: string;
  organizationId?: string;
  personalEmail?: string;
  personalPhone?: string;
  title?: string;
  workEmail?: string;
  workPhone?: string;
};
export type HandlersEstimateLineItemResponse = {
  authorizedIndirectCosts?: number;
  clearanceName?: string;
  days?: number;
  description?: string;
  fee?: number;
  ftes?: number;
  lineItem?: number;
  locations?: string;
  objectId?: string;
  objectName?: string;
  objectType?: string;
  overtimeHours?: number;
  quantity?: number;
  totalLineItemEstimate?: number;
  totalNTECost?: number;
  unitCost?: number;
  unitType?: string;
};
export type HandlersEstimateResponse = {
  estimateId?: string;
  estimateType?: string;
  lineItems?: HandlersEstimateLineItemResponse[];
  totalEstimate?: number;
};
export type HandlersCtpResponse = {
  associatedLineOfAccounting?: string;
  attachments?: HandlersAttachmentResponse[];
  awardPIID?: string;
  clientName?: string;
  clinx002Value?: number;
  contractorName?: string;
  ctpDate?: string;
  ctpId?: string;
  ctpNumber?: string;
  cumulativeAmountBilled?: number;
  currentCLINX002Balance?: number;
  currentVersion?: boolean;
  dateCreated?: string;
  dateSubmitted?: string;
  descOfSuppliesOrServices?: string;
  description?: string;
  dueDate?: string;
  estimates?: HandlersEstimateResponse[];
  formId?: string;
  formVersionNumber?: number;
  fromContactId?: string;
  lastModified?: string;
  newCLINX002Balance?: number;
  projectId?: string;
  projectName?: string;
  proposedSubcontractor?: string;
  proposedSubcontractorPrice?: number;
  purposeJustification?: string;
  rawFormData?: string;
  subject?: string;
  throughContactId?: string;
  toContactId?: string;
  typeofSubcontract?: string;
  versionNumber?: number;
  workflowId?: string;
  workflowState?: string;
};
export type HandlersCtpListResponse = {
  ctps?: HandlersCtpResponse[];
  page?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
};
export type UtilErr404 = {
  code?: number;
  details?: string;
  error?: string;
};
export type HandlersCreateEstimateLineItemRequest = {
  authorizedIndirectCosts?: number;
  days?: number;
  description?: string;
  fee?: number;
  ftes?: number;
  locations?: string;
  overtimeHours?: number;
  quantity?: number;
  totalLineItemEstimate?: number;
  totalNTECost?: number;
  unitCost?: number;
  unitType?: string;
};
export type HandlersCreateEstimateRequest = {
  estimateType: string;
  lineItems?: HandlersCreateEstimateLineItemRequest[];
  objectName?: string;
  totalEstimate?: number;
};
export type HandlersCreateCtpRequest = {
  associatedLineOfAccounting?: string;
  awardPIID?: string;
  clientName?: string;
  clinx002Value?: number;
  contractorName?: string;
  ctpDate?: string;
  ctpNumber: string;
  cumulativeAmountBilled?: number;
  currentCLINX002Balance?: number;
  descOfSuppliesOrServices?: string;
  description?: string;
  dueDate?: string;
  estimates?: HandlersCreateEstimateRequest[];
  formId: string;
  formVersionNumber: number;
  fromContactId?: string;
  newCLINX002Balance?: number;
  projectId?: string;
  projectName?: string;
  proposedSubcontractor?: string;
  proposedSubcontractorPrice?: number;
  purposeJustification?: string;
  rawFormData?: string;
  subject?: string;
  throughContactId?: string;
  toContactId?: string;
  typeofSubcontract?: string;
};
export type HandlersUpdateCtpRequest = {
  associatedLineOfAccounting?: string;
  awardPIID?: string;
  clientName?: string;
  clinx002Value?: number;
  contractorName?: string;
  ctpDate?: string;
  ctpNumber?: string;
  cumulativeAmountBilled?: number;
  currentCLINX002Balance?: number;
  descOfSuppliesOrServices?: string;
  description?: string;
  dueDate?: string;
  estimates?: HandlersCreateEstimateRequest[];
  fromContactId?: string;
  newCLINX002Balance?: number;
  projectName?: string;
  proposedSubcontractor?: string;
  proposedSubcontractorPrice?: number;
  purposeJustification?: string;
  rawFormData?: string;
  subject?: string;
  throughContactId?: string;
  toContactId?: string;
  typeofSubcontract?: string;
};
export type HandlersUpdateEstimateRequest = {
  estimateType?: string;
  lineItems?: HandlersCreateEstimateLineItemRequest[];
  objectName?: string;
  totalEstimate?: number;
};
export type HandlersFormWorkflowResponse = {
  description?: string;
  name?: string;
  objectId?: string;
  objectType?: string;
  objectVersionNumber?: number;
  workflowCompleted?: string;
  workflowId?: string;
  workflowStarted?: string;
  workflowState?: string;
  workflowType?: string;
};
export type HandlersCtpVersionResponse = {
  ctpId?: string;
  currentVersion?: boolean;
  dateCreated?: string;
  lastModified?: string;
  versionNumber?: number;
};
export type DbContact = {
  address?: string;
  archived?: boolean;
  contactId?: string;
  govEmail?: string;
  name?: string;
  organizationId?: string;
  personalEmail?: string;
  personalPhone?: string;
  title?: string;
  upn?: string;
  workEmail?: string;
  workPhone?: string;
};
export type DbDeliverableType = {
  abbreviation?: string;
  archived?: boolean;
  deliverableTypeId?: string;
  description?: string;
  formId?: string;
  formVersionNumber?: number;
  isDocument?: boolean;
  title?: string;
};
export type DbLocation = {
  archived?: boolean;
  description?: string;
  locationAddress?: string;
  locationCity?: string;
  locationCode?: string;
  locationId?: string;
  locationState?: string;
  locationType?: string;
  locationZip?: string;
  name?: string;
};
export type DbProject = {
  archived?: boolean;
  description?: string;
  locationId?: string;
  ownerId?: string;
  programId?: string;
  projectCode?: string;
  projectFilePath?: string;
  projectId?: string;
  title?: string;
};
export type HandlersDeliverableResponse = {
  archived?: boolean;
  /** Attachments associated with this deliverable */
  attachments?: HandlersAttachmentResponse[];
  author?: DbContact;
  authorId?: string;
  dateSubmitted?: string;
  deliverableId?: string;
  deliverableTitle?: string;
  deliverableType?: DbDeliverableType;
  deliverableTypeId?: string;
  deliverableVersionNumber?: number;
  description?: string;
  dueDate?: string;
  expectedApprovalEnd?: string;
  expectedApprovalStart?: string;
  formId?: string;
  formVersionNumber?: number;
  lastModified?: string;
  location?: DbLocation;
  locationId?: string;
  programId?: string;
  project?: DbProject;
  /** Parent relationship - a deliverable belongs to either a program or a project (not both) */
  projectId?: string;
  /** Workflow information (from most recent workflow) */
  workflowId?: string;
  workflowState?: string;
};
export type HandlersDeliverableListResponse = {
  deliverables?: HandlersDeliverableResponse[];
  limit?: number;
  page?: number;
  total?: number;
};
export type HandlersDeliverableCreateRequest = {
  /** Contact ID (not User ID) of the deliverable author */
  authorId?: string;
  dateSubmitted?: string;
  deliverableTitle: string;
  deliverableTypeId: string;
  description?: string;
  dueDate?: string;
  expectedApprovalEnd?: string;
  expectedApprovalStart?: string;
  formId?: string;
  formVersionNumber?: number;
  locationId?: string;
  projectId?: string;
  /** Workflow type name. Valid values are listed by GET /api/workflows/types and must be enabled for the program (GET /api/programs/{id}/workflow-types). */
  workflowType: string;
};
export type HandlersAbbreviatedProject = {
  projectId?: string;
  title?: string;
};
export type HandlersDeliverableTypeWithProjectsResponse = {
  abbreviation?: string;
  archived?: boolean;
  deliverableTypeId?: string;
  description?: string;
  formId?: string;
  formVersionNumber?: number;
  isDocument?: boolean;
  projects?: HandlersAbbreviatedProject[];
  title?: string;
};
export type HandlersDeliverableUpdateRequest = {
  /** Contact ID (not User ID) of the deliverable author */
  authorId?: string;
  dateSubmitted?: string;
  deliverableTitle?: string;
  deliverableTypeId?: string;
  description?: string;
  dueDate?: string;
  expectedApprovalEnd?: string;
  expectedApprovalStart?: string;
  formId?: string;
  formVersionNumber?: number;
  locationId?: string;
  /** Workflow type name (can only be changed when deliverable is in Pending state). Valid values are listed by GET /api/workflows/types and must be enabled for the program (GET /api/programs/{id}/workflow-types). */
  workflowType?: string;
};
export type HandlersDeliverableApprovalStatus = {
  completedTasksCount?: number;
  dateSubmitted?: string;
  deliverableId?: string;
  hasWorkflow?: boolean;
  isApproved?: boolean;
  isPending?: boolean;
  isRejected?: boolean;
  totalTasksCount?: number;
  workflowState?: string;
};
export type HandlersWorkflowTaskActionResponse = {
  actionCompleted?: string;
  actionName?: string;
  actionNumber?: number;
  actionOwnerId?: string;
  actionStarted?: string;
  description?: string;
  workflowId?: string;
  workflowTaskNumber?: number;
  workflowTaskState?: string;
};
export type HandlersWorkflowTaskResponse = {
  /** Related data */
  actions?: HandlersWorkflowTaskActionResponse[];
  comment?: string;
  description?: string;
  signatureId?: string;
  taskCompleted?: string;
  taskName?: string;
  taskOwnerId?: string;
  taskStarted?: string;
  workflowId?: string;
  /** WorkflowSyncFailed is true when the task update committed (HTTP 200) but the
    parent workflow state could not be advanced — e.g. the workflow type has no
    engine support in the phase registry. It flags that the parent state did NOT
    move despite the successful task action; omitted on success (#1352). */
  workflowSyncFailed?: boolean;
  workflowSyncMessage?: string;
  workflowTaskNumber?: number;
  workflowTaskState?: string;
};
export type HandlersWorkflowResponse = {
  description?: string;
  name?: string;
  objectId?: string;
  objectType?: string;
  objectVersionNumber?: number;
  rawWorkflowTemplate?: string;
  /** Related data */
  tasks?: HandlersWorkflowTaskResponse[];
  workflowCompleted?: string;
  workflowId?: string;
  workflowStarted?: string;
  workflowState?: string;
  /** WorkflowSyncFailed flags that a committed action (e.g. submit) kept its 200
    but the workflow state could not be advanced for lack of engine support;
    omitted on success (#1352). */
  workflowSyncFailed?: boolean;
  workflowSyncMessage?: string;
  workflowType?: string;
};
export type HandlersSendTestEmailResponse = {
  /** Echo back CC recipients */
  cc?: string[];
  /** Description */
  message?: string;
  /** Echo back recipients */
  recipients?: string[];
  /** "success" or "error" */
  status?: string;
};
export type HandlersSendTestEmailRequest = {
  /** Required - email body (plain text) */
  body: string;
  /** Optional - array of CC email addresses */
  cc?: string[];
  /** Required - array of email addresses */
  recipients: string[];
  /** Required - email subject */
  subject: string;
};
export type HandlersDevSignCmsResponse = {
  cms?: string;
  payloadDigest?: string;
  payloadType?: string;
  signerUpn?: string;
};
export type HandlersSignatureError = {
  code?: string;
  error?: string;
  message?: string;
};
export type HandlersDevSignCmsRequest = {
  action: 'approve' | 'reject';
  comments?: string;
  /** Tamper, if true, mints a CMS with a deliberately wrong
    messageDigest so the verifier returns DIGEST_MISMATCH. Use this
    to demo the tamper-rejection path. */
  tamper?: boolean;
  taskNumber: number;
  workflowId: string;
};
export type HandlersCreateDevTokenResponse = {
  /** JWT for direct API testing (Authorization header) */
  aclToken?: string;
  claims?: {
    [key: string]: any;
  };
  expiresAt?: string;
  /** SAML-like JWT for frontend cookie testing */
  loginToken?: string;
};
export type AuthProgramAcl = {
  actions?: string[];
  programs?: string[];
};
export type AuthProjectAcl = {
  actions?: string[];
  projects?: string[];
};
export type HandlersCreateDevTokenRequest = {
  /** Optional - duration string like "4h", defaults to "4h" */
  expiresIn?: string;
  /** Optional - defaults to username */
  fullName?: string;
  /** Optional - matches JWT claim name */
  programAcl?: AuthProgramAcl[];
  /** Optional - matches JWT claim name */
  projectAcl?: AuthProjectAcl[];
  /** Optional - "Tyto" (default), "Government", or "External" */
  userType?: string;
  /** Required - used to get/create database user */
  username?: string;
};
export type HandlersDevTokenExamplesResponse = {
  emptyPermissions?: HandlersCreateDevTokenRequest;
  multiRole?: HandlersCreateDevTokenRequest;
  platformRoles?: {
    [key: string]: HandlersCreateDevTokenRequest;
  };
  programManager?: HandlersCreateDevTokenRequest;
  projectContributor?: HandlersCreateDevTokenRequest;
  projectViewer?: HandlersCreateDevTokenRequest;
  siteAdmin?: HandlersCreateDevTokenRequest;
  workflowAdmin?: HandlersCreateDevTokenRequest;
};
export type HandlersClinBreakdown = {
  budget?: number;
  clinCode?: string;
  clinId?: string;
  clinTitle?: string;
  funded?: number;
  incurred?: number;
  invoiced?: number;
  percentConsumed?: number;
  remaining?: number;
};
export type HandlersProjectBreakdown = {
  clinAllocations?: HandlersClinBreakdown[];
  projectId?: string;
  projectName?: string;
};
export type HandlersBurnRateData = {
  monthly?: number;
  quarterly?: number;
  yearly?: number;
};
export type HandlersFinancialSummary = {
  burnRate?: HandlersBurnRateData;
  totalBudget?: number;
  totalFunded?: number;
  totalIncurred?: number;
  totalInvoiced?: number;
};
export type HandlersFinancialDashboardResponse = {
  clinBreakdown?: HandlersClinBreakdown[];
  period?: {
    end?: string;
    start?: string;
  };
  projectBreakdown?: HandlersProjectBreakdown[];
  summary?: HandlersFinancialSummary;
};
export type HandlersFinancialFidelityTypeResponse = {
  fidelityType?: string;
  fidelityTypeDesc?: string;
};
export type HandlersGfebsReportResponse = {
  contractNumber?: string;
  currentUser?: string;
  fund?: string;
  fundsCenter?: string;
  gfebsReportId?: string;
  lastUpdate?: string;
  programId?: string;
  reportRunTime?: string;
  spiin?: string;
};
export type HandlersGfebsReportListResponse = {
  items?: HandlersGfebsReportResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersGfebsLineItemResponse = {
  clinCode?: string;
  commCumulative?: string;
  disbCumulative?: string;
  expenseCumulative?: string;
  functionalArea?: string;
  obligCumulative?: string;
  openExpenses?: string;
  project?: string;
  pronNumber?: string;
  undisbursedCumulative?: string;
};
export type HandlersInvoiceLineItemResponse = {
  amount?: number;
  description?: string;
  employeeName?: string;
  hours?: number;
  invoiceLineItemId?: string;
  laborCategory?: string;
  laborCategoryDesc?: string;
  rate?: number;
  timeSheetDate?: string;
};
export type HandlersProjectInfo = {
  projectCode?: string;
  projectName?: string;
};
export type HandlersInvoiceResponse = {
  amount?: number;
  fiscalYear?: number;
  invoiceCode?: string;
  invoiceDate?: string;
  invoiceDueDate?: string;
  lineItems?: HandlersInvoiceLineItemResponse[];
  month?: number;
  project?: HandlersProjectInfo;
};
export type HandlersInvoiceListResponse = {
  items?: HandlersInvoiceResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersInvoiceCostByClinItem = {
  billFamilyGroupLabel?: string;
  billFamilyLineType?: string;
  clinCode?: string;
  clinTitle?: string;
  totalBilledAmount?: number;
};
export type HandlersInvoiceCostsByClinResponse = {
  invoiceCode?: string;
  items?: HandlersInvoiceCostByClinItem[];
  total?: number;
};
export type HandlersInvoiceCostByProjectItem = {
  billFamilyGroupLabel?: string;
  billFamilyLineType?: string;
  clinCode?: string;
  clinTitle?: string;
  lineItemProjectCode?: string;
  totalBilledAmount?: number;
};
export type HandlersInvoiceCostsByProjectResponse = {
  invoiceCode?: string;
  items?: HandlersInvoiceCostByProjectItem[];
  total?: number;
};
export type HandlersItdReportResponse = {
  effectiveDate?: string;
  programId?: string;
  summaryId?: string;
};
export type HandlersItdReportListResponse = {
  items?: HandlersItdReportResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersItdReportDetailResponse = {
  clinCode?: string;
  exhausted?: string;
  funded?: string;
  fundingYear?: string;
  percentExhausted?: string;
  percentExhaustedAfter90?: string;
  project?: string;
  projectCode?: string;
  projectLabel?: string;
  remaining?: string;
  remainingAfter90?: string;
  remainingWithin90?: string;
};
export type HandlersTabularActionItemResponse = {
  /** "$1,061,823.90" */
  actionAmount?: string;
  /** "9/20/2024" */
  actionDate?: string;
  actionItemDesc?: string;
  actionItemNumber?: number;
  clinCode?: string;
  deltekFromProjectCode?: string;
  deltekToProjectCode?: string;
  /** "9/20/2024" */
  effectiveDate?: string;
  planActionId?: string;
};
export type HandlersTabularLineItemResponse = {
  deltekProjectCode?: string;
  fiscalYear?: number;
  lineItemDesc?: string;
  lineItemNumber?: number;
  planApril?: string;
  planAugust?: string;
  planDecember?: string;
  planFebruary?: string;
  /** Quarterly (when fidelityType = "Quarter") */
  planFirstQtr?: string;
  planFourthQtr?: string;
  /** Monthly (when fidelityType = "Month") */
  planJanuary?: string;
  planJuly?: string;
  planJune?: string;
  planLineItemId?: string;
  planMarch?: string;
  planMay?: string;
  planNovember?: string;
  planOctober?: string;
  planSecondQtr?: string;
  planSeptember?: string;
  planThirdQtr?: string;
  /** "$1,250,000.00" */
  planYear?: string;
};
export type HandlersTabularPlanResponse = {
  actionItems?: HandlersTabularActionItemResponse[];
  /** "01/01/2024" */
  effectiveDate?: string;
  fidelityType?: string;
  fidelityTypeDesc?: string;
  /** "$23,498,459.80" */
  fundingTotal?: string;
  /** "05/20/2025" */
  lastEffectiveDate?: string;
  lineItems?: HandlersTabularLineItemResponse[];
  planId?: string;
  planName?: string;
  planType?: string;
  planTypeDesc?: string;
  /** "05/20/2025" */
  updateDate?: string;
  updatedBy?: HandlersContactResponse;
};
export type HandlersTabularPlanListResponse = {
  items?: HandlersTabularPlanResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type DbForm = {
  archived?: boolean;
  formData?: any;
  formId?: string;
  formType?: string;
  versionNumber?: number;
};
export type DbFormType = {
  deprecated?: boolean;
  description?: string;
  formType?: string;
  objectType?: string;
};
export type HandlersServiceStatus = {
  lastCheck?: string;
  status?: string;
};
export type HandlersHealthResponse = {
  services?: {
    [key: string]: HandlersServiceStatus;
  };
  status?: string;
};
export type HandlersFileDeliverable = {
  archived?: boolean;
  /** Blob storage ID from Attachment.URL */
  blobId?: string;
  createdAt?: string;
  dateSubmitted?: string;
  /** Only populated for deliverables */
  deliverableType?: DbDeliverableType;
  /** Only populated for deliverables */
  deliverableTypeId?: string;
  description?: string;
  /** Signed URL for direct download (files only; omitted for folders or when blob storage is unavailable) */
  downloadUrl?: string;
  /** Original UUID from source table (same as ID) */
  entityId?: string;
  /** "program", "project", "deliverable", "attachment" */
  entityType?: string;
  fileSize?: number;
  /** Uses the actual EntityID (UUID) */
  id?: string;
  /** Computed from EntityType */
  isFolder?: boolean;
  label?: string;
  mimeType?: string;
  /** Contact ID for programs/projects */
  ownerContactId?: string;
  /** Parent EntityID (UUID) */
  parentId?: string;
  status?: string;
  updatedAt?: string;
};
export type HandlersLibraryResponse = {
  count?: number;
  files?: HandlersFileDeliverable[];
};
export type HandlersLibraryDirectoryResponse = {
  /** Optional fields */
  description?: string;
  /** DirectoryID is now the primary identifier (UUID) */
  directoryId?: string;
  /** Required fields */
  directoryName?: string;
  locationId?: string;
  parentDirectoryId?: string;
  programId?: string;
  projectId?: string;
};
export type HandlersCreateLibraryDirectoryRequest = {
  /** Optional fields */
  description?: string;
  directoryName: string;
  locationId?: string;
  parentDirectoryId?: string;
  /** Required fields (must match database NOT NULL constraints) */
  programId: string;
  projectId?: string;
};
export type HandlersUpdateLibraryDirectoryRequest = {
  description?: string;
  /** All fields are optional for updates */
  directoryName?: string;
  locationId?: string;
  /** Use empty string "" to move to program level */
  projectId?: string;
};
export type HandlersLibraryTreeNode = {
  /** Archival status */
  archived?: boolean;
  /** Blob storage ID for files */
  blobId?: string;
  children?: HandlersLibraryTreeNode[];
  /** Creation timestamp */
  createdAt?: string;
  dateSubmitted?: string;
  /** Only populated for deliverables */
  deliverableType?: DbDeliverableType;
  /** Only populated for deliverables */
  deliverableTypeId?: string;
  description?: string;
  /** Signed URL for direct download (files only; omitted for folders or when blob storage is unavailable) */
  downloadUrl?: string;
  /** Original UUID from source table (same as ID) */
  entityId?: string;
  /** "program", "project", "deliverable", "attachment" */
  entityType?: string;
  /** File size in bytes */
  fileSize?: number;
  /** Changed to string (UUID) */
  id?: string;
  /** Computed from EntityType */
  isFolder?: boolean;
  label?: string;
  /** MIME type for files */
  mimeType?: string;
  /** Contact ID for programs/projects */
  ownerContactId?: string;
  /** Parent relationship (UUID) */
  parentId?: string;
  status?: string;
  /** Last modified timestamp */
  updatedAt?: string;
};
export type HandlersLocationTypeResponse = {
  archived?: boolean;
  description?: string;
  locationType?: string;
};
export type UtilErr409 = {
  code?: number;
  details?: string;
  error?: string;
  errorCode?: string;
};
export type HandlersLocationTypeRequest = {
  description?: string;
  locationType: string;
};
export type HandlersLocationTypeUpdateRequest = {
  description?: string;
};
export type HandlersLocationCatalogResponse = {
  archived?: boolean;
  description?: string;
  locationAddress?: string;
  locationCity?: string;
  locationCode?: string;
  locationId?: string;
  locationState?: string;
  locationType?: string;
  locationZip?: string;
  name?: string;
};
export type HandlersCreateLocationRequest = {
  description?: string;
  locationAddress?: string;
  locationCity?: string;
  locationCode?: string;
  locationState?: string;
  locationType: string;
  locationZip?: string;
  name: string;
};
export type HandlersUpdateLocationRequest = {
  description?: string;
  locationAddress?: string;
  locationCity?: string;
  locationCode?: string;
  locationState?: string;
  locationType?: string;
  locationZip?: string;
  name?: string;
};
export type MpxjTaskData = {
  cost?: number;
  critical?: boolean;
  durationDays?: number;
  finishDate?: string;
  id?: number;
  /** API: always used in responses */
  lineItemLevel?: number;
  milestone?: boolean;
  name?: string;
  notes?: string;
  /** Internal: receives from MPXJ, cleared after transform */
  outlineLevel?: number;
  parentTaskId?: number;
  percentComplete?: number;
  priority?: string;
  resourceNames?: string;
  startDate?: string;
  status?: string;
};
export type MpxjProjectData = {
  comments?: string;
  company?: string;
  filename?: string;
  finishDate?: string;
  milestoneCount?: number;
  milestones?: MpxjTaskData[];
  name?: string;
  projectManager?: string;
  startDate?: string;
  taskCount?: number;
  tasks?: MpxjTaskData[];
};
export type ServicesMasterSchedule = {
  errorMessage?: string;
  filename?: string;
  id?: string;
  name?: string;
  projectData?: MpxjProjectData;
  /** Project this schedule belongs to */
  projectId?: string;
  status?: string;
  uploadedAt?: string;
};
export type HandlersUpdateMasterScheduleRequest = {
  name?: string;
  projectId?: string;
};
export type ServicesMasterScheduleStatusResponse = {
  daysRemaining?: number;
  isOnSchedule?: boolean;
  milestonesComplete?: number;
  milestonesTotal?: number;
  overallProgress?: number;
  tasksCompleted?: number;
  tasksInProgress?: number;
  tasksNotStarted?: number;
  tasksTotal?: number;
};
export type HandlersObjectTypeResponse = {
  archived?: boolean;
  objectTable?: string;
  objectType?: string;
};
export type HandlersObjectTypeRequest = {
  objectTable: string;
  objectType: string;
};
export type HandlersObjectTypeUpdateRequest = {
  objectTable?: string;
};
export type HandlersOrganizationResponse = {
  archived?: boolean;
  description?: string;
  name?: string;
  organizationId?: string;
  organizationType?: string;
};
export type HandlersOrganizationListResponse = {
  items?: HandlersOrganizationResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersCreateOrganizationRequest = {
  description?: string;
  name: string;
  organizationType: string;
};
export type HandlersOrganizationTypeResponse = {
  archived?: boolean;
  description?: string;
  organizationType?: string;
};
export type HandlersSimpleCatalogResponse = {
  archived?: boolean;
  description?: string;
  /** ID is set only for resources with a UUID PK (ObjectType). Others leave it empty. */
  id?: string;
  name?: string;
};
export type HandlersSimpleCatalogRequest = {
  description?: string;
  name: string;
};
export type HandlersSimpleCatalogUpdateRequest = {
  description?: string;
  /** For ObjectType, the mutable "name" column is allowed (UUID is the PK, not the string). */
  name?: string;
};
export type HandlersUpdateOrganizationRequest = {
  description?: string;
  name?: string;
  organizationType?: string;
};
export type HandlersFormVisibilityEntry = {
  description?: string;
  enabled?: boolean;
  formType?: string;
  objectType?: string;
};
export type HandlersFormVisibilityResponse = {
  forms?: HandlersFormVisibilityEntry[];
};
export type HandlersUpdateFormVisibilityEntry = {
  enabled?: boolean;
  formType: string;
};
export type HandlersUpdateFormVisibilityRequest = {
  forms: HandlersUpdateFormVisibilityEntry[];
};
export type HandlersPnrResponse = {
  actionRequiredByGovernment?: boolean;
  actionRequiredByGovernmentDesc?: string;
  attachments?: HandlersAttachmentResponse[];
  correctiveActionCompleteDesc?: string;
  correctiveActionDesc?: string;
  currentVersion?: boolean;
  dateCreated?: string;
  dateSubmitted?: string;
  deliveryScheduleRecoverable?: boolean;
  deliveryScheduleRecoverableDesc?: string;
  description?: string;
  dueDate?: string;
  formId?: string;
  formVersionNumber?: number;
  increasedCostAnticipated?: boolean;
  increasedCostAnticipatedDesc?: string;
  lastModified?: string;
  natureSourceProblemDesc?: string;
  pnrId?: string;
  pnrNumber?: string;
  problemImpactDelivery?: boolean;
  problemImpactDeliveryDesc?: string;
  projectId?: string;
  rawFormData?: string;
  versionNumber?: number;
  workflowId?: string;
  workflowState?: string;
};
export type HandlersPnrListResponse = {
  page?: number;
  pageSize?: number;
  pnrs?: HandlersPnrResponse[];
  totalCount?: number;
};
export type HandlersCreatePnrRequest = {
  actionRequiredByGovernment?: boolean;
  actionRequiredByGovernmentDesc?: string;
  correctiveActionCompleteDesc?: string;
  correctiveActionDesc?: string;
  deliveryScheduleRecoverable?: boolean;
  deliveryScheduleRecoverableDesc?: string;
  description?: string;
  dueDate?: string;
  formId: string;
  formVersionNumber: number;
  increasedCostAnticipated?: boolean;
  increasedCostAnticipatedDesc?: string;
  natureSourceProblemDesc?: string;
  pnrNumber: string;
  problemImpactDelivery?: boolean;
  problemImpactDeliveryDesc?: string;
  projectId?: string;
  rawFormData?: string;
};
export type HandlersUpdatePnrRequest = {
  actionRequiredByGovernment?: boolean;
  actionRequiredByGovernmentDesc?: string;
  correctiveActionCompleteDesc?: string;
  correctiveActionDesc?: string;
  deliveryScheduleRecoverable?: boolean;
  deliveryScheduleRecoverableDesc?: string;
  description?: string;
  dueDate?: string;
  increasedCostAnticipated?: boolean;
  increasedCostAnticipatedDesc?: string;
  natureSourceProblemDesc?: string;
  pnrNumber?: string;
  problemImpactDelivery?: boolean;
  problemImpactDeliveryDesc?: string;
  rawFormData?: string;
};
export type HandlersReportEmbedInfo = {
  embedUrl?: string;
  reportId?: string;
};
export type HandlersGetEmbedTokenResponse = {
  datasetIds?: string[];
  expiration?: string;
  reports?: HandlersReportEmbedInfo[];
  token?: string;
  tokenId?: string;
};
export type HandlersGetEmbedTokenRequest = {
  datasetIds: string[];
  reportIds: string[];
};
export type HandlersProgramResponse = {
  contractCode?: string;
  description?: string;
  ownerContactId?: string;
  programId?: string;
  title?: string;
};
export type HandlersCreateProgramRequest = {
  contractCode: string;
  description?: string;
  ownerContactId: string;
  title: string;
};
export type HandlersProgramDeliverableTypeResponse = {
  archived?: boolean;
  deliverableNumber?: number;
  deliverableType?: DbDeliverableType;
  deliverableTypeId?: string;
  documentRef?: string;
  govRights?: string;
  parentObjectType?: string;
  programId?: string;
  requiresWorkflow?: boolean;
  source?: string;
  whenDue?: string;
};
export type HandlersDeltekProjectResponse = {
  parentProjectCode?: string;
  projectCode?: string;
  projectLongName?: string;
  projectName?: string;
};
export type HandlersDeltekContractResponse = {
  contractAdminId?: string;
  contractCode?: string;
  contractEndDate?: string;
  contractName?: string;
  contractStartDate?: string;
  contractType?: string;
  customerCode?: string;
  projects?: HandlersDeltekProjectResponse[];
  taskOrderNumber?: string;
};
export type HandlersUpdateProgramRequest = {
  contractCode?: string;
  description?: string;
  ownerContactId?: string;
  title?: string;
};
export type HandlersCreateProgramDeliverableTypeRequest = {
  deliverableNumber?: number;
  deliverableTypeId: string;
  documentRef?: string;
  govRights?: string;
  parentObjectType?: string;
  requiresWorkflow?: boolean;
  source?: string;
  whenDue?: string;
};
export type HandlersProgramDeliverableTypesRequest = {
  deliverableTypes: HandlersCreateProgramDeliverableTypeRequest[];
};
export type HandlersUpdateProgramDeliverableTypeRequest = {
  deliverableNumber: number;
  deliverableTypeId?: string;
  documentRef?: string;
  govRights?: string;
  parentObjectType?: string;
  requiresWorkflow?: boolean;
  source?: string;
  whenDue?: string;
};
export type HandlersProgramLocationResponse = {
  locationArchived?: boolean;
  locationCity?: string;
  locationCode?: string;
  locationId?: string;
  locationState?: string;
  locationType?: string;
  name?: string;
  programId?: string;
};
export type HandlersAddProgramLocationRequest = {
  locationId: string;
};
export type HandlersProgramObjectTypeResponse = {
  objectTable?: string;
  objectType?: string;
  objectTypeArchived?: boolean;
  programId?: string;
};
export type HandlersAddProgramObjectTypeRequest = {
  objectType: string;
};
export type HandlersProgramRoleAssignmentResponse = {
  programId?: string;
  projectId?: string;
  roleName?: string;
  userEmail?: string;
  userId?: string;
};
export type HandlersProgramRoleResponse = {
  archived?: boolean;
  programId?: string;
  roleName?: string;
  roleType?: string;
  scope?: string;
  source?: string;
};
export type HandlersCreateRoleRequest = {
  description?: string;
  roleName: string;
  roleType: string;
  scope?: string;
  source: string;
};
export type HandlersUpdateRoleRequest = {
  roleType?: string;
  scope?: string;
  source?: string;
};
export type HandlersRoleActionResponse = {
  actionName?: string;
  programId?: string;
  roleName?: string;
};
export type HandlersAddRoleActionRequest = {
  actionName: string;
};
export type HandlersSiteSettingsResponse = {
  adminEmail?: string;
  domains?: string[];
};
export type HandlersUpdateAdminEmailRequest = {
  adminEmail: string;
};
export type HandlersAddDomainRequest = {
  domain: string;
};
export type HandlersProgramUserRoleResponse = {
  programId?: string;
  roleName?: string;
  userEmail?: string;
  userId?: string;
};
export type HandlersWorkflowStateLabelResponse = {
  workflowState?: string;
  workflowStateLabel?: string;
};
export type HandlersWorkflowTaskStateLabelResponse = {
  workflowTaskState?: string;
  workflowTaskStateLabel?: string;
};
export type HandlersWorkflowLabelsResponse = {
  workflowStateLabels?: HandlersWorkflowStateLabelResponse[];
  workflowTaskStateLabels?: HandlersWorkflowTaskStateLabelResponse[];
};
export type HandlersWorkflowStateLabelUpdate = {
  workflowState: string;
  workflowStateLabel: string;
};
export type HandlersWorkflowTaskStateLabelUpdate = {
  workflowTaskState: string;
  workflowTaskStateLabel: string;
};
export type HandlersUpdateWorkflowLabelsRequest = {
  workflowStateLabels?: HandlersWorkflowStateLabelUpdate[];
  workflowTaskStateLabels?: HandlersWorkflowTaskStateLabelUpdate[];
};
export type HandlersProgramWorkflowTypeRequest = {
  /** Workflow type name. Valid values are listed by GET /api/workflows/types. */
  workflowType: string;
};
export type HandlersProjectResponse = {
  archived?: boolean;
  /** Enriched fields joined from ProjectSummary, Contact, Location.
    dateCreated/lastModified are null for legacy projects with no current ProjectSummary row. */
  dateCreated?: string;
  description?: string;
  lastModified?: string;
  locationId?: string;
  locationName?: string;
  ownerId?: string;
  ownerName?: string;
  programId?: string;
  projectCode?: string;
  projectFilePath?: string;
  projectId?: string;
  title?: string;
  workflowState?: string;
};
export type HandlersProjectListResponse = {
  page?: number;
  pageSize?: number;
  projects?: HandlersProjectResponse[];
  total?: number;
};
export type UtilErr422 = {
  code?: number;
  details?: string;
  error?: string;
  errorCode?: string;
};
export type UtilErr504 = {
  code?: number;
  details?: string;
  error?: string;
  errorCode?: string;
};
export type HandlersCreateProjectRequest = {
  description?: string;
  locationId?: string;
  ownerId: string;
  programId: string;
  projectCode: string;
  projectFilePath?: string;
  title: string;
};
export type HandlersProjectDeliverableTypeResponse = {
  deliverableNumber?: number;
  deliverableType?: DbDeliverableType;
  deliverableTypeId?: string;
  projectId?: string;
};
export type HandlersUpdateProjectRequest = {
  archived?: boolean;
  description?: string;
  locationId?: string;
  ownerId?: string;
  programId?: string;
  projectCode?: string;
  projectFilePath?: string;
  title?: string;
};
export type HandlersProjectClinResponse = {
  clinCode?: string;
  clinId?: string;
  clinTitle?: string;
  programId?: string;
  projectId?: string;
};
export type HandlersAddProjectClinRequest = {
  clinId: string;
};
export type HandlersProjectDeliverableTypesRequest = {
  deliverableTypeIds: string[];
};
export type HandlersProjectSummaryResponse = {
  archived?: boolean;
  attachments?: HandlersAttachmentResponse[];
  currentVersion?: boolean;
  dateCreated?: string;
  /** New fields for enhanced response */
  description?: string;
  formId?: string;
  formVersionNumber?: number;
  lastModified?: string;
  locationId?: string;
  locationName?: string;
  ownerId?: string;
  ownerName?: string;
  projectCode?: string;
  projectId?: string;
  psId?: string;
  rawFormData?: string;
  title?: string;
  versionNumber?: number;
};
export type HandlersProjectSummaryRequest = {
  formId: string;
  formVersionNumber: number;
  rawFormData?: string;
};
export type HandlersRipResponse = {
  associatedLineOfAccounting?: string;
  attachments?: HandlersAttachmentResponse[];
  awardPIID?: string;
  clientName?: string;
  clinx002Value?: number;
  contractorName?: string;
  cumulativeAmountBilled?: number;
  currentCLINX002Balance?: number;
  currentVersion?: boolean;
  dateCreated?: string;
  dateSubmitted?: string;
  description?: string;
  dueDate?: string;
  estimates?: HandlersEstimateResponse[];
  formId?: string;
  formVersionNumber?: number;
  fromContactId?: string;
  lastModified?: string;
  newCLINX002Balance?: number;
  projectId?: string;
  projectName?: string;
  purposeJustification?: string;
  rawFormData?: string;
  ripDate?: string;
  ripId?: string;
  ripNumber?: string;
  subject?: string;
  throughContactId?: string;
  toContactId?: string;
  versionNumber?: number;
  workflowId?: string;
  workflowState?: string;
};
export type HandlersRipListResponse = {
  page?: number;
  pageSize?: number;
  rips?: HandlersRipResponse[];
  totalCount?: number;
  totalPages?: number;
};
export type HandlersCreateRipRequest = {
  associatedLineOfAccounting?: string;
  awardPIID?: string;
  clientName?: string;
  clinx002Value?: number;
  contractorName?: string;
  cumulativeAmountBilled?: number;
  currentCLINX002Balance?: number;
  description?: string;
  dueDate?: string;
  estimates?: HandlersCreateEstimateRequest[];
  formId: string;
  formVersionNumber: number;
  fromContactId?: string;
  newCLINX002Balance?: number;
  projectId?: string;
  projectName?: string;
  purposeJustification?: string;
  rawFormData?: string;
  ripDate?: string;
  ripNumber: string;
  subject?: string;
  throughContactId?: string;
  toContactId?: string;
};
export type HandlersUpdateRipRequest = {
  associatedLineOfAccounting?: string;
  awardPIID?: string;
  clientName?: string;
  clinx002Value?: number;
  contractorName?: string;
  cumulativeAmountBilled?: number;
  currentCLINX002Balance?: number;
  description?: string;
  dueDate?: string;
  estimates?: HandlersCreateEstimateRequest[];
  fromContactId?: string;
  newCLINX002Balance?: number;
  projectName?: string;
  purposeJustification?: string;
  rawFormData?: string;
  ripDate?: string;
  ripNumber?: string;
  subject?: string;
  throughContactId?: string;
  toContactId?: string;
};
export type HandlersRipVersionResponse = {
  currentVersion?: boolean;
  dateCreated?: string;
  lastModified?: string;
  ripId?: string;
  versionNumber?: number;
};
export type DbIroRiskCategory = {
  archived?: boolean;
  category?: string;
  categoryCode?: number;
  description?: string;
};
export type DbIroImpact = {
  archived?: boolean;
  impactCode?: number;
  impactDesc?: string;
  impactScore?: number;
};
export type DbIroProbability = {
  archived?: boolean;
  probabilityCode?: number;
  probabilityDesc?: string;
  probabilityLevel?: string;
  probabilityScore?: number;
};
export type HandlersIroRiskResponse = {
  assessmentDate?: string;
  assessmentFactor?: number;
  category?: number;
  categoryRisk?: DbIroRiskCategory;
  closureCode?: number;
  contingencyPlan?: string;
  contractCode?: string;
  description?: string;
  escalate?: boolean;
  finalResolution?: string;
  impact?: DbIroImpact;
  impactCode?: number;
  isClosure?: boolean;
  mitigationPlan?: string;
  probability?: DbIroProbability;
  probabilityCode?: number;
  progressNotes?: string;
  recordUpdate?: string;
  riskDateClosed?: string;
  riskDateOpened?: string;
  riskEarliestImpactDate?: string;
  riskId?: number;
  riskImpactSummary?: string;
  riskOwnerId?: string;
  riskTreatment?: string;
  submitterId?: string;
  title?: string;
  transfereeId?: string;
};
export type HandlersIroRiskListResponse = {
  limit?: number;
  page?: number;
  risks?: HandlersIroRiskResponse[];
  total?: number;
};
export type HandlersIroRiskCreateRequest = {
  assessmentFactor: number;
  category: number;
  closureCode?: number;
  contingencyPlan?: string;
  contractCode: string;
  description?: string;
  escalate?: boolean;
  finalResolution?: string;
  impactCode: number;
  isClosure?: boolean;
  mitigationPlan?: string;
  probabilityCode: number;
  progressNotes?: string;
  riskDateClosed?: string;
  riskEarliestImpactDate: string;
  riskImpactSummary: string;
  riskOwnerId: string;
  riskTreatment?: string;
  submitterId: string;
  title: string;
  transfereeId?: string;
};
export type DbIroCategoryImpact = {
  archived?: boolean;
  /** Relationships */
  category?: DbIroRiskCategory;
  categoryCode?: number;
  categoryImpactDesc?: string;
  impact?: DbIroImpact;
  impactCode?: number;
};
export type DbIroClosure = {
  archived?: boolean;
  closureCode?: number;
  closureReason?: string;
};
export type HandlersIroRiskUpdateRequest = {
  assessmentFactor?: number;
  category?: number;
  closureCode?: number;
  contingencyPlan?: string;
  description?: string;
  escalate?: boolean;
  finalResolution?: string;
  impactCode?: number;
  isClosure?: boolean;
  mitigationPlan?: string;
  probabilityCode?: number;
  progressNotes?: string;
  riskDateClosed?: string;
  riskEarliestImpactDate?: string;
  riskImpactSummary?: string;
  riskOwnerId?: string;
  riskTreatment?: string;
  title?: string;
  transfereeId?: string;
};
export type HandlersTamsInventoryResponse = {
  acquisition?: string;
  comments?: string;
  description?: string;
  disposition?: string;
  dispositionDate?: string;
  enteredBy?: string;
  gfeId?: number;
  gpTagNum?: string;
  inventoryDate?: string;
  location?: string;
  model?: string;
  partNumber?: string;
  projectCode?: string;
  serialNumber?: string;
};
export type HandlersTamsInventoryListResponse = {
  items?: HandlersTamsInventoryResponse[];
  page?: number;
  pageSize?: number;
  totalCount?: number;
};
export type HandlersCreateTamsInventoryRequest = {
  acquisition?: string;
  comments?: string;
  description: string;
  disposition?: string;
  dispositionDate?: string;
  enteredBy?: string;
  gfeId: number;
  gpTagNum: string;
  inventoryDate?: string;
  location?: string;
  model: string;
  partNumber: string;
  projectCode: string;
  serialNumber: string;
};
export type HandlersUpdateTamsInventoryRequest = {
  acquisition?: string;
  comments?: string;
  description?: string;
  disposition?: string;
  dispositionDate?: string;
  enteredBy?: string;
  gpTagNum?: string;
  inventoryDate?: string;
  location?: string;
  model?: string;
  partNumber?: string;
  projectCode?: string;
  serialNumber?: string;
};
export type HandlersItineraryLegResponse = {
  arrival?: string;
  departure?: string;
  destinationLocationId?: string;
  itineraryLeg?: number;
  originLocationId?: string;
};
export type HandlersItineraryResponse = {
  airlineId?: string;
  estimates?: HandlersEstimateResponse[];
  hotelId?: string;
  itineraryId?: string;
  legs?: HandlersItineraryLegResponse[];
  name?: string;
  rentalId?: string;
  travelerId?: string;
};
export type HandlersTarResponse = {
  /** Associated data */
  attachments?: HandlersAttachmentResponse[];
  currentVersion?: boolean;
  dailyLodgingRate?: number;
  dailyMieRate?: number;
  dateCreated?: string;
  dateSubmitted?: string;
  description?: string;
  dueDate?: string;
  /** FK-resolved display fields (populated by resolveTARDisplayFields, not from model) */
  dutyLocation?: string;
  emergencyContactEmail?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyPocId?: string;
  estimates?: HandlersEstimateResponse[];
  eventEndDate?: string;
  eventLocation?: string;
  eventLocationId?: string;
  eventStartDate?: string;
  formId?: string;
  formVersionNumber?: number;
  itineraries?: HandlersItineraryResponse[];
  lastModified?: string;
  lodgingZipCode?: string;
  popEnd?: string;
  popStart?: string;
  projectId?: string;
  rawFormData?: string;
  tarContactId?: string;
  tarContractCode?: string;
  tarDate?: string;
  tarId?: string;
  tarNumber?: string;
  taskOrderNumber?: string;
  tdyPocEmail?: string;
  tdyPocId?: string;
  tdyPocName?: string;
  tdyPocPhone?: string;
  tripLocationId?: string;
  tripRemarks?: string;
  tripTitle?: string;
  versionNumber?: number;
  workflowId?: string;
  workflowState?: string;
};
export type HandlersTarListResponse = {
  page?: number;
  pageSize?: number;
  tars?: HandlersTarResponse[];
  totalCount?: number;
  totalPages?: number;
};
export type HandlersCreateItineraryLegRequest = {
  arrival: string;
  departure: string;
  destinationLocationId: string;
  originLocationId: string;
};
export type HandlersCreateItineraryRequest = {
  airlineId?: string;
  hotelId?: string;
  legs?: HandlersCreateItineraryLegRequest[];
  name: string;
  rentalId?: string;
  travelerId?: string;
};
export type HandlersCreateTarRequest = {
  dailyLodgingRate?: number;
  dailyMieRate?: number;
  description?: string;
  dueDate?: string;
  emergencyPocId?: string;
  estimates?: HandlersCreateEstimateRequest[];
  eventEndDate?: string;
  eventLocationId?: string;
  eventStartDate?: string;
  formId: string;
  formVersionNumber: number;
  itineraries?: HandlersCreateItineraryRequest[];
  projectId?: string;
  rawFormData?: string;
  tarContactId?: string;
  tarContractCode?: string;
  tarDate?: string;
  tarNumber?: string;
  tdyPocId?: string;
  tripLocationId?: string;
  tripRemarks?: string;
  tripTitle?: string;
  /** Workflow type name. Valid values are listed by GET /api/workflows/types and must be enabled for the program (GET /api/programs/{id}/workflow-types). */
  workflowType: string;
};
export type HandlersUpdateTarRequest = {
  dailyLodgingRate?: number;
  dailyMieRate?: number;
  description?: string;
  dueDate?: string;
  emergencyPocId?: string;
  estimates?: HandlersCreateEstimateRequest[];
  eventEndDate?: string;
  eventLocationId?: string;
  eventStartDate?: string;
  itineraries?: HandlersCreateItineraryRequest[];
  rawFormData?: string;
  tarContactId?: string;
  tarContractCode?: string;
  tarDate?: string;
  tarNumber?: string;
  tdyPocId?: string;
  tripLocationId?: string;
  tripRemarks?: string;
  tripTitle?: string;
};
export type HandlersUpdateItineraryRequest = {
  airlineId?: string;
  hotelId?: string;
  legs?: HandlersCreateItineraryLegRequest[];
  name?: string;
  rentalId?: string;
  travelerId?: string;
};
export type HandlersTarVersionResponse = {
  currentVersion?: boolean;
  dateCreated?: string;
  lastModified?: string;
  tarId?: string;
  versionNumber?: number;
};
export type HandlersTrResponse = {
  attachments?: HandlersAttachmentResponse[];
  awardPIID?: string;
  commentsConclusions?: string;
  currentVersion?: boolean;
  dateCreated?: string;
  dateSubmitted?: string;
  description?: string;
  dueDate?: string;
  formId?: string;
  formVersionNumber?: number;
  fromDate?: string;
  fromLocationOfTravel?: string;
  knowledgeGained?: string;
  lastModified?: string;
  pocTravelerId?: string;
  projectId?: string;
  projectName?: string;
  purposeJustification?: string;
  rawFormData?: string;
  tarId?: string;
  tarVersionNumber?: number;
  toDate?: string;
  toLocationOfTravel?: string;
  totalCostOfTrip?: number;
  trDate?: string;
  trId?: string;
  trNumber?: string;
  travelerId?: string;
  versionNumber?: number;
  workflowId?: string;
  workflowState?: string;
};
export type HandlersTrListResponse = {
  items?: HandlersTrResponse[];
  page?: number;
  pageSize?: number;
  total?: number;
};
export type HandlersCreateTrRequest = {
  awardPIID?: string;
  commentsConclusions?: string;
  description?: string;
  dueDate?: string;
  formId: string;
  formVersionNumber: number;
  fromDate?: string;
  fromLocationOfTravel?: string;
  knowledgeGained?: string;
  pocTravelerId?: string;
  projectId?: string;
  projectName?: string;
  purposeJustification?: string;
  rawFormData?: string;
  tarId: string;
  tarVersionNumber: number;
  toDate?: string;
  toLocationOfTravel?: string;
  totalCostOfTrip?: number;
  trDate?: string;
  trNumber: string;
  travelerId?: string;
};
export type HandlersUpdateTrRequest = {
  awardPIID?: string;
  commentsConclusions?: string;
  description?: string;
  dueDate?: string;
  fromDate?: string;
  fromLocationOfTravel?: string;
  knowledgeGained?: string;
  pocTravelerId?: string;
  projectName?: string;
  purposeJustification?: string;
  rawFormData?: string;
  toDate?: string;
  toLocationOfTravel?: string;
  totalCostOfTrip?: number;
  trDate?: string;
  trNumber?: string;
  travelerId?: string;
};
export type HandlersTrVersionResponse = {
  currentVersion?: boolean;
  dateCreated?: string;
  lastModified?: string;
  trId?: string;
  trNumber?: string;
  versionNumber?: number;
};
export type HandlersActionResponse = {
  action?: string;
  description?: string;
  scope?: string;
  type?: string;
};
export type HandlersRoleResponse = {
  /** Actions available to this role */
  actions?: HandlersActionResponse[];
  programId?: string;
  programName?: string;
  projectId?: string;
  projectName?: string;
  roleName?: string;
  /** Role type from Role table */
  roleType?: string;
  /** "program" or "project" - assignment level */
  scope?: string;
  /** Source from Role table */
  source?: string;
};
export type HandlersUserDetailResponse = {
  contact?: HandlersContactResponse;
  id?: string;
  /** Enhanced role information */
  roles?: HandlersRoleResponse[];
  userEmail?: string;
  userName?: string;
  userType?: string;
};
export type HandlersContactInfo = {
  address?: string;
  govEmail?: string;
  name: string;
  organizationId?: string;
  personalPhone?: string;
  workEmail?: string;
  workPhone?: string;
};
export type HandlersRoleAssignment = {
  programId: string;
  /** Optional: if provided, creates project-level role */
  projectId?: string;
  roleName: string;
};
export type HandlersCreateUserRequest = {
  contact: HandlersContactInfo;
  roles?: HandlersRoleAssignment[];
  userEmail: string;
  userType: string;
};
export type HandlersNotificationPreferencesResponse = {
  preferences?: HandlersNotificationPreferenceResponse[];
};
export type HandlersUpdateNotificationPreferencesRequest = {
  preferences?: HandlersNotificationPreferenceInput[];
};
export type DbAction = {
  action?: string;
  description?: string;
  scope?: string;
  type?: string;
};
export type DbRoleAction = {
  /** Relationships */
  action?: DbAction;
  actionName?: string;
  programId?: string;
  roleName?: string;
};
export type DbRoleWithActions = {
  actions?: DbAction[];
  archived?: boolean;
  programId?: string;
  roleActions?: DbRoleAction[];
  roleName?: string;
  roleType?: string;
  scope?: string;
  source?: string;
};
export type DbUserType = {
  archived?: boolean;
  description?: string;
  userType?: string;
};
export type HandlersUpdateUserRequest = {
  contact?: HandlersContactInfo;
  roles?: HandlersRoleAssignment[];
  userEmail?: string;
  userType?: string;
};
export type HandlersUserTaskResponse = {
  description?: string;
  objectId?: string;
  objectType?: string;
  taskCompleted?: string;
  taskName?: string;
  taskStarted?: string;
  workflowCompleted?: string;
  workflowId?: string;
  workflowName?: string;
  workflowStarted?: string;
  workflowState?: string;
  workflowTaskNumber?: number;
  workflowTaskState?: string;
};
export type HandlersUserWorkflowResponse = {
  description?: string;
  name?: string;
  objectId?: string;
  objectType?: string;
  /** Number of tasks user completed */
  userCompletedTasksCount?: number;
  /** Number of tasks assigned to user */
  userTasksCount?: number;
  workflowCompleted?: string;
  workflowId?: string;
  workflowStarted?: string;
  workflowState?: string;
  workflowType?: string;
};
export type HandlersUserRoleProjectResponse = {
  programId?: string;
  projectId?: string;
  roleName?: string;
  userId?: string;
};
export type HandlersAddUserRoleProjectRequest = {
  programId: string;
  projectId: string;
  roleName: string;
};
export type HandlersUserRoleResponse = {
  programId?: string;
  roleName?: string;
  userId?: string;
};
export type HandlersAddUserRoleRequest = {
  programId: string;
  roleName: string;
};
export type HandlersVersionResponse = {
  dataModelVersion?: string;
  mpxjVersion?: string;
  servicesApiVersion?: string;
};
export type HandlersWorkflowListResponse = {
  limit?: number;
  page?: number;
  total?: number;
  workflows?: HandlersWorkflowResponse[];
};
export type HandlersWorkflowCreateRequest = {
  description?: string;
  name: string;
  objectId: string;
  objectType: string;
  objectVersionNumber?: number;
  rawWorkflowTemplate?: string;
  /** Workflow type name. Valid values are listed by GET /api/workflows/types and must be enabled for the program (GET /api/programs/{id}/workflow-types). */
  workflowType: string;
};
export type DbWorkflowState = {
  archived?: boolean;
  description?: string;
  workflowState?: string;
};
export type HandlersWorkflowCatalogResponse = {
  archived?: boolean;
  description?: string;
  name?: string;
};
export type HandlersWorkflowCatalogRequest = {
  description?: string;
  name: string;
};
export type HandlersWorkflowCatalogUpdateRequest = {
  description?: string;
};
export type DbWorkflowTaskState = {
  archived?: boolean;
  description?: string;
  workflowTaskState?: string;
};
export type CmsCertType = 'CAC' | 'Windows';
export type SigningTypedRequirement = {
  allowedCertTypes?: CmsCertType[];
  applies?: string;
  required?: boolean;
  taskNumber?: number;
};
export type HandlersWorkflowTypeWithSigning = {
  archived?: boolean;
  description?: string;
  signingRequirements?: SigningTypedRequirement[];
  workflowType?: string;
};
export type HandlersWorkflowUpdateRequest = {
  description?: string;
  name?: string;
  rawWorkflowTemplate?: string;
  workflowState?: string;
};
export type HandlersWorkflowTaskCreateRequest = {
  comment?: string;
  description?: string;
  taskName: string;
  /** Contact ID (not User ID) */
  taskOwnerId?: string;
  workflowTaskState: string;
};
export type HandlersWorkflowTaskUpdateRequest = {
  comment?: string;
  description?: string;
  signatureId?: string;
  taskCompleted?: string;
  taskName?: string;
  /** Contact ID (not User ID) */
  taskOwnerId?: string;
  taskStarted?: string;
  workflowTaskState?: string;
};
export type HandlersWorkflowTaskActionCreateRequest = {
  actionName: string;
  /** Contact ID (not User ID) */
  actionOwnerId?: string;
  description?: string;
  workflowTaskState: string;
};
export type HandlersWorkflowTaskActionUpdateRequest = {
  actionCompleted?: string;
  actionName?: string;
  /** Contact ID (not User ID) */
  actionOwnerId?: string;
  actionStarted?: string;
  description?: string;
  workflowTaskState?: string;
};
export type HandlersTaskApprovalRequest = {
  comments?: string;
  signatureId?: string;
};
export type HandlersTaskAssignmentRequest = {
  /** Contact ID (not User ID) - The contact who will own this task */
  taskOwnerId: string;
};
export type HandlersTaskRejectionRequest = {
  comments: string;
  reason?: string;
  signatureId?: string;
};
export type HandlersSignatureResponse = {
  certType?: string;
  cmsBlobUrl?: string;
  signatureId?: string;
  signedAt?: string;
  signerContactId?: string;
  signerUpn?: string;
  state?: string;
};
export type HandlersSignatureCreateRequest = {
  /** base64-encoded DER */
  cms: string;
  /** hex SHA-256 of canonical payload */
  payloadDigest: string;
  /** free-form descriptor */
  payloadType?: string;
};
export const {
  useGetApiActionsQuery,
  useLazyGetApiActionsQuery,
  usePostApiActionsMutation,
  useGetApiActionsByActionNameQuery,
  useLazyGetApiActionsByActionNameQuery,
  useDeleteApiActionsByActionNameMutation,
  usePatchApiActionsByActionNameMutation,
  useGetApiAdminRoleNotificationPreferencesQuery,
  useLazyGetApiAdminRoleNotificationPreferencesQuery,
  usePutApiAdminRoleNotificationPreferencesMutation,
  useGetApiApprovalQueueQuery,
  useLazyGetApiApprovalQueueQuery,
  useGetApiApprovalQueueStatsQuery,
  useLazyGetApiApprovalQueueStatsQuery,
  useGetApiAttachmentsQuery,
  useLazyGetApiAttachmentsQuery,
  usePostApiAttachmentsMutation,
  usePostApiAttachmentsBatchMutation,
  usePostApiAttachmentsDownloadMutation,
  useGetApiAttachmentsByIdQuery,
  useLazyGetApiAttachmentsByIdQuery,
  useDeleteApiAttachmentsByIdMutation,
  usePatchApiAttachmentsByIdMutation,
  useGetApiAttachmentsByIdDownloadQuery,
  useLazyGetApiAttachmentsByIdDownloadQuery,
  useGetApiAttachmentsByIdVAndVersionQuery,
  useLazyGetApiAttachmentsByIdVAndVersionQuery,
  useDeleteApiAttachmentsByIdVAndVersionMutation,
  useGetApiAttachmentsByIdVAndVersionDownloadQuery,
  useLazyGetApiAttachmentsByIdVAndVersionDownloadQuery,
  useGetApiAttachmentsByIdVersionsQuery,
  useLazyGetApiAttachmentsByIdVersionsQuery,
  usePostApiAttachmentsByIdVersionsMutation,
  useGetApiAuditLogsQuery,
  useLazyGetApiAuditLogsQuery,
  usePostApiAuditLogsMutation,
  useGetApiAuditLogsVerboseLevelsQuery,
  useLazyGetApiAuditLogsVerboseLevelsQuery,
  useGetApiAuditLogsByIdQuery,
  useLazyGetApiAuditLogsByIdQuery,
  useGetApiAuthCallbackQuery,
  useLazyGetApiAuthCallbackQuery,
  useGetApiAuthLoginQuery,
  useLazyGetApiAuthLoginQuery,
  usePostApiBillingImportsMutation,
  useGetApiClinsQuery,
  useLazyGetApiClinsQuery,
  usePostApiClinsMutation,
  useGetApiClinsByIdQuery,
  useLazyGetApiClinsByIdQuery,
  useDeleteApiClinsByIdMutation,
  usePatchApiClinsByIdMutation,
  useGetApiContactsQuery,
  useLazyGetApiContactsQuery,
  usePostApiContactsMutation,
  useGetApiContactsByContactIdQuery,
  useLazyGetApiContactsByContactIdQuery,
  useDeleteApiContactsByContactIdMutation,
  usePatchApiContactsByContactIdMutation,
  useGetApiCtpsQuery,
  useLazyGetApiCtpsQuery,
  usePostApiCtpsMutation,
  useGetApiCtpsByIdQuery,
  useLazyGetApiCtpsByIdQuery,
  useDeleteApiCtpsByIdMutation,
  usePatchApiCtpsByIdMutation,
  usePostApiCtpsByIdEstimateMutation,
  useDeleteApiCtpsByIdEstimateAndEstimateIdMutation,
  usePatchApiCtpsByIdEstimateAndEstimateIdMutation,
  usePostApiCtpsByIdSubmitMutation,
  useGetApiCtpsByIdVAndVersionQuery,
  useLazyGetApiCtpsByIdVAndVersionQuery,
  useGetApiCtpsByIdVersionsQuery,
  useLazyGetApiCtpsByIdVersionsQuery,
  useGetApiDeliverablesQuery,
  useLazyGetApiDeliverablesQuery,
  usePostApiDeliverablesMutation,
  useGetApiDeliverablesTypesQuery,
  useLazyGetApiDeliverablesTypesQuery,
  useGetApiDeliverablesByIdQuery,
  useLazyGetApiDeliverablesByIdQuery,
  useDeleteApiDeliverablesByIdMutation,
  usePatchApiDeliverablesByIdMutation,
  useGetApiDeliverablesByIdApprovalStatusQuery,
  useLazyGetApiDeliverablesByIdApprovalStatusQuery,
  usePostApiDeliverablesByIdCancelMutation,
  usePostApiDeliverablesByIdSubmitMutation,
  useGetApiDeliverablesByIdWorkflowQuery,
  useLazyGetApiDeliverablesByIdWorkflowQuery,
  usePostApiDevSendTestEmailMutation,
  usePostApiDevSignCmsMutation,
  usePostApiDevTokenMutation,
  useGetApiDevTokenAdminQuery,
  useLazyGetApiDevTokenAdminQuery,
  useGetApiDevTokenExamplesQuery,
  useLazyGetApiDevTokenExamplesQuery,
  useGetApiFinancialsDashboardQuery,
  useLazyGetApiFinancialsDashboardQuery,
  useGetApiFinancialsFidelityTypesQuery,
  useLazyGetApiFinancialsFidelityTypesQuery,
  useGetApiFinancialsGfebsReportsQuery,
  useLazyGetApiFinancialsGfebsReportsQuery,
  useGetApiFinancialsGfebsReportsByReportIdLineItemsQuery,
  useLazyGetApiFinancialsGfebsReportsByReportIdLineItemsQuery,
  useGetApiFinancialsInvoicesQuery,
  useLazyGetApiFinancialsInvoicesQuery,
  useGetApiFinancialsInvoicesByCodeQuery,
  useLazyGetApiFinancialsInvoicesByCodeQuery,
  useGetApiFinancialsInvoicesByInvoiceCodeCostsByClinQuery,
  useLazyGetApiFinancialsInvoicesByInvoiceCodeCostsByClinQuery,
  useGetApiFinancialsInvoicesByInvoiceCodeCostsByProjectQuery,
  useLazyGetApiFinancialsInvoicesByInvoiceCodeCostsByProjectQuery,
  useGetApiFinancialsItdReportsQuery,
  useLazyGetApiFinancialsItdReportsQuery,
  useGetApiFinancialsItdReportsBySummaryIdDetailsQuery,
  useLazyGetApiFinancialsItdReportsBySummaryIdDetailsQuery,
  useGetApiFinancialsMetricsBurnRateQuery,
  useLazyGetApiFinancialsMetricsBurnRateQuery,
  useGetApiFinancialsTabularDataQuery,
  useLazyGetApiFinancialsTabularDataQuery,
  useGetApiFormsQuery,
  useLazyGetApiFormsQuery,
  useGetApiFormsTypesQuery,
  useLazyGetApiFormsTypesQuery,
  useGetApiFormsByIdQuery,
  useLazyGetApiFormsByIdQuery,
  useGetApiHealthQuery,
  useLazyGetApiHealthQuery,
  useGetApiLibraryQuery,
  useLazyGetApiLibraryQuery,
  useGetApiLibraryDirectoriesQuery,
  useLazyGetApiLibraryDirectoriesQuery,
  usePostApiLibraryDirectoriesMutation,
  useGetApiLibraryDirectoriesByDirectoryIdQuery,
  useLazyGetApiLibraryDirectoriesByDirectoryIdQuery,
  useDeleteApiLibraryDirectoriesByDirectoryIdMutation,
  usePatchApiLibraryDirectoriesByDirectoryIdMutation,
  usePostApiLibraryLabelByIdAndNewLabelMutation,
  usePostApiLibraryMoveByIdAndNewParentIdMutation,
  useGetApiLibraryTreeQuery,
  useLazyGetApiLibraryTreeQuery,
  useGetApiLibraryByIdQuery,
  useLazyGetApiLibraryByIdQuery,
  useGetApiLibraryByIdDownloadQuery,
  useLazyGetApiLibraryByIdDownloadQuery,
  useGetApiLocationTypesQuery,
  useLazyGetApiLocationTypesQuery,
  usePostApiLocationTypesMutation,
  useGetApiLocationTypesByLocationTypeQuery,
  useLazyGetApiLocationTypesByLocationTypeQuery,
  useDeleteApiLocationTypesByLocationTypeMutation,
  usePatchApiLocationTypesByLocationTypeMutation,
  useGetApiLocationsQuery,
  useLazyGetApiLocationsQuery,
  usePostApiLocationsMutation,
  useGetApiLocationsByIdQuery,
  useLazyGetApiLocationsByIdQuery,
  useDeleteApiLocationsByIdMutation,
  usePatchApiLocationsByIdMutation,
  useGetApiLogoutQuery,
  useLazyGetApiLogoutQuery,
  useGetApiMasterScheduleQuery,
  useLazyGetApiMasterScheduleQuery,
  usePostApiMasterScheduleUploadMutation,
  useGetApiMasterScheduleByScheduleIdQuery,
  useLazyGetApiMasterScheduleByScheduleIdQuery,
  useDeleteApiMasterScheduleByScheduleIdMutation,
  usePatchApiMasterScheduleByScheduleIdMutation,
  useGetApiMasterScheduleByScheduleIdMilestonesQuery,
  useLazyGetApiMasterScheduleByScheduleIdMilestonesQuery,
  useGetApiMasterScheduleByScheduleIdStatusQuery,
  useLazyGetApiMasterScheduleByScheduleIdStatusQuery,
  useGetApiMasterScheduleByScheduleIdTasksQuery,
  useLazyGetApiMasterScheduleByScheduleIdTasksQuery,
  usePostApiObjectTypesMutation,
  useGetApiObjectTypesByObjectTypeQuery,
  useLazyGetApiObjectTypesByObjectTypeQuery,
  useDeleteApiObjectTypesByObjectTypeMutation,
  usePatchApiObjectTypesByObjectTypeMutation,
  useGetApiOrganizationsQuery,
  useLazyGetApiOrganizationsQuery,
  usePostApiOrganizationsMutation,
  useGetApiOrganizationsTypesQuery,
  useLazyGetApiOrganizationsTypesQuery,
  usePostApiOrganizationsTypesMutation,
  useGetApiOrganizationsTypesByOrganizationTypeQuery,
  useLazyGetApiOrganizationsTypesByOrganizationTypeQuery,
  useDeleteApiOrganizationsTypesByOrganizationTypeMutation,
  usePatchApiOrganizationsTypesByOrganizationTypeMutation,
  useGetApiOrganizationsByIdQuery,
  useLazyGetApiOrganizationsByIdQuery,
  useDeleteApiOrganizationsByIdMutation,
  usePatchApiOrganizationsByIdMutation,
  useGetApiPlatformConfigFormsQuery,
  useLazyGetApiPlatformConfigFormsQuery,
  usePutApiPlatformConfigFormsMutation,
  useGetApiPnrsQuery,
  useLazyGetApiPnrsQuery,
  usePostApiPnrsMutation,
  useGetApiPnrsByIdQuery,
  useLazyGetApiPnrsByIdQuery,
  useDeleteApiPnrsByIdMutation,
  usePatchApiPnrsByIdMutation,
  usePostApiPnrsByIdSubmitMutation,
  useGetApiPnrsByIdVAndVersionQuery,
  useLazyGetApiPnrsByIdVAndVersionQuery,
  useGetApiPnrsByIdVersionsQuery,
  useLazyGetApiPnrsByIdVersionsQuery,
  usePostApiPowerbiEmbedTokenMutation,
  useGetApiProgramsQuery,
  useLazyGetApiProgramsQuery,
  usePostApiProgramsMutation,
  useGetApiProgramsAvailableDeliverableTypesQuery,
  useLazyGetApiProgramsAvailableDeliverableTypesQuery,
  useGetApiProgramsDeliverableTypesQuery,
  useLazyGetApiProgramsDeliverableTypesQuery,
  useGetApiProgramsDeltekContractsQuery,
  useLazyGetApiProgramsDeltekContractsQuery,
  useGetApiProgramsByIdQuery,
  useLazyGetApiProgramsByIdQuery,
  useDeleteApiProgramsByIdMutation,
  usePatchApiProgramsByIdMutation,
  useGetApiProgramsByIdDeliverableTypesQuery,
  useLazyGetApiProgramsByIdDeliverableTypesQuery,
  usePostApiProgramsByIdDeliverableTypesMutation,
  useDeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdMutation,
  usePatchApiProgramsByIdDeliverableTypesAndDeliverableTypeIdMutation,
  useGetApiProgramsByIdLocationsQuery,
  useLazyGetApiProgramsByIdLocationsQuery,
  usePostApiProgramsByIdLocationsMutation,
  useDeleteApiProgramsByIdLocationsAndLocationIdMutation,
  useGetApiProgramsByIdObjectTypesQuery,
  useLazyGetApiProgramsByIdObjectTypesQuery,
  usePostApiProgramsByIdObjectTypesMutation,
  useDeleteApiProgramsByIdObjectTypesAndObjectTypeMutation,
  useGetApiProgramsByIdRoleAssignmentsQuery,
  useLazyGetApiProgramsByIdRoleAssignmentsQuery,
  useGetApiProgramsByIdRolesQuery,
  useLazyGetApiProgramsByIdRolesQuery,
  usePostApiProgramsByIdRolesMutation,
  useGetApiProgramsByIdRolesAndRoleNameQuery,
  useLazyGetApiProgramsByIdRolesAndRoleNameQuery,
  useDeleteApiProgramsByIdRolesAndRoleNameMutation,
  usePatchApiProgramsByIdRolesAndRoleNameMutation,
  useGetApiProgramsByIdRolesAndRoleNameActionsQuery,
  useLazyGetApiProgramsByIdRolesAndRoleNameActionsQuery,
  usePostApiProgramsByIdRolesAndRoleNameActionsMutation,
  useDeleteApiProgramsByIdRolesAndRoleNameActionsActionNameMutation,
  useGetApiProgramsByIdSiteSettingsQuery,
  useLazyGetApiProgramsByIdSiteSettingsQuery,
  usePutApiProgramsByIdSiteSettingsAdminEmailMutation,
  useGetApiProgramsByIdSiteSettingsDomainsQuery,
  useLazyGetApiProgramsByIdSiteSettingsDomainsQuery,
  usePostApiProgramsByIdSiteSettingsDomainsMutation,
  useDeleteApiProgramsByIdSiteSettingsDomainsAndDomainMutation,
  useGetApiProgramsByIdUserRolesQuery,
  useLazyGetApiProgramsByIdUserRolesQuery,
  useGetApiProgramsByIdWorkflowLabelsQuery,
  useLazyGetApiProgramsByIdWorkflowLabelsQuery,
  usePatchApiProgramsByIdWorkflowLabelsMutation,
  useDeleteApiProgramsByIdWorkflowLabelsStatesAndStateMutation,
  useDeleteApiProgramsByIdWorkflowLabelsTaskStatesAndStateMutation,
  useGetApiProgramsByIdWorkflowTypesQuery,
  useLazyGetApiProgramsByIdWorkflowTypesQuery,
  usePostApiProgramsByIdWorkflowTypesMutation,
  useDeleteApiProgramsByIdWorkflowTypesAndTypeMutation,
  useGetApiProjectsQuery,
  useLazyGetApiProjectsQuery,
  usePostApiProjectsMutation,
  useGetApiProjectsDeliverableTypesQuery,
  useLazyGetApiProjectsDeliverableTypesQuery,
  useGetApiProjectsByIdQuery,
  useLazyGetApiProjectsByIdQuery,
  useDeleteApiProjectsByIdMutation,
  usePatchApiProjectsByIdMutation,
  useGetApiProjectsByIdClinsQuery,
  useLazyGetApiProjectsByIdClinsQuery,
  usePostApiProjectsByIdClinsMutation,
  useDeleteApiProjectsByIdClinsAndClinIdMutation,
  usePutApiProjectsByIdDeliverableTypesMutation,
  usePostApiProjectsByIdDeliverableTypesMutation,
  useGetApiProjectsByIdSummaryQuery,
  useLazyGetApiProjectsByIdSummaryQuery,
  usePutApiProjectsByIdSummaryMutation,
  usePostApiProjectsByIdSummaryMutation,
  useGetApiRipsQuery,
  useLazyGetApiRipsQuery,
  usePostApiRipsMutation,
  useGetApiRipsByIdQuery,
  useLazyGetApiRipsByIdQuery,
  useDeleteApiRipsByIdMutation,
  usePatchApiRipsByIdMutation,
  usePostApiRipsByIdEstimateMutation,
  useDeleteApiRipsByIdEstimateAndEstimateIdMutation,
  usePatchApiRipsByIdEstimateAndEstimateIdMutation,
  usePostApiRipsByIdSubmitMutation,
  useGetApiRipsByIdVAndVersionQuery,
  useLazyGetApiRipsByIdVAndVersionQuery,
  useGetApiRipsByIdVersionsQuery,
  useLazyGetApiRipsByIdVersionsQuery,
  useGetApiRisksQuery,
  useLazyGetApiRisksQuery,
  usePostApiRisksMutation,
  useGetApiRisksCategoriesQuery,
  useLazyGetApiRisksCategoriesQuery,
  useGetApiRisksCategoriesByCategoryCodeImpactsQuery,
  useLazyGetApiRisksCategoriesByCategoryCodeImpactsQuery,
  useGetApiRisksClosuresQuery,
  useLazyGetApiRisksClosuresQuery,
  useGetApiRisksImpactsQuery,
  useLazyGetApiRisksImpactsQuery,
  useGetApiRisksProbabilitiesQuery,
  useLazyGetApiRisksProbabilitiesQuery,
  useGetApiRisksByRiskIdQuery,
  useLazyGetApiRisksByRiskIdQuery,
  usePatchApiRisksByRiskIdMutation,
  useGetApiRisksByRiskIdVAndRecordUpdateQuery,
  useLazyGetApiRisksByRiskIdVAndRecordUpdateQuery,
  useGetApiRisksByRiskIdVersionsQuery,
  useLazyGetApiRisksByRiskIdVersionsQuery,
  useGetApiTamsInventoryQuery,
  useLazyGetApiTamsInventoryQuery,
  usePostApiTamsInventoryMutation,
  useGetApiTamsInventoryByIdQuery,
  useLazyGetApiTamsInventoryByIdQuery,
  useDeleteApiTamsInventoryByIdMutation,
  usePatchApiTamsInventoryByIdMutation,
  useGetApiTarsQuery,
  useLazyGetApiTarsQuery,
  usePostApiTarsMutation,
  useGetApiTarsByIdQuery,
  useLazyGetApiTarsByIdQuery,
  useDeleteApiTarsByIdMutation,
  usePatchApiTarsByIdMutation,
  usePostApiTarsByIdEstimateMutation,
  useDeleteApiTarsByIdEstimateAndEstimateIdMutation,
  usePatchApiTarsByIdEstimateAndEstimateIdMutation,
  usePostApiTarsByIdItineraryMutation,
  useDeleteApiTarsByIdItineraryAndItineraryIdMutation,
  usePatchApiTarsByIdItineraryAndItineraryIdMutation,
  usePostApiTarsByIdItineraryAndItineraryIdEstimateMutation,
  useDeleteApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdMutation,
  usePatchApiTarsByIdItineraryAndItineraryIdEstimateEstimateIdMutation,
  usePostApiTarsByIdSubmitMutation,
  useGetApiTarsByIdVAndVersionQuery,
  useLazyGetApiTarsByIdVAndVersionQuery,
  useGetApiTarsByIdVersionsQuery,
  useLazyGetApiTarsByIdVersionsQuery,
  useGetApiTokenQuery,
  useLazyGetApiTokenQuery,
  useGetApiTripReportsQuery,
  useLazyGetApiTripReportsQuery,
  usePostApiTripReportsMutation,
  useGetApiTripReportsByIdQuery,
  useLazyGetApiTripReportsByIdQuery,
  useDeleteApiTripReportsByIdMutation,
  usePatchApiTripReportsByIdMutation,
  usePostApiTripReportsByIdSubmitMutation,
  useGetApiTripReportsByIdVAndVersionQuery,
  useLazyGetApiTripReportsByIdVAndVersionQuery,
  useGetApiTripReportsByIdVersionsQuery,
  useLazyGetApiTripReportsByIdVersionsQuery,
  useGetApiTytoauthQuery,
  useLazyGetApiTytoauthQuery,
  usePostApiTytoauthMutation,
  useGetApiUsersQuery,
  useLazyGetApiUsersQuery,
  usePostApiUsersMutation,
  useGetApiUsersActionsQuery,
  useLazyGetApiUsersActionsQuery,
  useGetApiUsersMeQuery,
  useLazyGetApiUsersMeQuery,
  useGetApiUsersMeNotificationPreferencesQuery,
  useLazyGetApiUsersMeNotificationPreferencesQuery,
  usePutApiUsersMeNotificationPreferencesMutation,
  useGetApiUsersRolesQuery,
  useLazyGetApiUsersRolesQuery,
  useGetApiUsersTypesQuery,
  useLazyGetApiUsersTypesQuery,
  usePostApiUsersTypesMutation,
  useGetApiUsersTypesByUserTypeQuery,
  useLazyGetApiUsersTypesByUserTypeQuery,
  useDeleteApiUsersTypesByUserTypeMutation,
  usePatchApiUsersTypesByUserTypeMutation,
  useGetApiUsersByIdQuery,
  useLazyGetApiUsersByIdQuery,
  useDeleteApiUsersByIdMutation,
  usePatchApiUsersByIdMutation,
  useGetApiUsersByIdAssignedTasksQuery,
  useLazyGetApiUsersByIdAssignedTasksQuery,
  useGetApiUsersByIdWorkflowsQuery,
  useLazyGetApiUsersByIdWorkflowsQuery,
  useGetApiUsersByUserIdRoleProjectsQuery,
  useLazyGetApiUsersByUserIdRoleProjectsQuery,
  usePostApiUsersByUserIdRoleProjectsMutation,
  useDeleteApiUsersByUserIdRoleProjectsAndRoleNameProgramIdProjectIdMutation,
  useGetApiUsersByUserIdRolesQuery,
  useLazyGetApiUsersByUserIdRolesQuery,
  usePostApiUsersByUserIdRolesMutation,
  useDeleteApiUsersByUserIdRolesAndRoleNameProgramIdMutation,
  useGetApiVersionQuery,
  useLazyGetApiVersionQuery,
  useGetApiWorkflowsQuery,
  useLazyGetApiWorkflowsQuery,
  usePostApiWorkflowsMutation,
  useGetApiWorkflowsStatesQuery,
  useLazyGetApiWorkflowsStatesQuery,
  usePostApiWorkflowsStatesMutation,
  useGetApiWorkflowsStatesByStateQuery,
  useLazyGetApiWorkflowsStatesByStateQuery,
  useDeleteApiWorkflowsStatesByStateMutation,
  usePatchApiWorkflowsStatesByStateMutation,
  useGetApiWorkflowsTaskStatesQuery,
  useLazyGetApiWorkflowsTaskStatesQuery,
  usePostApiWorkflowsTaskStatesMutation,
  useGetApiWorkflowsTaskStatesByStateQuery,
  useLazyGetApiWorkflowsTaskStatesByStateQuery,
  useDeleteApiWorkflowsTaskStatesByStateMutation,
  usePatchApiWorkflowsTaskStatesByStateMutation,
  useGetApiWorkflowsTypesQuery,
  useLazyGetApiWorkflowsTypesQuery,
  usePostApiWorkflowsTypesMutation,
  useGetApiWorkflowsTypesByWorkflowTypeQuery,
  useLazyGetApiWorkflowsTypesByWorkflowTypeQuery,
  useDeleteApiWorkflowsTypesByWorkflowTypeMutation,
  usePatchApiWorkflowsTypesByWorkflowTypeMutation,
  useGetApiWorkflowsByIdQuery,
  useLazyGetApiWorkflowsByIdQuery,
  useDeleteApiWorkflowsByIdMutation,
  usePatchApiWorkflowsByIdMutation,
  usePostApiWorkflowsByIdCompleteMutation,
  usePostApiWorkflowsByIdStartMutation,
  useGetApiWorkflowsByIdTasksQuery,
  useLazyGetApiWorkflowsByIdTasksQuery,
  usePostApiWorkflowsByIdTasksMutation,
  useGetApiWorkflowsByIdTasksAndTaskNumberQuery,
  useLazyGetApiWorkflowsByIdTasksAndTaskNumberQuery,
  useDeleteApiWorkflowsByIdTasksAndTaskNumberMutation,
  usePatchApiWorkflowsByIdTasksAndTaskNumberMutation,
  useGetApiWorkflowsByIdTasksAndTaskNumberActionsQuery,
  useLazyGetApiWorkflowsByIdTasksAndTaskNumberActionsQuery,
  usePostApiWorkflowsByIdTasksAndTaskNumberActionsMutation,
  useGetApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberQuery,
  useLazyGetApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberQuery,
  useDeleteApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberMutation,
  usePatchApiWorkflowsByIdTasksAndTaskNumberActionsActionNumberMutation,
  usePostApiWorkflowsByIdTasksAndTaskNumberApproveMutation,
  usePostApiWorkflowsByIdTasksAndTaskNumberAssignMutation,
  usePostApiWorkflowsByIdTasksAndTaskNumberRejectMutation,
  useGetDemoByFilenameQuery,
  useLazyGetDemoByFilenameQuery,
  useGetHealthzQuery,
  useLazyGetHealthzQuery,
  useGetReadyQuery,
  useLazyGetReadyQuery,
  usePostSamlAcsMutation,
  useGetSamlMetadataQuery,
  useLazyGetSamlMetadataQuery,
  useGetSamlSloQuery,
  useLazyGetSamlSloQuery,
  usePostSignaturesMutation,
  useGetSignaturesByIdQuery,
  useLazyGetSignaturesByIdQuery,
} = injectedRtkApi;
